// @ts-nocheck
import React, {
  useState, useEffect, useRef, useMemo, useCallback, useLayoutEffect
} from 'react';
import { flushSync } from 'react-dom';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ChevronLeft, ChevronDown, ChevronRight, ChevronUp, Download, Image, CheckCircle, CheckCircle2, AlertTriangle,
  X, Search, AlertCircle, Flag, Plus, GripVertical, Pencil, Trash2, ImageOff,
  Upload, Bold, Italic, List, ListOrdered, FileText, FileWarning, RefreshCw,
  ArrowUp, ArrowDown, Filter as FilterIcon, Columns3,
  Check, Lock, Package, Info,
} from 'lucide-react';
import { useEditor, EditorContent } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors, DragOverlay,
} from '@dnd-kit/core';
import {
  SortableContext, verticalListSortingStrategy,
  useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { CSS as DndCSS } from '@dnd-kit/utilities';
import { useVirtualizer } from '@tanstack/react-virtual';
import { api } from '../utils/api.js';
import { getToken } from '../utils/auth.js';
import { deriveClass2BannerState } from '../utils/class2Banner.js';
import { buildVirtualRows } from '../utils/virtualRows.js';
import { deriveCatalogFlagsBanner } from '../utils/catalogFlagsBanner.js';
import ImageUploadModal from '../components/ReviewTable/ImageUploadModal.jsx';
import BulkActionBar from '../components/ReviewTable/BulkActionBar.jsx';
import CustomColumnModal from '../components/ReviewTable/CustomColumnModal.jsx';
import ColumnReorder from '../components/ReviewTable/ColumnReorder.jsx';
import CategoryPicker from '../components/ReviewTable/CategoryPicker.tsx';
import ProcessingFeed from '../components/ReviewTable/ProcessingFeed.jsx';
import PaywallModal from '../components/shared/PaywallModal.jsx';
import ExportPaywallModal from '../components/shared/ExportPaywallModal.jsx';
import { capture } from '../utils/analytics.js';
import { getSupabaseRealtimeClient } from '../utils/supabaseClient.js';
// Dispatch 2 T2 (2026-05-28) — canonical render reads merged manual_tags +
// auto_tags + legacy tags via the shared helper. Replaces direct `product.tags`
// reads at 7 sites which would return null for new uploads (BUILD-4 routes
// source-CSV Tags to manual_tags; legacy column stays null on new rows until
// the Migration 083 trigger backfills it).
import { mergeRenderTags } from '../utils/mergeTags';

// ─── Theme hook ───────────────────────────────────────────────────────────────
function useDarkMode() {
  const [dark, setDark] = useState(() => {
    try { return localStorage.getItem('cc_theme') === 'dark'; } catch { return false; }
  });
  function toggle() {
    setDark(d => {
      const next = !d;
      document.documentElement.setAttribute('data-theme', next ? 'dark' : '');
      try { localStorage.setItem('cc_theme', next ? 'dark' : 'light'); } catch {}
      return next;
    });
  }
  return [dark, toggle];
}

// ─── Constants ────────────────────────────────────────────────────────────────
const STATUS = {
  red:    { bg: 'var(--color-red-bg)',    border: 'var(--color-red-border)',    text: 'var(--color-red-text)',    dot: 'var(--color-red)'    },
  yellow: { bg: 'var(--color-yellow-bg)', border: 'var(--color-yellow-border)', text: 'var(--color-yellow-text)', dot: 'var(--color-yellow)' },
  green:  { bg: 'var(--color-green-bg)',  border: 'var(--color-green-border)',  text: 'var(--color-green-text)',  dot: 'var(--color-green)'  },
  blue:   { bg: 'var(--color-info-bg)',   border: 'var(--color-info-border)',   text: 'var(--color-info-text)',   dot: 'var(--color-primary)' },
};

const FLAG_LABELS = {
  missing_title:       'Missing Title',
  missing_sku:         'Missing SKU',
  missing_price:       'Missing Price',
  duplicate_sku:       'Duplicate SKU',
  invalid_price:       'Invalid Price',
  price_exceeds_rrp:   'Price exceeds MSRP',
  currency_mismatch:   'Mixed currencies',
  variant_mismatch:    'Option mismatch',
  low_confidence:      'Needs review',
  field_contradiction: 'Conflicting data',
  zero_price:          'Zero Price',
  invalid_image_url:   'Invalid Image URL',
  // V2 expansion flags (Phase 2.5)
  range_expanded_from_file: 'Size range expanded',
  expansion_skipped_no_grammar: 'Expansion skipped',
  non_expandable_multifit: 'Multi-fit size',
  off_style_token_ignored: 'Off-style token',
  capacity_not_size:   'Capacity token',
  assortment_atomic:   'Assortment preserved',
  expansion_rpc_failed: 'Expansion failed',
  // V2 authority flags (Phase 2.5)
  low_confidence_authority: 'Low role confidence',
  low_coverage_authority:   'Low file coverage',
  stale_authority_warning:  'Stale source data',
  // Phase 4 image association flags
  image_low_confidence_match: 'Image — needs review',
  image_unmatched:            'Images unmatched',
  image_decorative_filtered:  'Decorative images skipped',
  image_orphan_sku_not_in_catalog: 'Image refs missing SKU',
  image_color_code_mismatch:  'Color code mismatch',
  video_requires_manual_import: 'Video file — manual import',
  image_format_converted:     'Format conversions',
  // Phase 4 encrypted ZIP flags
  zip_password_required:      'Password-protected ZIP',
  zip_zipcrypto_unsupported:  'Legacy encrypted ZIP',
  zip_aes_encryption_unsupported: 'AES-encrypted ZIP',
  // Brief 1A multi-file (Brief V1-Finishing v2 D.4 / DV1.19) — severity=warning, NOT in ERROR_FLAG_TYPES
  multi_catalog_drop_detected: 'Multiple catalogs detected',
  // Dispatch A (2026-05-27): status⇄flag invariant + missing-field surfacing
  // F-049 (2026-05-29): missing_vendor dropped per Migration 084 (vendor optional in Shopify).
  derived_title_needs_review: 'Title derived — review',
  missing_category:            'Missing category',
  // Dispatch B Piece 1 (2026-05-27): category flag types
  // category_missing fires from framework.ts on null-resolution (path-agnostic).
  // category_outlier fires from the catalog-majority sweep (Piece 4).
  // category_uncertain + category_token_mismatch are FORWARD-COMPAT — no
  // emitters land this dispatch (Migration 076 provisions them ahead of
  // always-run-Haiku consensus + title-token validator).
  category_missing:            'Missing category',
  category_uncertain:          'Category uncertain',
  category_outlier:            'Category looks off',
  category_token_mismatch:     'Title doesn’t match category',
  // Migration 080 (2026-05-27 dispatch T10) — header-resolver flags
  unmapped_column:               'Unrecognized column',
  status_coercion_unclear:       'Status not recognized',
  category_source_disagreement:  'Category disagreement',
  gender_source_disagreement:    'Gender disagreement',
  age_group_source_disagreement: 'Age group disagreement',
  // UB#10 (Phase 2B SD4, 2026-05-29) — catalog-level flag types surfaced in the
  // catalog-flags info banner. Previously emitted with variant_id NULL and had
  // no merchant-visible label (only encrypted-ZIP banner rendered). Never show
  // the raw flag_type code to merchants (CLAUDE.md hard rule).
  // Labels for the 6 below match COPY.md §2 verbatim (locked source of truth);
  // the 5 new types are added to COPY.md §2 in the same dispatch.
  catalog_anchor_demoted:         'Catalog file not used',
  source_recall_drift:            'Possible missing variants',
  vendor_inferred:                'Vendor inferred',
  classification_failed:          'File type unclear',
  grouping_axis_defaulted:        'Grouping inferred',
  grouping_no_valid_candidate:    'Grouping unclear',
  grouping_saved_policy_stale:    'Grouping rule updated',
  section_axis_ambiguous:         'Multiple SKU patterns',
  title_derived:                  'Titles derived',
  range_price_present:            'Price is a range',
  extraction_yielded_zero_products: 'Couldn’t extract any products',
  column_mapping_ambiguous:       'Column mapping uncertain',
};

// UB#10 (Phase 2B SD4, 2026-05-29) — merchant-facing copy for catalog-level
// flags surfaced in the catalog-flags info banner. Defined here (early) so the
// banner render in the ReviewTable body has no use-before-define (these flags
// are variant_id NULL, so they never appear in the per-variant IssuesDrawer —
// DRAWER_MESSAGES intentionally omits them). source_recall_drift wording per
// D2 (Halt #8): merchant-action-recommended. Copy mirrored in COPY.md §8.5.
const CATALOG_FLAG_BANNER_MESSAGES = {
  catalog_anchor_demoted:         'We picked one of your files as the main catalog. If the wrong file was used, open the file list and choose the correct one.',
  source_recall_drift:            'This catalog may be missing some variants from the source file. Check the source for products or sizes that didn’t come through.',
  vendor_inferred:                'We didn’t find a vendor in the catalog itself, so we inferred one from the file. Check the Vendor field and correct it if needed.',
  classification_failed:          'We couldn’t confidently tell what type one of your files is. Verify it was handled the way you expected.',
  grouping_axis_defaulted:        'We grouped variants under each product automatically. Review the grouping and adjust if it doesn’t match your catalog.',
  grouping_no_valid_candidate:    'We couldn’t confidently group variants for this catalog, so products were kept as individual rows. Review the structure before exporting.',
  grouping_saved_policy_stale:    'Your saved grouping rule no longer applied cleanly, so we re-derived it. Review the grouping for this catalog.',
  section_axis_ambiguous:         'We detected multiple sections in your file. Verify products were grouped the way you expected.',
  title_derived:                  'Some product titles were derived from your file because no separate title column was found. Review the derived titles before exporting.',
  range_price_present:            'Some prices look like a range rather than a single value. Set a single price per variant before exporting.',
  extraction_yielded_zero_products: 'We couldn’t find any products in this file. Check that it has SKU, title, and price columns.',
  column_mapping_ambiguous:       'More than one column matched the same field. Verify the right columns were used for this catalog.',
};

// ── Signal hierarchy invariant ──────────────────────────────────────────────
// ERROR_FLAG_TYPES drives cell-level red indicators (red bg, red "missing" text).
// Only flags that BLOCK Shopify import belong here (severity='error' + in WILL_FAIL_TYPES).
// missing_sku is severity='warning' and non-blocking — it must NOT be in this set.
// Shopify assigns an internal ID when SKU is absent; alarming the merchant for it
// causes false urgency and alarm fatigue. See brief: 2026-04-22-missing-field-indicators-audit.md
const ERROR_FLAG_TYPES = new Set(['missing_price', 'missing_title', 'invalid_price']);
// Retired flag types still in the DB from older extractions — hide from all UI.
// F-049 (2026-05-29): missing_vendor added per Migration 084. 23,710 historical
// rows pre-date the drop; this Set hides them from review-table badges + cell-bg.
const RETIRED_FLAG_TYPES = new Set(['missing_image', 'missing_vendor']);

function getMostSevereFlag(flags) {
  const active = (flags || []).filter(f => !f.resolved && !RETIRED_FLAG_TYPES.has(f.flag_type));
  if (!active.length) return null;
  const errors = active.filter(f => f.severity === 'error' || ERROR_FLAG_TYPES.has(f.flag_type));
  const pool = errors.length ? errors : active;
  pool.sort((a, b) => (a.flag_type || '').localeCompare(b.flag_type || ''));
  return pool[0];
}
const FLAG_COLUMNS = {
  missing_title:    'title',
  missing_sku:      'sku',
  missing_price:    'price',
  zero_price:       'price',
  duplicate_sku:    'sku',
  invalid_price:    'price',
  price_exceeds_rrp:'price',
  invalid_image_url:'image',
  currency_mismatch:'price',
  variant_mismatch: 'option1',
  low_confidence:   null,
  // Dispatch A — column-specific yellow-bg / navigation targets
  // F-049 (2026-05-29): missing_vendor entry dropped per Migration 084.
  derived_title_needs_review: 'title',
  missing_category:           'productCategory',
  // Dispatch B Piece 1 — category flag types share the Category cell
  category_missing:           'productCategory',
  category_outlier:           'productCategory',
  category_uncertain:         'productCategory',
  category_token_mismatch:    'productCategory',
};
const DRAWER_OPEN_EDIT = new Set(['missing_title', 'missing_sku', 'missing_price', 'invalid_price']);
function groupFlags(flags) {
  const groups = {};
  for (const f of flags) {
    if (RETIRED_FLAG_TYPES.has(f.flag_type)) continue;
    // field_contradiction flags each have a unique message — don't collapse them
    if (f.flag_type === 'field_contradiction') {
      const key = `field_contradiction_${f.id || f.field_name || Math.random()}`;
      groups[key] = { flag_type: f.flag_type, severity: f.severity, field_name: f.field_name, message: f.message, count: 1 };
      continue;
    }
    if (!groups[f.flag_type]) groups[f.flag_type] = { flag_type: f.flag_type, severity: f.severity, field_name: f.field_name, count: 0 };
    groups[f.flag_type].count++;
  }
  // Sort: error first, then warning, then info
  const severityRank = (s) => s === 'error' ? 0 : s === 'warning' ? 1 : 2;
  return Object.values(groups).sort((a, b) => severityRank(a.severity) - severityRank(b.severity));
}

// ─── Utilities ────────────────────────────────────────────────────────────────
function pct(n, total) { return total ? Math.round((n / total) * 100) : 0; }
function mostCommon(arr) {
  const a = arr.filter(Boolean);
  if (!a.length) return null;
  const f = {};
  for (const v of a) f[v] = (f[v] || 0) + 1;
  return Object.entries(f).sort((a, b) => b[1] - a[1])[0][0];
}

const COL_KEYS = ['image','compareAt','productCategory','body','status','type','vendor','tags','collection','option1','option2','option3','barcode','inventory','continueSellingOos','weight','seoTitle','seoDesc'];
// ── Default columns decision (locked 2026-04-22, see .claude/briefs/2026-04-22-default-columns-audit.md) ──
// Research: eye-tracking F-pattern shows columns 1-3 get disproportionate attention.
// Competitor audit: wholesale linesheet tools (NuOrder, JOOR, Brandboom) don't show Status or Vendor.
// Status removed: every row shows "draft" after extraction — zero information value.
// Vendor removed: single-brand linesheets (dominant ICP) have identical vendor on every row.
// Option columns removed: on collapsed product rows they show em dashes — visible on variant expand.
// Order: Product → Images → SKU → Price → Compare At → Category → Description → Type → Review
//   Images at position 2: visual pairing is the first thing merchants verify after title.
//   SKU/Price in positions 3-4: fast-verify fields in the attention zone.
//   Compare At adjacent to Price: pricing columns grouped — proves wholesale price intelligence.
//   Type after Description: shows AI classification, drives Shopify collection auto-rules.
//   Description moved to 6: slowest to verify, checked least frequently.
// All removed columns remain available in the Columns panel. Only affects first visit to a new catalog.
const COL_FRESH_DEFAULTS = { body: true, image: true, productCategory: true, compareAt: true, type: true };
// No columns are pinned — all panel columns can be toggled. Product/SKU/Price/Review are always-on
// but are not in COL_KEYS; they render unconditionally in the table.
const PINNED_COLS = new Set([]);

// ─── Shared style objects ────────────────────────────────────────────────────
const primaryBtn = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  background: 'var(--color-primary)', color: '#fff',
  border: 'none', borderRadius: 'var(--r-md)',
  padding: '8px 16px', fontWeight: 600, fontSize: 14, cursor: 'pointer',
};
const secondaryBtn = {
  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
  background: 'var(--color-surface)', color: 'var(--color-text-2)',
  border: '1px solid var(--color-border)', borderRadius: 'var(--r-md)',
  padding: '8px 14px', fontWeight: 500, fontSize: 14, cursor: 'pointer',
};
const iconBtn = {
  background: 'none', border: 'none',
  color: 'var(--color-text-3)', display: 'flex', alignItems: 'center', justifyContent: 'center',
  cursor: 'pointer', padding: 4, borderRadius: 'var(--r-sm)', lineHeight: 1,
};
const overlayStyle = {
  position: 'fixed', inset: 0,
  background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(3px)',
  zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
};

function highlight(text, query) {
  if (!query || !text) return text;
  const q = query.trim();
  if (!q) return text;
  const str = String(text);
  const lower = str.toLowerCase();
  const ql = q.toLowerCase();
  const parts = [];
  let last = 0;
  let idx = lower.indexOf(ql, last);
  if (idx === -1) return text;
  while (idx !== -1) {
    if (idx > last) parts.push(str.slice(last, idx));
    parts.push(React.createElement('mark', {
      key: idx,
      style: { background: '#FEF9C3', borderRadius: 2, padding: '0 1px', color: 'inherit' }
    }, str.slice(idx, idx + q.length)));
    last = idx + q.length;
    idx = lower.indexOf(ql, last);
  }
  if (last < str.length) parts.push(str.slice(last));
  return React.createElement(React.Fragment, null, ...parts);
}

// ─── SkeletonRow ──────────────────────────────────────────────────────────────
function SkeletonRow({ cols }) {
  const shimmer = {
    background: 'linear-gradient(90deg,var(--color-surface-2) 25%,var(--color-border) 50%,var(--color-surface-2) 75%)',
    backgroundSize: '800px 100%', animation: 'shimmer 2.2s infinite',
    borderRadius: 4, height: 14
  };
  return (
    <tr style={{ borderBottom: '1px solid var(--color-border)' }}>
      {Array.from({ length: cols }).map((_, i) => (
        <td key={i} style={{ padding: '4px 8px' }}>
          <div style={{ ...shimmer, width: i === 0 ? 32 : i === 1 ? 90 : 60 }} />
        </td>
      ))}
    </tr>
  );
}

// ─── StatusDot ────────────────────────────────────────────────────────────────
function StatusDot({ status }) {
  const c = STATUS[status] || STATUS.yellow;
  const label = status === 'red' ? 'Has errors — needs fixes' : status === 'yellow' ? 'Has warnings — review recommended' : 'Ready to export';
  const Icon = status === 'red' ? AlertCircle : status === 'yellow' ? AlertTriangle : CheckCircle;
  const iconProps = status === 'green' ? { size: 14, strokeWidth: 1.5 } : { size: 14, strokeWidth: 2 };
  return (
    <span
      aria-label={label}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4,
        color: c.dot,
        flexShrink: 0,
      }}
    >
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: c.dot, flexShrink: 0 }} />
      <Icon {...iconProps} style={{ opacity: status === 'green' ? 0.6 : 1 }} />
    </span>
  );
}

// ─── EditableCell style constants (must precede EditableCell) ─────────────────
// missSt is red because missingLabel is only used on ERROR-severity fields (price, title).
// Warning-severity fields (missing_sku) must NOT have a missingLabel — they show as empty cells.
// See brief: 2026-04-22-missing-field-indicators-audit.md
const missSt = { position: 'absolute', left: 6, top: 3, fontSize: 13, color: 'var(--color-red)', pointerEvents: 'none', whiteSpace: 'nowrap' };
const errSt  = { fontSize: 11, color: 'var(--color-red)', marginTop: 2, fontWeight: 500, whiteSpace: 'nowrap' };

// ─── EditableCell — always-present input for product-level header rows ──────
function EditableCell({ value, onSave, multiline = false, placeholder = '', extraStyle = {} }) {
  const [draft,    setDraft]    = useState(value ?? '');
  const [focused,  setFocused]  = useState(false);
  const committed                = useRef(value ?? '');

  useEffect(() => {
    if (!focused) { setDraft(value ?? ''); committed.current = value ?? ''; }
  }, [value, focused]);

  function commit() {
    const trimmed = draft.trim();
    if (trimmed !== committed.current.trim()) {
      committed.current = trimmed;
      console.log('[EditableCell.commit] committing', { trimmed, saveValue: trimmed || null });
      onSave(trimmed || null);
    } else {
      console.log('[EditableCell.commit] no change, skipping', { trimmed, committed: committed.current });
    }
  }

  const base = {
    display: 'block', width: '100%', padding: '3px 6px', fontSize: 13,
    borderRadius: 'var(--r-sm)', outline: 'none', boxSizing: 'border-box',
    background: focused ? 'var(--color-surface)' : 'transparent',
    border: focused ? '1.5px solid var(--color-primary)' : '1px solid transparent',
    color: (draft || '') ? 'var(--color-text)' : 'var(--color-text-3)',
    cursor: 'text', transition: 'border-color 0.12s, background 0.12s',
    ...extraStyle,
  };

  if (multiline) {
    return (
      <textarea
        className="ie"
        value={draft || ''}
        placeholder={placeholder}
        rows={focused ? 2 : 1}
        onChange={e => setDraft(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => { setFocused(false); commit(); }}
        onClick={e => e.stopPropagation()}
        onKeyDown={e => {
          if (e.key === 'Escape') { e.stopPropagation(); setDraft(committed.current); e.target.blur(); }
        }}
        style={{ ...base, resize: focused ? 'vertical' : 'none', overflowY: focused ? 'auto' : 'hidden', lineHeight: 1.4 }}
      />
    );
  }

  return (
    <input
      type="text"
      className="ie"
      value={draft || ''}
      placeholder={placeholder}
      onChange={e => setDraft(e.target.value)}
      onFocus={() => setFocused(true)}
      onBlur={() => { setFocused(false); commit(); }}
      onClick={e => e.stopPropagation()}
      onKeyDown={e => {
        if (e.key === 'Enter') { e.preventDefault(); commit(); e.target.blur(); }
        if (e.key === 'Escape') { e.stopPropagation(); setDraft(committed.current); e.target.blur(); }
      }}
      style={base}
    />
  );
}

// ─── InlineEdit ───────────────────────────────────────────────────────────────
const STATUS_CONFIG = {
  active:   { label: 'Active',   bg: 'var(--color-green-bg)',  color: 'var(--color-green-text)' },
  draft:    { label: 'Draft',    bg: 'var(--color-surface-2)', color: 'var(--color-text-2)' },
  archived: { label: 'Archived', bg: 'var(--color-border)',    color: 'var(--color-text-3)' },
};

function StatusCell({ value, onSave }) {
  const [open, setOpen] = React.useState(false);
  const [highlightIdx, setHighlightIdx] = React.useState(-1);
  const ref = React.useRef(null);
  const cfg = STATUS_CONFIG[value] || STATUS_CONFIG.draft;
  const options = ['draft', 'active', 'archived'] as const;

  React.useEffect(() => {
    if (!open) return;
    function handleClick(e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); }
    function handleBlur() { setOpen(false); }
    document.addEventListener('mousedown', handleClick);
    window.addEventListener('blur', handleBlur);
    return () => { document.removeEventListener('mousedown', handleClick); window.removeEventListener('blur', handleBlur); };
  }, [open]);

  function handleKeyDown(e) {
    if (!open) {
      if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); e.stopPropagation(); setOpen(true); setHighlightIdx(options.indexOf(value || 'draft')); }
      return;
    }
    if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); }
    else if (e.key === 'ArrowDown') { e.preventDefault(); setHighlightIdx(i => Math.min(i + 1, options.length - 1)); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setHighlightIdx(i => Math.max(i - 1, 0)); }
    else if (e.key === 'Enter' && highlightIdx >= 0) { e.preventDefault(); e.stopPropagation(); onSave(options[highlightIdx]); setOpen(false); }
  }

  return (
    <div ref={ref} tabIndex={0} onKeyDown={handleKeyDown} style={{ position: 'relative', display: 'flex', alignItems: 'center', width: '100%', height: '100%', cursor: 'pointer', outline: 'none' }} onClick={e => { e.stopPropagation(); setOpen(o => !o); setHighlightIdx(options.indexOf(value || 'draft')); }}>
      <div
        style={{ display: 'inline-flex', alignItems: 'center', padding: '2px 8px', borderRadius: 4, fontSize: 12, fontWeight: 500, background: cfg.bg, color: cfg.color, userSelect: 'none', whiteSpace: 'nowrap', outline: open ? '2px solid var(--color-primary)' : 'none', outlineOffset: -1 }}
      >{cfg.label}</div>
      {open && (
        <div style={{ position: 'absolute', top: '100%', left: 0, marginTop: 4, zIndex: 200, background: 'var(--color-surface)', border: '1px solid var(--color-primary)', borderRadius: 8, boxShadow: 'var(--shadow-card)', padding: 4, display: 'flex', flexDirection: 'column', gap: 2, minWidth: 110 }}>
          {options.map((s, idx) => {
            const c = STATUS_CONFIG[s];
            return (
              <button
                key={s}
                onMouseDown={e => { e.preventDefault(); onSave(s); setOpen(false); }}
                onMouseEnter={() => setHighlightIdx(idx)}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 10px', border: 'none', borderRadius: 6, cursor: 'pointer', background: idx === highlightIdx ? 'var(--color-surface-2)' : 'transparent', textAlign: 'left', width: '100%' }}
              >
                <span style={{ display: 'inline-block', padding: '1px 7px', borderRadius: 4, fontSize: 12, fontWeight: 500, background: c.bg, color: c.color }}>{c.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function InlineEdit({
  value, onSave, type = 'text', placeholder = '', validate,
  extraStyle = {}, missingLabel = '', underline = false,
  expandable = false, maxWidth, debounceMs = 0, renderValue,
  textAlign
}) {
  const [val, setVal]           = useState(value ?? '');
  const [err, setErr]           = useState(null);
  const [focus, setFocus]       = useState(false);
  const [saveStatus, setSaveStatus] = useState(null);
  const committed               = useRef(value ?? '');
  const debounceRef             = useRef(null);
  const statusTimerRef          = useRef(null);
  const inputRef                = useRef(null);

  useEffect(() => { if (!focus) { setVal(value ?? ''); committed.current = value ?? ''; } }, [value, focus]);
  useEffect(() => () => { clearTimeout(debounceRef.current); clearTimeout(statusTimerRef.current); }, []);

  function tryCommit() {
    const trimmed = type === 'number' ? val : String(val ?? '').trim();
    if (validate) { const msg = validate(trimmed); if (msg) { console.log('[InlineEdit.tryCommit] validation failed:', msg); setErr(msg); return; } }
    setErr(null);
    if (trimmed !== committed.current) {
      committed.current = trimmed;
      const saveValue = trimmed === '' ? null : type === 'number' ? (parseFloat(trimmed) || null) : trimmed;
      console.log('[InlineEdit.tryCommit] committing', { trimmed, saveValue, type });
      const result = onSave(saveValue);
      if (debounceMs > 0 && result && typeof result.then === 'function') {
        setSaveStatus('saving');
        result.then(() => { setSaveStatus('saved'); statusTimerRef.current = setTimeout(() => setSaveStatus(null), 1000); }).catch(() => setSaveStatus(null));
      }
    }
  }

  function handleChange(newVal) {
    setVal(newVal);
    if (err) setErr(null);
    if (debounceMs > 0) {
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        const trimmed = type === 'number' ? newVal : String(newVal ?? '').trim();
        if (trimmed !== committed.current) {
          committed.current = trimmed;
          setSaveStatus('saving');
          const result = onSave(trimmed === '' ? null : type === 'number' ? (parseFloat(trimmed) || null) : trimmed);
          if (result && typeof result.then === 'function') {
            result.then(() => { setSaveStatus('saved'); statusTimerRef.current = setTimeout(() => setSaveStatus(null), 1000); }).catch(() => setSaveStatus(null));
          } else { setSaveStatus('saved'); statusTimerRef.current = setTimeout(() => setSaveStatus(null), 1000); }
        }
      }, debounceMs);
    }
  }

  const isEmpty = String(val ?? '').trim() === '';
  const showMiss = missingLabel && isEmpty && !focus;
  const showRenderValue = !focus && renderValue && !showMiss;
  const isExpanded = focus && expandable;

  const base = {
    display: 'block', width: '100%',
    background: (focus && !isExpanded) ? 'var(--color-surface)' : 'transparent',
    border: (focus && !isExpanded) ? '1.5px solid var(--color-primary)' : '1px solid transparent',
    borderRadius: 'var(--r-sm)',
    padding: '3px 6px', fontSize: 13, color: 'var(--color-text)',
    outline: 'none', cursor: 'text', boxSizing: 'border-box',
    transition: 'border-color 0.12s, background 0.12s',
    textDecoration: underline ? 'underline dashed var(--color-yellow)' : 'none',
    textUnderlineOffset: underline ? 3 : undefined,
    maxWidth: maxWidth || undefined,
    textAlign: textAlign || undefined,
    ...extraStyle,
  };

  const statusEl = saveStatus && (
    <span style={{ position: 'absolute', bottom: 2, right: 4, fontSize: 11, fontStyle: 'italic', color: 'var(--color-text-3)', pointerEvents: 'none' }}>
      {saveStatus === 'saving' ? 'Saving…' : 'Saved'}
    </span>
  );

  return (
    <div style={{ position: 'relative', minWidth: 0, flex: 1 }}>
      {showRenderValue && (
        <div style={{ ...base, cursor: 'text', position: 'absolute', inset: 0, zIndex: 1 }}
          onClick={e => { e.stopPropagation(); inputRef.current?.focus(); }}
        >{renderValue}</div>
      )}
      {isExpanded && (
        <div aria-hidden="true" style={{ padding: '3px 6px', fontSize: 13, lineHeight: 'normal', border: '1px solid transparent', visibility: 'hidden', pointerEvents: 'none', boxSizing: 'border-box' }}>&nbsp;</div>
      )}
      <input
        ref={inputRef}
        type={type} value={val}
        placeholder={showMiss ? '' : placeholder}
        step={type === 'number' ? '0.01' : undefined}
        min={type === 'number' ? '0' : undefined}
        onChange={e => handleChange(e.target.value)}
        onFocus={() => setFocus(true)}
        onBlur={() => { setFocus(false); if (!debounceMs) tryCommit(); }}
        onKeyDown={e => {
          if (e.key === 'Enter')  { e.preventDefault(); tryCommit(); e.target.blur(); }
          if (e.key === 'Escape') { e.stopPropagation(); setVal(committed.current); setErr(null); e.target.blur(); }
        }}
        className="ie"
        style={{
          ...base,
          color: showMiss ? 'transparent' : showRenderValue ? 'transparent' : base.color,
          ...(isExpanded ? {
            position: 'absolute',
            top: 0, left: 0, right: 0,
            zIndex: 10,
            background: 'var(--color-surface)',
            border: '1.5px solid var(--color-primary)',
            borderRadius: 'var(--r-sm)',
          } : {}),
        }}
      />
      {showMiss && <span style={missSt}>{missingLabel}</span>}
      {err && <div style={errSt}>{err}</div>}
      {statusEl}
    </div>
  );
}

// ─── TipTap global styles (injected once) ────────────────────────────────────
;(function injectTipTapStyles() {
  if (typeof document === 'undefined') return;
  if (document.getElementById('cc-tiptap-styles')) return;
  const s = document.createElement('style');
  s.id = 'cc-tiptap-styles';
  s.textContent = `
    .ProseMirror { outline: none; font-family: inherit; line-height: 1.5; }
    .ProseMirror p { margin: 0 0 4px; }
    .ProseMirror ul, .ProseMirror ol { padding-left: 20px; margin: 0 0 4px; }
    .ProseMirror li { margin: 0; }
    .ProseMirror p:last-child, .ProseMirror ul:last-child, .ProseMirror ol:last-child { margin-bottom: 0; }
  `;
  document.head.appendChild(s);
})();

// ─── DescriptionEdit ─────────────────────────────────────────────────────────
function stripHtml(html) {
  return (html || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();
}

function DescriptionEdit({ value, onSave }) {
  const [open, setOpen]       = useState(false);
  const [focused, setFocused] = useState(false);
  const cancelledRef          = useRef(false);
  const committedHtml         = useRef(value ?? '');
  const wrapperRef            = useRef(null);

  const editor = useEditor({
    extensions: [
      StarterKit.configure({
        heading:         false,
        blockquote:      false,
        code:            false,
        codeBlock:       false,
        horizontalRule:  false,
        strike:          false,
      }),
    ],
    content: value ?? '',
    editable: true,
    onFocus() { setFocused(true); },
    onBlur({ event }) {
      // If focus moved to a toolbar button inside our wrapper, don't commit yet
      if (wrapperRef.current?.contains(event.relatedTarget)) return;
      setFocused(false);
      setOpen(false);
      if (!cancelledRef.current) {
        const html = editor.getHTML();
        const isEmpty = stripHtml(html).length === 0;
        const next = isEmpty ? null : html;
        if (next !== (committedHtml.current || null)) {
          committedHtml.current = next ?? '';
          onSave(next);
        }
      }
      cancelledRef.current = false;
    },
  });

  // Keep editor content in sync when value changes externally (while closed)
  useEffect(() => {
    if (!open && editor && (value ?? '') !== committedHtml.current) {
      editor.commands.setContent(value ?? '');
      committedHtml.current = value ?? '';
    }
  }, [value, open]);

  // Close on window blur
  useEffect(() => {
    if (!open) return;
    function handleBlur() { editor?.commands.blur(); }
    window.addEventListener('blur', handleBlur);
    return () => window.removeEventListener('blur', handleBlur);
  }, [open, editor]);

  function handleKeyDown(e) {
    if (e.key === 'Escape') {
      e.stopPropagation(); e.preventDefault();
      cancelledRef.current = true;
      editor.commands.setContent(committedHtml.current);
      editor.commands.blur();
    }
  }

  function openEditor(e) {
    e.stopPropagation();
    setOpen(true);
    // slight delay so the editor mounts/transitions before focus
    setTimeout(() => editor?.commands.focus('end'), 0);
  }

  const displayText = stripHtml(value ?? '');
  const isEmpty     = displayText.length === 0;

  // While useEditor() initialises, render the closed preview so cells don't flash blank
  if (!editor) {
    return (
      <div style={{ position: 'relative', width: '100%', height: '100%' }}>
        <div className="ie" style={{
          padding: '3px 6px', fontSize: 13, cursor: 'pointer',
          color: isEmpty ? 'var(--color-text-3)' : 'var(--color-text)',
          overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
          border: '1px solid transparent', borderRadius: 'var(--r-sm)', userSelect: 'none',
          width: '100%', height: '100%', display: 'flex', alignItems: 'center',
        }}>
          {isEmpty ? '' : displayText}
        </div>
      </div>
    );
  }

  const toolbarBtnStyle = (active) => ({
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    width: 26, height: 26, borderRadius: 4, border: 'none', cursor: 'pointer',
    background: active ? 'var(--color-primary)' : 'transparent',
    color:      active ? 'var(--color-surface)' : 'var(--color-text-2)',
    flexShrink: 0,
  });

  return (
    <div ref={wrapperRef} style={{ position: 'relative', width: '100%', height: '100%' }}>
      {/* ── Display (closed) state ── */}
      <div
        className="ie"
        onClick={openEditor}
        style={{
          padding: '3px 6px', fontSize: 13, cursor: 'pointer',
          color: isEmpty ? 'var(--color-text-3)' : 'var(--color-text)',
          overflow: 'hidden', whiteSpace: 'nowrap', textOverflow: 'ellipsis',
          border: '1px solid transparent', borderRadius: 'var(--r-sm)',
          userSelect: 'none',
          visibility: open ? 'hidden' : 'visible',
          width: '100%', height: '100%', display: 'flex', alignItems: 'center',
        }}
      >
        {isEmpty ? '' : displayText}
      </div>

      {/* Always-mounted hidden trigger input — lets focusInput() find this cell
          via querySelector('input') and open the editor on keyboard navigation. */}
      <input
        type="text"
        readOnly
        tabIndex={-1}
        aria-hidden="true"
        onFocus={() => { setOpen(true); setTimeout(() => editor.commands.focus('end'), 0); }}
        style={{ position: 'absolute', width: 0, height: 0, opacity: 0, border: 'none', padding: 0, margin: 0, pointerEvents: 'none' }}
      />

      {/* ── Editor (open) state ── */}
      {open && (
        <div
          style={{
            position: 'absolute', top: 0, left: 0, right: 0, zIndex: 100,
            border: `1px solid ${focused ? 'var(--color-primary)' : 'var(--color-border)'}`,
            borderRadius: 'var(--r-sm)', background: 'var(--color-surface)',
            boxSizing: 'border-box', minWidth: 220, cursor: 'text',
          }}
          onKeyDown={handleKeyDown}
        >
          {/* Toolbar */}
          <div style={{
            display: 'flex', gap: 2, alignItems: 'center',
            padding: '4px 6px',
            borderBottom: '1px solid var(--color-border)',
          }}>
            <button
              type="button"
              onMouseDown={e => { e.preventDefault(); editor.chain().focus().toggleBold().run(); }}
              style={toolbarBtnStyle(editor.isActive('bold'))}
              title="Bold"
            ><Bold size={13} /></button>
            <button
              type="button"
              onMouseDown={e => { e.preventDefault(); editor.chain().focus().toggleItalic().run(); }}
              style={toolbarBtnStyle(editor.isActive('italic'))}
              title="Italic"
            ><Italic size={13} /></button>
            <button
              type="button"
              onMouseDown={e => { e.preventDefault(); editor.chain().focus().toggleBulletList().run(); }}
              style={toolbarBtnStyle(editor.isActive('bulletList'))}
              title="Bullet list"
            ><List size={13} /></button>
            <button
              type="button"
              onMouseDown={e => { e.preventDefault(); editor.chain().focus().toggleOrderedList().run(); }}
              style={toolbarBtnStyle(editor.isActive('orderedList'))}
              title="Numbered list"
            ><ListOrdered size={13} /></button>
          </div>

          {/* TipTap editor area */}
          <EditorContent
            editor={editor}
            style={{ fontSize: 14, minHeight: 80, padding: '8px 12px', lineHeight: 1.5 }}
          />
        </div>
      )}
    </div>
  );
}

// ─── ImageThumb ───────────────────────────────────────────────────────────────
function ImageThumb({ src, onDelete, onError }) {
  const [hovered, setHovered] = useState(false);
  return (
    <div
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        position: 'relative', width: 64, height: 64,
        borderRadius: 8, overflow: 'hidden',
        border: '1px solid var(--color-border)', flexShrink: 0,
        background: 'var(--color-surface-2)',
      }}
    >
      <img
        src={src} alt=""
        onError={e => { e.target.style.display = 'none'; onError?.(); }}
        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
      />
      {onDelete && (
        <button
          onClick={e => { e.stopPropagation(); onDelete(); }}
          title="Remove image"
          style={{
            position: 'absolute', top: 3, right: 3,
            width: 15, height: 15, borderRadius: 4,
            background: hovered ? 'var(--color-red)' : 'rgba(0,0,0,0.40)',
            border: 'none', cursor: 'pointer',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            transition: 'background 0.12s',
          }}
        >
          <X size={8} color="#fff" strokeWidth={3} />
        </button>
      )}
    </div>
  );
}

// ─── ImageCell ────────────────────────────────────────────────────────────────
function ImageCell({ productId, variantId = null, images = [], onImagesChange, onToast }) {
  const [open, setOpen]               = useState(false);
  const [url, setUrl]                 = useState('');
  const [adding, setAdding]           = useState(false);
  const [uploading, setUploading]     = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [urlError, setUrlError]             = useState(null);
  const [pendingUrlImageId, setPendingUrlImageId] = useState(null);
  const cellRef                       = useRef(null);
  const inputRef                      = useRef(null);
  const fileInputRef                  = useRef(null);
  const filePickerOpenRef             = useRef(false);

  useEffect(() => {
    if (!open) {
      setUrlError(null);
      setPendingUrlImageId(null);
      return;
    }
    function handleClick(e) {
      if (cellRef.current && !cellRef.current.contains(e.target)) setOpen(false);
    }
    function handleKey(e) {
      if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); }
    }
    function handleBlur() { if (!filePickerOpenRef.current) setOpen(false); }
    function handleFocus() { if (filePickerOpenRef.current) { filePickerOpenRef.current = false; } }
    document.addEventListener('mousedown', handleClick);
    document.addEventListener('keydown', handleKey);
    window.addEventListener('blur', handleBlur);
    window.addEventListener('focus', handleFocus);
    return () => {
      document.removeEventListener('mousedown', handleClick);
      document.removeEventListener('keydown', handleKey);
      window.removeEventListener('blur', handleBlur);
      window.removeEventListener('focus', handleFocus);
    };
  }, [open]);

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    function handlePaste(e) {
      const items = e.clipboardData?.items;
      if (!items) return;
      const imageFiles = [];
      for (const item of items) {
        if (item.type.startsWith('image/')) {
          const file = item.getAsFile();
          if (file) imageFiles.push(file);
        }
      }
      if (imageFiles.length > 0) {
        e.preventDefault();
        handleFileUpload(imageFiles);
      }
    }
    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [open, productId, variantId]);

  async function handleAdd() {
    const trimmed = url.trim();
    if (!trimmed || !productId) return;
    // Client-side URL validation
    if (!trimmed.startsWith('http://') && !trimmed.startsWith('https://') && !trimmed.startsWith('//')) {
      setUrlError('Enter a valid image URL starting with https://');
      return;
    }
    setAdding(true);
    setUrlError(null);
    try {
      const data = await api.uploadImageUrl(productId, variantId, trimmed);
      if (data.image) {
        onImagesChange([...images, data.image]);
        setPendingUrlImageId(data.image.id);
      } else {
        setUrlError('This image has already been added to this product.');
      }
      setUrl('');
    } catch (err) {
      // Show URL errors inline below input, not as toasts
      const msg = (err.message || '').toLowerCase();
      if (msg.includes('url') || msg.includes('https') || msg.includes('invalid') || msg.includes('format'))
        setUrlError(err.message);
      else if (msg.includes('session') || msg.includes('401'))
        setUrlError('Your session has expired. Please refresh the page and try again.');
      else if (msg.includes('failed to fetch') || msg.includes('network'))
        setUrlError('Upload interrupted — check your internet connection and try again.');
      else
        setUrlError(err.message || 'Could not add image. Please try again.');
    }
    setAdding(false);
  }

  function handleDelete(imageId) {
    const prev = images;
    onImagesChange(images.filter(img => img.id !== imageId));
    api.delete(`/images/${imageId}`).catch(() => {
      onImagesChange(prev);
      onToast?.('Could not remove image.');
    });
  }

  async function handleFileUpload(files) {
    if (!files?.length || !productId) return;
    const fileArray = Array.from(files);
    setUploading(true);
    const updated = [...images];
    for (let i = 0; i < fileArray.length; i++) {
      setUploadProgress(`${i + 1} / ${fileArray.length}`);
      try {
        const data = await api.uploadImage(fileArray[i], productId, variantId);
        if (data.image) {
          updated.push(data.image);
          onImagesChange([...updated]);
        } else {
          onToast?.(`${fileArray[i].name} was skipped (duplicate).`);
        }
      } catch (err) {
        const msg = (err.message || '').toLowerCase();
        let friendly = err.message;
        if (msg.includes('too large') || msg.includes('payload'))
          friendly = 'Image too large (max 20 MB)';
        else if (msg.includes('not a valid image') || msg.includes('unsupported'))
          friendly = 'Unsupported image format';
        else if (msg.includes('failed to fetch') || msg.includes('network'))
          friendly = 'Upload interrupted — check your connection';
        else if (msg.includes('session') || msg.includes('401'))
          friendly = 'Session expired — refresh the page';
        onToast?.(`Could not upload ${fileArray[i].name}: ${friendly}`);
      }
    }
    setUploading(false);
    setUploadProgress(null);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  const MAX_VISIBLE = 3;
  const visible  = images.slice(0, MAX_VISIBLE);
  const overflow = images.length - MAX_VISIBLE;

  // Phase 4: match status indicator color + tooltip
  function matchStatusDot(img) {
    const status = img.match_status;
    if (!status || status === 'merchant_assigned') return null;
    const conf = img.match_confidence;
    const method = img.match_method;
    const color = status === 'auto_matched' ? 'var(--color-green)'
      : status === 'needs_review' ? 'var(--color-yellow)'
      : status === 'unmatched' ? 'var(--color-red)' : null;
    if (!color) return null;
    const tip = `Match: ${(method || 'unknown').replace(/_/g, ' ')}${conf != null ? `, confidence: ${(conf * 100).toFixed(0)}%` : ''}`;
    return (
      <div title={tip} style={{
        position: 'absolute', bottom: -2, right: -2,
        width: 8, height: 8, borderRadius: '50%',
        background: color, border: '1.5px solid var(--color-surface)',
        zIndex: 1,
      }} />
    );
  }

  return (
    <div ref={cellRef} style={{ position: 'relative', width: '100%' }}>

      {/* ── Cell trigger ── */}
      <div
        onClick={() => setOpen(v => !v)}
        style={{ cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4, width: '100%', minHeight: 23 }}
      >
        {images.length === 1 && (
          <div style={{ position: 'relative', flexShrink: 0 }}>
            <img
              src={images[0].image_url} alt={images[0].image_alt || ''}
              onError={e => { e.target.style.display = 'none'; }}
              style={{
                width: 23, height: 23, borderRadius: 4, objectFit: 'cover',
                background: 'var(--color-surface-2)', flexShrink: 0,
                border: `1px solid ${open ? 'var(--color-primary)' : 'var(--color-border)'}`,
                outline: open ? '2px solid var(--color-primary)' : 'none',
                outlineOffset: 2, transition: 'all 0.12s',
              }}
            />
            {matchStatusDot(images[0])}
          </div>
        )}

        {images.length > 1 && (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 4,
            outline: open ? '2px solid var(--color-primary)' : 'none',
            outlineOffset: 2, borderRadius: 6, transition: 'outline 0.12s',
          }}>
            {visible.map((img, i) => (
              <div key={img.id || i} style={{ position: 'relative', flexShrink: 0 }}>
                <img src={img.image_url} alt=""
                  onError={e => { e.target.style.display = 'none'; }}
                  style={{
                    width: 23, height: 23, borderRadius: 4, objectFit: 'cover',
                    background: 'var(--color-surface-2)', flexShrink: 0,
                    border: '1px solid var(--color-border)',
                  }}
                />
                {matchStatusDot(img)}
              </div>
            ))}
            {overflow > 0 && (
              <div style={{
                height: 23, padding: '0 6px', borderRadius: 4,
                background: 'var(--color-surface-2)',
                border: '1px solid var(--color-border)',
                display: 'flex', alignItems: 'center',
                fontSize: 11, fontWeight: 600, color: 'var(--color-text-2)',
                whiteSpace: 'nowrap',
              }}>+{overflow}</div>
            )}
          </div>
        )}
      </div>

      {/* ── Popover ── */}
      {open && (
        <div style={{
          position: 'absolute', top: 'calc(100% + 8px)', left: 0, zIndex: 200,
          background: 'var(--color-surface)',
          border: '1px solid var(--color-border)',
          borderRadius: 'var(--r-lg)',
          boxShadow: '0 8px 32px rgba(0,0,0,0.10), 0 2px 8px rgba(0,0,0,0.06)',
          padding: 14, width: 300, boxSizing: 'border-box',
        }}>
          <div style={{
            display: 'flex', alignItems: 'center',
            justifyContent: 'space-between', marginBottom: 12,
          }}>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text)' }}>
              {images.length === 0 ? 'Add image' : `Images · ${images.length}`}
            </span>
            <button
              onClick={() => setOpen(false)}
              style={{
                width: 22, height: 22, borderRadius: 'var(--r-sm)',
                background: 'none', border: 'none', cursor: 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: 'var(--color-text-3)', fontSize: 16, lineHeight: 1,
              }}
            >×</button>
          </div>

          {images.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 12 }}>
              {images.map((img, i) => (
                <ImageThumb
                  key={img.id || i}
                  src={img.image_url}
                  onDelete={img.id ? () => handleDelete(img.id) : null}
                  onError={img.id === pendingUrlImageId ? () => {
                    onImagesChange(images.filter(x => x.id !== pendingUrlImageId));
                    api.delete(`/images/${pendingUrlImageId}`).catch(() => {});
                    setUrlError("That URL didn't load as an image. Paste a direct link to an image file (JPG, PNG, WebP, etc).");
                    setPendingUrlImageId(null);
                  } : undefined}
                />
              ))}
            </div>
          )}

          <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
            <input
              ref={inputRef}
              type="text"
              value={url}
              onChange={e => { setUrl(e.target.value); setUrlError(null); }}
              onKeyDown={e => {
                if (e.key === 'Enter') { e.preventDefault(); handleAdd(); }
                if (e.key === 'Escape') { e.stopPropagation(); setOpen(false); }
              }}
              placeholder={images.length === 0 ? 'Paste image URL…' : 'Add another URL…'}
              style={{
                flex: 1, minWidth: 0, height: 32, padding: '0 10px', fontSize: 12,
                border: '1px solid var(--color-border-strong, #CBD5E1)',
                borderRadius: 8,
                background: 'var(--color-surface-2)', color: 'var(--color-text)',
                outline: 'none', fontFamily: 'inherit',
                transition: 'border-color 0.15s',
              }}
            />
            <button
              onClick={handleAdd}
              disabled={adding || !url.trim()}
              style={{
                height: 32, padding: '0 12px',
                background: url.trim() ? 'var(--color-primary)' : 'var(--color-surface-2)',
                color: url.trim() ? '#fff' : 'var(--color-text-3)',
                border: 'none', borderRadius: 'var(--r-md)',
                fontSize: 12, fontWeight: 600,
                cursor: url.trim() && !adding ? 'pointer' : 'default',
                fontFamily: 'inherit', whiteSpace: 'nowrap',
                opacity: adding ? 0.7 : 1,
                transition: 'background 0.12s, color 0.12s',
              }}
            >{adding ? '…' : 'Add'}</button>
          </div>

          {/* Hidden file input */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*,.jpg,.jpeg,.png,.webp,.avif,.gif,.heic,.heif,.tiff,.tif,.bmp,.svg"
            multiple
            style={{ display: 'none' }}
            onChange={e => { filePickerOpenRef.current = false; handleFileUpload(e.target.files); }}
          />

          {urlError && (
            <p style={{
              margin: '6px 0 0', fontSize: 11, lineHeight: 1.4,
              color: 'var(--color-red)',
            }}>{urlError}</p>
          )}

          {/* Or divider */}
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, margin: '10px 0 0',
          }}>
            <div style={{ flex: 1, height: 1, background: 'var(--color-border)' }} />
            <span style={{ fontSize: 11, color: 'var(--color-text-3)', whiteSpace: 'nowrap' }}>or</span>
            <div style={{ flex: 1, height: 1, background: 'var(--color-border)' }} />
          </div>

          {/* Upload button */}
          <button
            onClick={() => { filePickerOpenRef.current = true; fileInputRef.current?.click(); }}
            disabled={uploading}
            style={{
              width: '100%', height: 32, marginTop: 8,
              background: 'none',
              border: '1px solid var(--color-border)',
              borderRadius: 'var(--r-md)',
              fontSize: 12, fontWeight: 500,
              color: uploading ? 'var(--color-text-3)' : 'var(--color-text-2)',
              cursor: uploading ? 'default' : 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
              fontFamily: 'inherit',
              transition: 'border-color 0.12s, color 0.12s',
            }}
            onMouseEnter={e => {
              if (!uploading) {
                e.currentTarget.style.borderColor = 'var(--color-primary)';
                e.currentTarget.style.color = 'var(--color-primary)';
              }
            }}
            onMouseLeave={e => {
              e.currentTarget.style.borderColor = 'var(--color-border)';
              e.currentTarget.style.color = uploading ? 'var(--color-text-3)' : 'var(--color-text-2)';
            }}
          >
            {uploading ? (
              <>
                <UploadSpinner />
                {uploadProgress ? `Uploading ${uploadProgress}…` : 'Uploading…'}
              </>
            ) : (
              <>
                <Upload size={12} strokeWidth={2} />
                Upload image
              </>
            )}
          </button>

          {images.length > 0 && (
            <div style={{
              fontSize: 11, color: 'var(--color-text-3)',
              marginTop: 8, lineHeight: 1.5,
            }}>
              Click × on any image to remove it.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── ImageStrip ───────────────────────────────────────────────────────────────
function ImageStrip({ images = [], size = 32 }) {
  const MAX = 3;
  const visible  = images.slice(0, MAX);
  const overflow = images.length - MAX;
  if (images.length === 0) return (
    <div style={{
      width: size, height: size, borderRadius: 4,
      background: 'var(--color-surface-2)',
      border: '1.5px dashed var(--color-border)',
      display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <Plus size={12} color="var(--color-text-3)" />
    </div>
  );
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
      {visible.map((img, i) => (
        <img
          key={i}
          src={img.image_url}
          alt=""
          style={{ width: size, height: size, borderRadius: 4, objectFit: 'cover', background: 'var(--color-surface-2)', flexShrink: 0 }}
          onError={e => { e.target.style.display = 'none'; }}
        />
      ))}
      {overflow > 0 && (
        <span style={{
          fontSize: 11, fontWeight: 600, color: 'var(--color-text-2)',
          background: 'var(--color-surface-2)', border: '1px solid var(--color-border)',
          borderRadius: 4, padding: '0 5px', height: size,
          display: 'flex', alignItems: 'center', flexShrink: 0,
        }}>
          +{overflow}
        </span>
      )}
    </div>
  );
}

// ─── ExportModal ──────────────────────────────────────────────────────────────
function ExportModal({ catalogId, stats, onClose, onFixErrors, onExportSuccess, products: allProducts }) {
  const [loading, setLoading] = useState(false);
  const [err, setErr]         = useState('');

  const variantCount = stats?.variant_count ?? 0;
  const productCount = stats?.product_count ?? 0;

  // Products where ALL variants have no image_url
  const missingImgCount = useMemo(() => {
    if (!allProducts) return 0;
    return allProducts.filter(p => {
      const vars = p.variants || [];
      return vars.length > 0 && vars.every(v => !v.image_url);
    }).length;
  }, [allProducts]);

  // Fix 1: Flag buckets locked per spec v1.3
  const WILL_FAIL_TYPES = new Set(['missing_title', 'missing_price', 'invalid_price', 'invalid_image_url']);
  const NON_BLOCKING_TYPES = new Set(['missing_sku', 'zero_price']);

  // Products with at least one unresolved warning (no errors)
  const warningProductIds = useMemo(() => {
    if (!allProducts) return [];
    return allProducts.filter(p =>
      p.row_status === 'yellow'
    ).map(p => p.id);
  }, [allProducts]);

  const errorAnalysis = useMemo(() => {
    if (!allProducts) return null;
    const willFailFieldCounts = {};
    let willFailProductCount = 0;
    let warningOnlyProductCount = 0;
    for (const p of allProducts) {
      let hasWillFail = false;
      let hasWarning = false;
      const willFailFields = new Set();
      for (const v of p.variants || []) {
        for (const f of v.flags || []) {
          if (f.resolved || RETIRED_FLAG_TYPES.has(f.flag_type)) continue;
          const ft = f.flag_type || f.field_name || '';
          if (f.severity === 'error' && WILL_FAIL_TYPES.has(ft)) { hasWillFail = true; willFailFields.add(ft); }
          else if (f.severity === 'warning') { hasWarning = true; }
        }
      }
      if (hasWillFail) {
        willFailProductCount++;
        for (const field of willFailFields) {
          willFailFieldCounts[field] = (willFailFieldCounts[field] || 0) + 1;
        }
      } else if (hasWarning) {
        warningOnlyProductCount++;
      }
    }
    const fieldLabels = { missing_title: 'Title', missing_price: 'Price', invalid_price: 'Price', invalid_image_url: 'Image URL' };
    if (willFailProductCount > 0) {
      const topField = Object.entries(willFailFieldCounts).sort((a, b) => b[1] - a[1])[0]?.[0] || '';
      const label = fieldLabels[topField] || (topField.replace(/_/g, ' ') || 'required fields');
      return {
        state: 'will_fail',
        headline: `${willFailProductCount} product${willFailProductCount !== 1 ? 's' : ''} have errors that will block Shopify import`,
        detail: `Missing ${label} on ${willFailProductCount} product${willFailProductCount !== 1 ? 's' : ''} — fix before export`,
      };
    }
    if (warningOnlyProductCount > 0) {
      return {
        state: 'warnings_only',
        headline: `${warningOnlyProductCount} product${warningOnlyProductCount !== 1 ? 's' : ''} have warnings`,
        detail: 'These products may import with missing or incorrect data.',
      };
    }
    return { state: 'clean', headline: '', detail: '' };
  }, [allProducts]);

  // Red-row products and ready count for the export gate (must precede doExport)
  const redProductIds = useMemo(() => {
    if (!allProducts) return [];
    return allProducts.filter(p => p.row_status === 'red').map(p => p.id);
  }, [allProducts]);

  const readyCount = productCount - redProductIds.length;

  useEffect(() => {
    function onKey(e) { if (e.key === 'Escape') onClose(); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  async function doExport(excludeIds) {
    if (loading) return;
    capture('export_click_errors', { red_row_count: redProductIds.length, chose_ready_only: !!excludeIds?.length });
    setLoading(true);
    setErr('');
    try {
      const opts = excludeIds?.length ? { exclude_product_ids: excludeIds } : {};
      const data = await api.exportCatalog(catalogId, opts);
      if (data.csv_url) {
        const a = document.createElement('a');
        a.href = data.csv_url;
        a.download = '';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
      // Fix 5: Toast gets actual exported product count
      onExportSuccess(excludeIds?.length ? readyCount : productCount);
    } catch (e) {
      setErr(e.message || 'Export failed. Please try again.');
      setLoading(false);
    }
  }

  // Fix 2: Three modal states
  const modalState = errorAnalysis?.state || 'clean';

  const titleText = modalState === 'will_fail'
    ? `${redProductIds.length} product${redProductIds.length !== 1 ? 's' : ''} have errors that will block Shopify import`
    : modalState === 'warnings_only'
      ? `${productCount} product${productCount !== 1 ? 's' : ''} ready to export`
      : `Export ${productCount} product${productCount !== 1 ? 's' : ''} to Shopify`;

  return (
    <div
      style={{ ...overlayStyle, background: 'rgba(0,0,0,0.45)' }}
      onClick={e => e.target === e.currentTarget && onClose()}
    >
      <style>{`
        @keyframes exportModalIn {
          from { opacity: 0; transform: scale(0.98); }
          to   { opacity: 1; transform: scale(1); }
        }
      `}</style>
      <div style={{
        background: 'var(--color-surface)',
        borderRadius: 12,
        padding: 24,
        width: '100%',
        maxWidth: 440,
        border: '1px solid var(--color-border)',
        boxShadow: '0 10px 30px rgba(0,0,0,0.08)',
        animation: 'exportModalIn 0.18s ease-out',
        position: 'relative',
      }}>

        {/* Close button */}
        <button onClick={onClose} style={{ ...iconBtn, position: 'absolute', top: 16, right: 16 }}>
          <X size={16} />
        </button>

        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, paddingRight: 28 }}>
          {modalState === 'will_fail'
            ? <AlertCircle size={20} style={{ color: 'var(--color-red-text)', flexShrink: 0 }} />
            : modalState === 'warnings_only'
              ? <AlertTriangle size={20} style={{ color: 'var(--color-yellow-text)', flexShrink: 0 }} />
              : <CheckCircle2 size={20} style={{ color: 'var(--color-green-text)', flexShrink: 0 }} />
          }
          <h2 style={{ fontSize: 16, fontWeight: 600, color: 'var(--color-text)', lineHeight: 1.3, margin: 0 }}>
            {titleText}
          </h2>
        </div>

        {/* Inline error message */}
        {err && (
          <div style={{
            background: 'var(--color-red-bg)', border: '1px solid var(--color-red-border)',
            borderRadius: 8, padding: '10px 14px', marginTop: 16,
            fontSize: 13, color: 'var(--color-red-text)',
          }}>
            {err}
          </div>
        )}

        {/* Info block */}
        <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 6 }}>
          <div style={{ fontSize: 14, fontWeight: 500, color: 'var(--color-text)', fontVariantNumeric: 'tabular-nums' }}>
            {`${productCount} product${productCount !== 1 ? 's' : ''} · ${variantCount} variant${variantCount !== 1 ? 's' : ''}`}
          </div>
          {modalState === 'will_fail' && errorAnalysis && (
            <>
              <div style={{ fontSize: 13, color: 'var(--color-red-text)' }}>
                {errorAnalysis.detail}
              </div>
              <div style={{ fontSize: 13, color: 'var(--color-text-3)' }}>
                These products may import with missing or incorrect data.
              </div>
            </>
          )}
          {modalState === 'warnings_only' && errorAnalysis && (
            <div style={{ fontSize: 13, color: 'var(--color-yellow-text)' }}>
              {errorAnalysis.detail}
            </div>
          )}
        </div>

        {/* Missing images note */}
        {missingImgCount > 0 && (
          <div style={{ marginTop: 16, fontSize: 13, color: 'var(--color-text-2)' }}>
            {missingImgCount} product{missingImgCount !== 1 ? 's' : ''} are missing images — you can add them later in Shopify
          </div>
        )}

        {/* CTA */}
        <div style={{ marginTop: 20 }}>
          {modalState === 'clean' && (
            <div style={{ fontSize: 12, color: 'var(--color-text-3)', textAlign: 'center', marginBottom: 8 }}>
              Ready for Shopify import
            </div>
          )}
          {modalState === 'will_fail' ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {/* Two equal-weight buttons */}
              <button
                onClick={() => doExport(redProductIds)}
                disabled={loading || readyCount === 0}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  width: '100%', height: 44, borderRadius: 10,
                  background: 'var(--color-primary)', color: '#fff', border: 'none',
                  fontSize: 14, fontWeight: 600,
                  cursor: (loading || readyCount === 0) ? 'not-allowed' : 'pointer',
                  opacity: (loading || readyCount === 0) ? 0.75 : 1,
                }}
              >
                {loading
                  ? <><ExportSpinner />Exporting…</>
                  : <><Download size={16} />Export ready products ({readyCount})</>
                }
              </button>
              <button
                onClick={() => doExport(null)}
                disabled={loading}
                style={{
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  width: '100%', height: 44, borderRadius: 10,
                  background: 'transparent', color: 'var(--color-text)',
                  border: '1px solid var(--color-border)',
                  fontSize: 14, fontWeight: 500,
                  cursor: loading ? 'not-allowed' : 'pointer',
                }}
              >
                <Download size={16} />Export all products
              </button>
              <button
                onClick={() => { onClose(); onFixErrors(); }}
                disabled={loading}
                style={{
                  background: 'none', border: 'none', padding: '4px 0',
                  fontSize: 13, color: 'var(--color-text-3)',
                  cursor: 'pointer', textAlign: 'center', width: '100%',
                  textDecoration: 'underline', textUnderlineOffset: 2,
                }}
              >
                Fix errors first
              </button>
            </div>
          ) : (
            /* warnings_only and clean — single button */
            <button
              onClick={() => doExport(null)}
              disabled={loading}
              style={{
                display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                width: '100%', height: 44, borderRadius: 10,
                background: 'var(--color-primary)', color: '#fff', border: 'none',
                fontSize: 14, fontWeight: 600,
                cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.75 : 1,
              }}
            >
              {loading
                ? <><ExportSpinner />Exporting…</>
                : <><Download size={16} />Download Shopify CSV</>
              }
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function ExportSpinner() {
  return (
    <svg
      style={{ width: 15, height: 15, animation: 'exportSpin 0.7s linear infinite', flexShrink: 0 }}
      viewBox="0 0 24 24" fill="none"
    >
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.25" />
      <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
      <style>{`@keyframes exportSpin { to { transform: rotate(360deg); } }`}</style>
    </svg>
  );
}

function UploadSpinner() {
  return (
    <svg
      style={{ width: 12, height: 12, animation: 'exportSpin 0.7s linear infinite', flexShrink: 0 }}
      viewBox="0 0 24 24" fill="none"
    >
      <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" strokeOpacity="0.25" />
      <path d="M12 2a10 10 0 0 1 10 10" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

// ─── UndoToast ───────────────────────────────────────────────────────────────
function UndoToast({ message, onUndo, onDismiss, duration = 5000 }) {
  const [elapsed, setElapsed] = useState(0);
  const startRef = useRef(Date.now());
  const rafRef = useRef(null);
  const DURATION = duration;

  useEffect(() => {
    function tick() {
      const e = Date.now() - startRef.current;
      if (e >= DURATION) { onDismiss(); return; }
      setElapsed(e);
      rafRef.current = requestAnimationFrame(tick);
    }
    rafRef.current = requestAnimationFrame(tick);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, []);

  const pctLeft = Math.max(0, 1 - elapsed / DURATION) * 100;
  return (
    <div style={{
      position: 'fixed', bottom: 24, left: 24, zIndex: 200,
      background: 'var(--color-text)', color: '#fff', borderRadius: 10,
      padding: '12px 16px', minWidth: 280,
      boxShadow: '0 8px 32px rgba(0,0,0,0.10)',
      animation: 'toastIn 0.2s ease-out', overflow: 'hidden',
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
        <span style={{ fontSize: 13, fontWeight: 500 }}>{message}</span>
        <button onClick={onUndo} style={{ background: 'none', border: 'none', color: 'var(--color-primary)', fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}>Undo</button>
      </div>
      <div style={{ position: 'absolute', bottom: 0, left: 0, height: 3, background: 'var(--color-primary)', width: `${pctLeft}%`, transition: 'width 0.06s linear' }} />
    </div>
  );
}

// ─── Toast ───────────────────────────────────────────────────────────────────
function Toast({ msg, onDone }) {
  useEffect(() => { const t = setTimeout(onDone, 3000); return () => clearTimeout(t); }, []);
  return (
    <div style={{
      position: 'fixed', bottom: 24, left: '50%', transform: 'translateX(-50%)',
      background: 'var(--color-text)', color: 'var(--color-surface)',
      borderRadius: 'var(--r-full)', padding: '10px 20px',
      fontSize: 13, fontWeight: 500, zIndex: 100,
      boxShadow: 'var(--shadow-lg)', animation: 'toastIn 0.2s ease-out', whiteSpace: 'nowrap',
    }}>{msg}</div>
  );
}


// ─── ExportToast ─────────────────────────────────────────────────────────────
function ExportToast({ count, onDismiss }) {
  useEffect(() => {
    const t = setTimeout(onDismiss, 4000);
    return () => clearTimeout(t);
  }, []);
  return (
    <div style={{
      position: 'fixed', bottom: 24, right: 24, zIndex: 2000,
      width: 280, padding: '12px 16px',
      background: 'var(--color-surface)',
      border: '1px solid var(--color-border)',
      borderLeft: '3px solid var(--color-green)',
      borderRadius: 8,
      boxShadow: '0 4px 16px rgba(0,0,0,0.12)',
      animation: 'exportToastIn 0.2s ease-out',
      display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12,
    }}>
      <style>{`
        @keyframes exportToastIn {
          from { transform: translateX(100%); opacity: 0; }
          to   { transform: translateX(0);   opacity: 1; }
        }
      `}</style>
      <span style={{ fontSize: 13, color: 'var(--color-text)' }}>
        Shopify CSV downloaded — {count} product{count !== 1 ? 's' : ''} included
      </span>
      <button onClick={onDismiss} style={{ ...iconBtn, flexShrink: 0 }}>
        <X size={14} />
      </button>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function ReviewTable() {
  const { catalogId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const jobId = searchParams.get('job_id');

  const [products,       setProducts]       = useState([]);
  const [catalog,        setCatalog]        = useState(null);
  const [stats,          setStats]          = useState(null);
  const [loading,        setLoading]        = useState(true);
  const [loadError,      setLoadError]      = useState(null);
  const [filter,         setFilter]         = useState(() => {
    try {
      const saved = localStorage.getItem(`cc_review_state_${catalogId}`);
      if (saved) { const s = JSON.parse(saved); if (s.filter) return s.filter; }
    } catch {}
    return 'all';
  });
  const [activeFilters, setActiveFilters] = useState({ status: [], type: [], tags: [], price: [], image: [], category: [] });
  const [showFilterPanel, setShowFilterPanel] = useState(false);
  const [expandedFilterSections, setExpandedFilterSections] = useState({});
  // extraFilterSections / showAddFilter removed — all filter sections are now default
  const filterPanelRef = useRef(null);
  const filterBtnRef = useRef(null);
  const [expanded,       setExpanded]        = useState(new Set());
  const [drawerOpen,     setDrawerOpen]     = useState(null); // DEPRECATED — kept for compat, driven by sourcePanelTarget
  const [sourcePanelTarget, setSourcePanelTarget] = useState(null); // { productId, variantId } | null
  const sourcePanelTriggerRef = useRef(null); // tracks the row element that opened the panel
  const [sourcePanelWidth, setSourcePanelWidth] = useState(() => {
    try { const w = localStorage.getItem('cc_source_panel_w'); return w ? parseInt(w, 10) : 420; } catch { return 420; }
  });
  const [showExport,     setShowExport]     = useState(false);
  const [exportToast,    setExportToast]    = useState(null); // null | { count: N }
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);
  const [showImportImg,  setShowImportImg]  = useState(false);
  const [userPlan,       setUserPlan]       = useState(null);
  const [previewUsed,    setPreviewUsed]    = useState(false);
  const [reviewedOnceDismissed, setReviewedOnceDismissed] = useState(() => {
    try { return !!localStorage.getItem('cc_reviewed_once'); } catch { return false; }
  });
  const [bannerDismissed, setBannerDismissed] = useState(() => {
    try { return !!localStorage.getItem(`cc_banner_${catalogId}`); } catch { return false; }
  });
  const [toastMsg,    setToastMsg]   = useState(null);
  useDarkMode(); // initializes theme from localStorage
  const [catalogName, setCatalogName] = useState('');
  const [renameSaving, setRenameSaving] = useState(false);
  const [searchQuery,  setSearchQuery]  = useState('');
  const [searchOpen,   setSearchOpen]   = useState(false);
  const searchInputRef = useRef(null);
  const [contextMenu,  setContextMenu]  = useState(null); // { x, y, productId, variantId, type: 'product'|'variant' } | null
  const [undoToast,    setUndoToast]    = useState(null);
  const [undoKey,      setUndoKey]      = useState(0);
  const [selectedIds,  setSelectedIds]  = useState(new Set());
  const [selectedVariantIds, setSelectedVariantIds] = useState(new Set());
  const lastSelectedRef = useRef(null); // { type: 'product'|'variant', id, productId? }
  const [columnSort, setColumnSort] = useState({ column: null, direction: null });
  const [customColumns, setCustomColumns] = useState([]);
  const [customValues,  setCustomValues]  = useState({});
  const [productImagesMap, setProductImagesMap] = useState({});
  const [variantImagesMap, setVariantImagesMap] = useState({});
  const [showCustomColModal, setShowCustomColModal] = useState(false);
  const [editingCustomCol,   setEditingCustomCol]   = useState(null);
  const [showColReorder,     setShowColReorder]     = useState(false);
  const [showColDropdown,    setShowColDropdown]    = useState(false);
  const [merchantColPrefs,   setMerchantColPrefs]   = useState(() => {
    try { const s = localStorage.getItem(`cc_cols_${catalogId}`); return s ? JSON.parse(s) : COL_FRESH_DEFAULTS; } catch { return COL_FRESH_DEFAULTS; }
  });

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));
  const [dragOverId, setDragOverId] = useState(null); // id of item being hovered over during drag
  const [dragOverEdge, setDragOverEdge] = useState('below'); // 'above' or 'below' — which edge to show the indicator on
  const [activeDragId, setActiveDragId] = useState(null); // id of item currently being dragged

  // DnD is disabled when any sort, filter, or search is active (reordering a subset or non-default order is ambiguous)
  const dndDisabled = !!(searchQuery || (columnSort.column && columnSort.column !== 'display_order') || activeFilters.status.length > 0 || activeFilters.type.length > 0 || activeFilters.tags.length > 0 || (activeFilters.price || []).length > 0 || (activeFilters.image || []).length > 0 || (activeFilters.category || []).length > 0 || (filter !== 'all'));

  const [extractionComplete, setExtractionComplete] = useState(false);
  const [extractionPct,      setExtractionPct]      = useState(0);
  const [jobStatus,          setJobStatus]          = useState(null);
  const [bannerH,            setBannerH]            = useState(jobId ? 36 : 0);
  const [jobStartedAt,       setJobStartedAt]       = useState(null);
  const [completionBannerDismissed, setCompletionBannerDismissed] = useState(() => {
    try {
      return !!jobId && !!catalogId && localStorage.getItem(`cc_completion_${catalogId}`) === jobId;
    } catch { return false; }
  });
  const [queuePosition, setQueuePosition] = useState(0);
  const [extractionWarnings, setExtractionWarnings] = useState([]);
  const [warningBannerDismissed, setWarningBannerDismissed] = useState(false);
  const [catalogFlags, setCatalogFlags] = useState([]);
  const [zipBannerDismissed, setZipBannerDismissed] = useState(false);
  // UB#10 (Phase 2B SD4) — catalog-level flag info banner dismissal.
  const [catalogFlagsBannerDismissed, setCatalogFlagsBannerDismissed] = useState(false);
  // T11 (2026-05-27 dispatch) — Class 2 IGNORED_HEADERS_V1 banner. Reads
  // ignored_headers_v1 from /api/catalogs/:id top-level field (aggregated
  // server-side from catalog_files.role_reasons.columnMap.ignoredHeadersV1Hit).
  const [class2BannerDismissed, setClass2BannerDismissed] = useState(false);
  const [bulkBarH,           setBulkBarH]           = useState(0);
  const [colSearchQuery,     setColSearchQuery]     = useState('');
  const [showMoreMetafields, setShowMoreMetafields] = useState(false);

  const scrollRef          = useRef(null);
  const tableRef           = useRef(null);
  const bulkBarRef         = useRef(null);
  const navRef             = useRef({ toggleCollapse: null, expanded: new Set() });
  const issueNavRef        = useRef({ sortedProducts: [], sourcePanelTarget: null }); // J/K issue navigation
  const undoTimerRef       = useRef(null);
  const saveControllers    = useRef(new Map());
  const colDropdownRef     = useRef(null);
  const scrollTopRef       = useRef(0);
  const deletedSnapshotRef = useRef(new Map()); // opKey → snapshot array
  const deleteOpCounter    = useRef(0);
  const scrollSaveTimerRef = useRef(null);
  const restoredScrollRef  = useRef(false);
  const pendingDeleteRef   = useRef(new Set()); // P0-D: product IDs awaiting undo window expiry
  const extractSinceRef    = useRef(null);
  const extractedIdsRef    = useRef(new Set());
  const animatedIdsRef     = useRef(new Set());
  const newRowStaggerRef   = useRef(new Map());
  // Realtime: shared interval refs so Realtime and polling can both control them
  const prodIntervalRef     = useRef(null);
  const jobIntervalRef      = useRef(null);
  const pollNewProductsRef  = useRef(null);
  const pollJobStatusRef    = useRef(null);
  const jobStartedAtRef     = useRef(null);
  // PostHog "first" guards — fire once per page load
  const phTableRendered    = useRef(false);
  const phFirstEdit        = useRef(false);
  const phFirstFilter      = useRef(false);
  const phFirstSource      = useRef(false);

  // ── Source panel focus management (WCAG) ────────────────────────────────────
  const prevSourcePanelTarget = useRef(sourcePanelTarget);
  useEffect(() => {
    const prev = prevSourcePanelTarget.current;
    prevSourcePanelTarget.current = sourcePanelTarget;

    if (!prev && sourcePanelTarget) {
      // Panel opened — remember the triggering row and move focus to close button
      const triggerRow = document.querySelector(
        `[data-product-id="${sourcePanelTarget.productId}"]`
      );
      sourcePanelTriggerRef.current = triggerRow;
      // Defer to let panel render
      requestAnimationFrame(() => {
        const closeBtn = document.querySelector('[aria-label="Close source panel"]');
        if (closeBtn) closeBtn.focus();
      });
    } else if (prev && !sourcePanelTarget) {
      // Panel closed — return focus to the triggering row
      const row = sourcePanelTriggerRef.current;
      if (row && document.contains(row)) {
        row.focus({ preventScroll: true });
        // Ensure row is focusable
        if (!row.getAttribute('tabindex')) {
          row.setAttribute('tabindex', '-1');
          row.focus({ preventScroll: true });
        }
      }
      sourcePanelTriggerRef.current = null;
    }
  }, [sourcePanelTarget]);

  useEffect(() => {
    loadData();
    loadUserPlan();
    try {
      const s = localStorage.getItem(`cc_cols_${catalogId}`);
      setMerchantColPrefs(s ? JSON.parse(s) : COL_FRESH_DEFAULTS);
    } catch { setMerchantColPrefs(COL_FRESH_DEFAULTS); }
  }, [catalogId]);

  useEffect(() => {
    window.__diagCols = () => {
      const table = tableRef.current;
      if (!table) { console.log('[ColDiag] table not mounted'); return; }
      const headerCells = table.querySelectorAll('thead tr th');
      const prodRow = table.querySelector('tr.prod-row');
      const varRow  = table.querySelector('tr.var-row');
      const prodCells = prodRow ? prodRow.querySelectorAll('td') : [];
      const varCells  = varRow  ? varRow.querySelectorAll('td')  : [];
      console.log('[ColDiag] Header TH count:', headerCells.length);
      console.log('[ColDiag] ProductRow TD count:', prodCells.length);
      console.log('[ColDiag] VariantRow TD count:', varCells.length);
      const labels = Array.from(headerCells).map((th, i) => `col${i+1}:${th.textContent.trim()||'(empty)'}`);
      console.log('[ColDiag] Header columns:', labels.join(' | '));
      if (varCells.length) {
        const varLabels = Array.from(varCells).map((td, i) => `col${i+1}:${td.getAttribute('data-col')||td.textContent.trim().slice(0,10)||'(empty)'}`);
        console.log('[ColDiag] VariantRow cells:', varLabels.join(' | '));
      }
    };
    return () => { delete window.__diagCols; };
  }, []);

  useEffect(() => {
    if (!showColDropdown) return;
    function handleClickOutside(e) {
      if (colDropdownRef.current && !colDropdownRef.current.contains(e.target)) setShowColDropdown(false);
    }
    function handleEsc(e) { if (e.key === 'Escape') setShowColDropdown(false); }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);
    return () => { document.removeEventListener('mousedown', handleClickOutside); document.removeEventListener('keydown', handleEsc); };
  }, [showColDropdown]);

  // ── Filter panel: click-outside + Escape ────────────────────────────────────
  useEffect(() => {
    if (!showFilterPanel) return;
    function handleClickOutside(e) {
      if (filterPanelRef.current && !filterPanelRef.current.contains(e.target) &&
          filterBtnRef.current && !filterBtnRef.current.contains(e.target)) {
        setShowFilterPanel(false);
      }
    }
    function handleEsc(e) { if (e.key === 'Escape') setShowFilterPanel(false); }
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('keydown', handleEsc);
    return () => { document.removeEventListener('mousedown', handleClickOutside); document.removeEventListener('keydown', handleEsc); };
  }, [showFilterPanel]);


  // ── Computed filter options from products ───────────────────────────────────
  const filterOptions = useMemo(() => {
    const statusVals = [];
    if (products.some(p => p.row_status === 'red' || p.row_status === 'yellow')) statusVals.push('needs_review');
    if (products.some(p => p.row_status === 'green')) statusVals.push('green');
    return {
      status: statusVals,
      type: [...new Set(products.map(p => p.product_type).filter(Boolean))].sort(),
      tags: [...new Set(products.flatMap(p => mergeRenderTags(p).split(',').map(t => t.trim())).filter(Boolean))].sort(),
    };
  }, [products]);


  // ── P1-M: Resume review — persist filter + scroll product index ─────────────
  // Save filter to review state whenever it changes
  useEffect(() => {
    if (!catalogId) return;
    try {
      const raw = localStorage.getItem(`cc_review_state_${catalogId}`);
      const state = raw ? JSON.parse(raw) : {};
      state.filter = filter;
      localStorage.setItem(`cc_review_state_${catalogId}`, JSON.stringify(state));
    } catch {}
  }, [filter, catalogId]);

  // ── Extraction polling: runs only while a job_id is in the URL ───────────────
  useEffect(() => {
    if (!jobId) return;

    let mounted = true;
    extractSinceRef.current = null;
    jobStartedAtRef.current = null;
    setJobStartedAt(null);
    setBannerH(36);

    async function pollNewProducts() {
      if (!mounted) return;
      try {
        // Refresh catalog for pages_scanned / page_count
        try {
          const catRes = await api.getCatalog(catalogId);
          if (mounted && catRes?.catalog) {
            setCatalog(prev => prev ? { ...prev, ...catRes.catalog } : catRes.catalog);
          }
        } catch { /* non-fatal */ }

        const res = await api.getProductsSince(catalogId, extractSinceRef.current);
        const newProds = res.products || [];
        if (!mounted || newProds.length === 0) return;

        // Only accept products that already have their variant linked.
        const withVars = newProds.filter(p => (p.variants || []).length > 0);
        if (withVars.length > 0) {
          extractSinceRef.current = withVars[withVars.length - 1].created_at;
        }

        setProducts(prev => {
          const existingIds = new Set(prev.map(p => p.id));
          // P0-D: exclude products pending undo-delete so polls don't re-add them
          const pending = pendingDeleteRef.current;
          const fresh = withVars.filter(p => !existingIds.has(p.id) && !pending.has(p.id));
          if (fresh.length === 0) return prev;
          fresh.forEach((p, i) => {
            extractedIdsRef.current.add(p.id);
            newRowStaggerRef.current.set(p.id, Math.min(i, 15) * 40);
          });
          return [...prev, ...fresh];
        });
      } catch { /* non-fatal */ }
    }

    async function pollJobStatus() {
      if (!mounted) return;
      try {
        const status = await api.getJobStatus(jobId);
        if (!mounted) return;
        const pct = status.progress_pct;
        if (typeof pct === 'number') setExtractionPct(pct);
        if (typeof status.queue_position === 'number') setQueuePosition(status.queue_position);
        setJobStatus(status.status);
        if (status.created_at && !jobStartedAtRef.current) {
          jobStartedAtRef.current = status.created_at;
          setJobStartedAt(status.created_at);
        }
        if (status.status === 'complete' || status.status === 'completed_with_warnings' || status.status === 'failed') {
          clearInterval(prodIntervalRef.current);
          clearInterval(jobIntervalRef.current);
          prodIntervalRef.current = null;
          jobIntervalRef.current  = null;
          // P3.3: Fetch extraction warnings when job completed with degradation
          if (status.status === 'completed_with_warnings') {
            api.getCatalogWarnings(catalogId).then(res => {
              if (mounted && res?.warnings) setExtractionWarnings(res.warnings);
            }).catch(() => {});
          }
          // Final sweep to catch any products written since last poll
          await pollNewProducts();
          if (!mounted) return;
          setExtractionPct(100);
          setExtractionComplete(true);
          document.title = '✓ Products ready — CatalogConvert';
          // Re-fetch all products with finalized row_status and flags — polled products
          // may have been written before flag computation completed.
          await loadData();
          // Slide banner out then clean up URL
          setTimeout(() => {
            if (mounted) setBannerH(0);
          }, 350);
          setTimeout(() => {
            if (mounted) navigate(`/review/${catalogId}`, { replace: true });
          }, 3000);
        }
      } catch { /* non-fatal */ }
    }

    // Expose poll functions so Realtime effect and visibility handler can call them
    pollNewProductsRef.current = pollNewProducts;
    pollJobStatusRef.current   = pollJobStatus;

    pollNewProducts();
    prodIntervalRef.current = setInterval(pollNewProducts, 2000);

    pollJobStatus();
    jobIntervalRef.current = setInterval(pollJobStatus, 3000);

    return () => {
      mounted = false;
      clearInterval(prodIntervalRef.current);
      clearInterval(jobIntervalRef.current);
      prodIntervalRef.current = null;
      jobIntervalRef.current  = null;
    };
  }, [jobId, catalogId]);

  // ── Supabase Realtime: products/jobs/catalogs subscriptions ──────────────────
  // When connected, clears polling intervals (Realtime pushes updates ~100ms after
  // DB write). Falls back to polling automatically on error or disconnect.
  useEffect(() => {
    if (!jobId || !catalogId) return;

    const sbClient = getSupabaseRealtimeClient();

    function clearPollingIntervals() {
      clearInterval(prodIntervalRef.current);
      clearInterval(jobIntervalRef.current);
      prodIntervalRef.current = null;
      jobIntervalRef.current  = null;
    }

    function restorePollingIntervals() {
      if (!prodIntervalRef.current && pollNewProductsRef.current) {
        prodIntervalRef.current = setInterval(pollNewProductsRef.current, 2000);
      }
      if (!jobIntervalRef.current && pollJobStatusRef.current) {
        jobIntervalRef.current = setInterval(pollJobStatusRef.current, 3000);
      }
    }

    // products INSERT → call existing pollNewProducts (returns products with variants attached)
    const chProducts = sbClient
      .channel(`rt-products-${catalogId}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'products', filter: `catalog_id=eq.${catalogId}` },
        () => { pollNewProductsRef.current?.(); }
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          clearPollingIntervals();
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          restorePollingIntervals();
        }
      });

    // jobs UPDATE → call existing pollJobStatus (handles completion logic, warnings, navigation)
    const chJobs = sbClient
      .channel(`rt-jobs-${jobId}`)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'jobs', filter: `id=eq.${jobId}` },
        () => { pollJobStatusRef.current?.(); }
      )
      .subscribe();

    // catalogs UPDATE → update catalog state directly (page_count, pages_scanned, status)
    const chCatalogs = sbClient
      .channel(`rt-catalogs-${catalogId}`)
      .on('postgres_changes', { event: 'UPDATE', schema: 'public', table: 'catalogs', filter: `id=eq.${catalogId}` },
        (payload) => {
          const row = payload.new;
          if (row) setCatalog(prev => prev ? { ...prev, ...row } : row);
        }
      )
      .subscribe();

    return () => {
      sbClient.removeChannel(chProducts);
      sbClient.removeChannel(chJobs);
      sbClient.removeChannel(chCatalogs);
    };
  }, [jobId, catalogId]);

  // ── Tab visibility: immediate poll on return to tab ────────────────────────────
  useEffect(() => {
    if (!jobId) return;
    function onVisibilityChange() {
      if (document.visibilityState === 'visible') {
        pollNewProductsRef.current?.();
        pollJobStatusRef.current?.();
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [jobId]);

  // Clear completion banner dismissal when a new extraction finishes so the
  // banner reappears for the fresh run (user may have dismissed a prior run).
  useEffect(() => {
    if (extractionComplete && catalogId) {
      try { localStorage.removeItem(`cc_completion_${catalogId}`); } catch { /* non-fatal */ }
    }
  }, [extractionComplete]);

  // Restore original tab title on unmount
  useEffect(() => {
    return () => { document.title = 'CatalogConvert — Turn Supplier Catalogs into Shopify CSVs Instantly'; };
  }, []);

  // bulkBarRef/bulkBarH removed — floating bar no longer occupies layout space

  async function loadUserPlan() {
    try {
      const d = await api.getMe();
      setUserPlan(d.plan || 'free');
      setPreviewUsed(d.preview_used ?? false);
    }
    catch { setUserPlan('free'); }
  }

  async function loadData() {
    try {
      const prodData = await api.getProducts(catalogId);
      const p = prodData.products || [];
      const c = prodData.catalog;
      setProducts(p);
      setCatalog(c);
      setCatalogName(c?.name || '');
      setLoadError(null);
      setLoading(false);
      if (!phTableRendered.current && p.length > 0) {
        phTableRendered.current = true;
        const hasErrors = p.some(pr => (pr.variants || []).some(v => (v.flags || []).some(f => f.severity === 'error' && !f.resolved)));
        capture('table_render', { catalog_id: catalogId, product_count: p.length, has_errors: hasErrors });
      }
      loadStats();
      loadCustomData();
      loadProductImages(catalogId, p);
      // Phase 4: fetch catalog-level flags (encrypted ZIP banners, unmatched images)
      api.getCatalogFlags(catalogId).then(r => setCatalogFlags(r?.flags || [])).catch(() => {});
      // P3.3: Check if latest job completed with warnings (for direct navigation)
      if (!jobId) {
        api.getCatalogJob(catalogId).then(res => {
          if (res?.job?.status === 'completed_with_warnings') {
            setJobStatus('completed_with_warnings');
            api.getCatalogWarnings(catalogId).then(wRes => {
              if (wRes?.warnings) setExtractionWarnings(wRes.warnings);
            }).catch(() => {});
          }
        }).catch(() => {});
      }
    } catch (e) {
      setLoadError(e.message || 'Failed to load catalog. Please refresh the page.');
      setLoading(false);
    }
  }

  async function loadStats() {
    try { const s = await api.getStats(catalogId); setStats(s); } catch {}
  }


  function dismissBanner() {
    setBannerDismissed(true);
    try { localStorage.setItem(`cc_banner_${catalogId}`, '1'); } catch {}
  }

  async function handleRename(newName) {
    if (!newName || newName === catalog?.name) return;
    setRenameSaving(true);
    try { const res = await api.updateCatalog(catalogId, { name: newName }); setCatalog(res.catalog); setCatalogName(res.catalog.name); }
    catch {}
    setRenameSaving(false);
  }

  function showUndo(fieldLabel, undoFn) {
    if (undoTimerRef.current) clearTimeout(undoTimerRef.current);
    setUndoToast({ message: `${fieldLabel} updated`, undoFn });
    setUndoKey(k => k + 1);
  }

  function dismissUndo() { setUndoToast(null); }

  useLayoutEffect(() => {
    if (scrollRef.current && scrollTopRef.current) {
      scrollRef.current.scrollTop = scrollTopRef.current;
    }
  }, [products]);

  function preserveScroll(fn) {
    scrollTopRef.current = scrollRef.current?.scrollTop ?? 0;
    fn();
  }

  async function saveProduct(productId, field, value) {
    console.log('[saveProduct] called', { productId, field, value, valueType: typeof value });
    if (!phFirstEdit.current) {
      phFirstEdit.current = true;
      const hadFlag = (products.find(p => p.id === productId)?.variants || []).some(v => (v.flags || []).some(f => !f.resolved));
      capture('first_field_edit', { field_type: field, had_flag: hadFlag });
    }
    const prev = products.find(p => p.id === productId);
    const valueBefore = prev ? prev[field] : undefined;
    console.log('[saveProduct] valueBefore:', valueBefore, '→ valueAfter:', value);
    preserveScroll(() => setProducts(ps => ps.map(p => p.id === productId ? { ...p, [field]: value } : p)));
    const saveKey = `product:${productId}:${field}`;
    const prevCtrl = saveControllers.current.get(saveKey);
    if (prevCtrl) { console.log('[saveProduct] aborting previous save for', saveKey); prevCtrl.abort(); }
    const controller = new AbortController();
    saveControllers.current.set(saveKey, controller);
    const label = field.charAt(0).toUpperCase() + field.slice(1).replace(/_/g, ' ');
    let completed = false;
    showUndo(label, () => {
      if (!completed) controller.abort();
      preserveScroll(() => setProducts(ps => ps.map(p => p.id === productId ? { ...p, [field]: valueBefore } : p)));
      if (completed) api.updateProduct(productId, { [field]: valueBefore }).catch(() => {});
      dismissUndo();
    });
    try {
      console.log('[saveProduct] sending PATCH', { productId, payload: { [field]: value } });
      const res = await api.updateProduct(productId, { [field]: value }, { signal: controller.signal });
      completed = true;
      saveControllers.current.delete(saveKey);
      console.log('[saveProduct] response:', JSON.stringify(res, null, 2));
      // Apply server-authoritative fields so the UI reflects resolved data
      // immediately (row_status, product_category breadcrumb, etc.)
      if (res?.product) {
        preserveScroll(() => setProducts(ps => ps.map(p => {
          if (p.id !== productId) return p;
          const patch: Record<string, any> = {};
          if (res.product.row_status) patch.row_status = res.product.row_status;
          if (res.product.product_category !== undefined) patch.product_category = res.product.product_category;
          if (res.product.product_category_id !== undefined) patch.product_category_id = res.product.product_category_id;
          // Merge fresh variant flags + row_statuses so ReviewCell badges update immediately
          if (res.variant_updates) {
            patch.variants = (p.variants || []).map(v => {
              const update = res.variant_updates[v.id];
              return update ? { ...v, flags: update.flags, row_status: update.row_status } : v;
            });
          }
          return { ...p, ...patch };
        })));
      }
    } catch (e) {
      saveControllers.current.delete(saveKey);
      if (e.name === 'AbortError') { console.log('[saveProduct] aborted', { productId, field }); return; }
      console.error('[saveProduct] save failed:', e.message, { productId, field, value });
      preserveScroll(() => setProducts(ps => ps.map(p => p.id === productId ? { ...p, [field]: valueBefore } : p)));
      setToastMsg('Failed to save ��� change reverted.');
      dismissUndo();
    }
  }

  async function saveVariant(variantId, productId, field, value) {
    console.log('[saveVariant] called', { variantId, productId, field, value, valueType: typeof value });
    if (!phFirstEdit.current) {
      phFirstEdit.current = true;
      const prevVariant2 = products.find(p => p.id === productId)?.variants?.find(v => v.id === variantId);
      const hadFlag = (prevVariant2?.flags || []).some(f => !f.resolved);
      capture('first_field_edit', { field_type: field, had_flag: hadFlag });
    }
    const prevProduct = products.find(p => p.id === productId);
    const prevVariant = prevProduct?.variants?.find(v => v.id === variantId);
    const valueBefore = prevVariant ? prevVariant[field] : undefined;
    console.log('[saveVariant] valueBefore:', valueBefore, '→ valueAfter:', value);
    preserveScroll(() => setProducts(ps => ps.map(p => p.id !== productId ? p : {
      ...p, variants: (p.variants || []).map(v => v.id === variantId ? { ...v, [field]: value } : v)
    })));
    const saveKey = `variant:${variantId}:${field}`;
    const prevCtrl = saveControllers.current.get(saveKey);
    if (prevCtrl) { console.log('[saveVariant] aborting previous save for', saveKey); prevCtrl.abort(); }
    const controller = new AbortController();
    saveControllers.current.set(saveKey, controller);
    const label = field === 'price' ? 'Price' : field === 'sku' ? 'SKU' : field.charAt(0).toUpperCase() + field.slice(1).replace(/_/g, ' ');
    let completed = false;
    showUndo(label, () => {
      if (!completed) controller.abort();
      preserveScroll(() => setProducts(ps => ps.map(p => p.id !== productId ? p : {
        ...p, variants: (p.variants || []).map(v => v.id === variantId ? { ...v, [field]: valueBefore } : v)
      })));
      if (completed) api.updateVariant(variantId, field, valueBefore).catch(() => {});
      dismissUndo();
    });
    try {
      console.log('[saveVariant] sending PATCH', { variantId, field, value });
      const res = await api.updateVariant(variantId, field, value, { signal: controller.signal });
      completed = true;
      saveControllers.current.delete(saveKey);
      console.log('[saveVariant] response:', JSON.stringify(res, null, 2));
      // Apply server-authoritative state: the server returns the updated variant
      // (with new row_status from recalcRowStatuses), fresh unresolved flags,
      // and the product-level row_status — use all three to update local state
      // so status badges and flag counts are correct without a full page reload.
      if (res) {
        preserveScroll(() => setProducts(ps => ps.map(p => {
          if (p.id !== productId) return p;
          const updatedVariants = (p.variants || []).map(v => {
            if (v.id !== variantId) return v;
            return {
              ...v,
              ...res.variant,                    // new row_status + all DB fields
              flags: res.flags ?? v.flags,       // fresh unresolved flag list
            };
          });
          return {
            ...p,
            row_status: res.product_row_status ?? p.row_status,
            variants: updatedVariants,
          };
        })));
      }
      loadStats();
    } catch (e) {
      saveControllers.current.delete(saveKey);
      if (e.name === 'AbortError') { console.log('[saveVariant] aborted', { variantId, field }); return; }
      console.error('[saveVariant] save failed:', e.message, { variantId, productId, field, value });
      preserveScroll(() => setProducts(ps => ps.map(p => p.id !== productId ? p : {
        ...p, variants: (p.variants || []).map(v => v.id === variantId ? { ...v, [field]: valueBefore } : v)
      })));
      setToastMsg('Failed to save — change reverted.');
      dismissUndo();
    }
  }

  async function handleDeleteVariant(variantId, productId) {
    try {
      await api.deleteVariant(variantId);
      setProducts(prev =>
        prev.map(p => p.id !== productId ? p : { ...p, variants: p.variants.filter(v => v.id !== variantId) })
            .filter(p => p.variants?.length > 0)
      );
      await loadStats();
      setUndoToast({ message: 'Variant deleted', undoFn: async () => { await loadData(); dismissUndo(); } });
      setUndoKey(k => k + 1);
    } catch (e) { setToastMsg(e.message || 'Failed to delete variant.'); }
  }

  async function handleVariantReorder(productId, activeId, overId) {
    const product = products.find(p => p.id === productId);
    if (!product) return;
    const variantsBefore = [...(product.variants || [])];
    const oldIndex = variantsBefore.findIndex(v => v.id === activeId);
    const newIndex = variantsBefore.findIndex(v => v.id === overId);
    if (oldIndex === newIndex) return;

    const reordered = arrayMove(variantsBefore, oldIndex, newIndex);
    const withOrder = reordered.map((v, i) => ({ ...v, sort_order: i }));

    setProducts(prev => prev.map(p =>
      p.id !== productId ? p : { ...p, variants: withOrder }
    ));

    try {
      await api.reorderVariants(withOrder.map(v => ({ id: v.id, sort_order: v.sort_order })));
    } catch {
      setProducts(prev => prev.map(p =>
        p.id !== productId ? p : { ...p, variants: variantsBefore }
      ));
      setToastMsg('Could not save order — please try again.');
    }
  }

  async function handleMultiVariantReorder(productId: string, activeId: string, overId: string) {
    const product = products.find(p => p.id === productId);
    if (!product) return;
    const variantsBefore = [...(product.variants || [])];
    const visualOrder = variantsBefore.map(v => v.id);
    const siblingIds = new Set(visualOrder);
    const draggedIds = new Set([...selectedVariantIds].filter(id => siblingIds.has(id)));
    const remaining = visualOrder.filter(id => !draggedIds.has(id));
    let insertIdx = remaining.indexOf(overId);
    if (insertIdx === -1) return;
    const oldActiveIdx = visualOrder.indexOf(activeId);
    const overIdx = visualOrder.indexOf(overId);
    if (oldActiveIdx < overIdx) insertIdx += 1;
    const orderedDragged = visualOrder.filter(id => draggedIds.has(id));
    remaining.splice(insertIdx, 0, ...orderedDragged);
    const withOrder = remaining.map((id, i) => {
      const v = variantsBefore.find(vr => vr.id === id);
      return { ...v, sort_order: i };
    });
    setProducts(prev => prev.map(p =>
      p.id !== productId ? p : { ...p, variants: withOrder }
    ));
    try {
      await api.reorderVariants(withOrder.map(v => ({ id: v.id, sort_order: v.sort_order })));
    } catch {
      setProducts(prev => prev.map(p =>
        p.id !== productId ? p : { ...p, variants: variantsBefore }
      ));
      setToastMsg('Could not save order — please try again.');
    }
  }

  // ── Derived state (must precede reorder handlers that use sortedProducts) ───
  const isFailed    = catalog?.status === 'failed';
  const allVariants = useMemo(() => products.flatMap(p => p.variants || []), [products]);

  const filteredProducts = useMemo(() => {
    let list = products;
    // Status filter — from activeFilters (array-based, OR within)
    if (activeFilters.status.length > 0) {
      list = list.filter(p => {
        const ps = (p.row_status === 'red' || p.row_status === 'yellow') ? 'needs_review' : 'green';
        return activeFilters.status.some(s => ps === s);
      });
    }
    // Field filters (AND across sections, OR within)
    if (activeFilters.type.length > 0) list = list.filter(p => activeFilters.type.includes(p.product_type));
    if (activeFilters.tags.length > 0) list = list.filter(p => activeFilters.tags.some(t => mergeRenderTags(p).includes(t)));
    // Add-on field-presence filters (OR within each section, AND across sections)
    if (activeFilters.price?.length > 0) {
      list = list.filter(p => {
        const hasPrice = (p.variants || []).some(v => v.price !== null && v.price !== undefined);
        if (activeFilters.price.includes('With price') && activeFilters.price.includes('Missing price')) return true;
        if (activeFilters.price.includes('With price')) return hasPrice;
        return !hasPrice;
      });
    }
    if (activeFilters.image?.length > 0) {
      list = list.filter(p => {
        const hasImage = (p.variants || []).some(v => v.image_url);
        if (activeFilters.image.includes('With image') && activeFilters.image.includes('Missing image')) return true;
        if (activeFilters.image.includes('With image')) return hasImage;
        return !hasImage;
      });
    }
    if (activeFilters.category?.length > 0) {
      list = list.filter(p => {
        const hasCat = !!p.product_category_id;
        if (activeFilters.category.includes('Categorized') && activeFilters.category.includes('Uncategorized')) return true;
        if (activeFilters.category.includes('Categorized')) return hasCat;
        return !hasCat;
      });
    }
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      list = list.filter(p =>
        (p.title || '').toLowerCase().includes(q) ||
        (p.vendor || '').toLowerCase().includes(q) ||
        (p.product_type || '').toLowerCase().includes(q) ||
        mergeRenderTags(p).toLowerCase().includes(q) ||
        (p.collection || '').toLowerCase().includes(q) ||
        (p.variants || []).some(v =>
          (v.sku || '').toLowerCase().includes(q) ||
          (v.option1_value || '').toLowerCase().includes(q) ||
          (v.option2_value || '').toLowerCase().includes(q) ||
          (v.option3_value || '').toLowerCase().includes(q)
        )
      );
    }
    return list;
  }, [products, filter, searchQuery, activeFilters]);

  const sortedProducts = useMemo(() => {
    const statusOrder = { red: 0, yellow: 1, green: 2 };

    // Column sort from header click or sort panel overrides everything
    if (columnSort.column && columnSort.direction) {
      const sorted = [...filteredProducts].sort((a, b) => {
        if (columnSort.column === 'status') {
          const diff = (statusOrder[a.row_status] ?? 1) - (statusOrder[b.row_status] ?? 1);
          if (diff !== 0) return columnSort.direction === 'desc' ? -diff : diff;
          return (a.display_order ?? 0) - (b.display_order ?? 0);
        }
        if (columnSort.column === 'price') {
          const aPrice = (a.variants || [])[0]?.price ?? null;
          const bPrice = (b.variants || [])[0]?.price ?? null;
          if (aPrice === null && bPrice === null) return 0;
          if (aPrice === null) return 1;
          if (bPrice === null) return -1;
          const diff = aPrice - bPrice;
          return columnSort.direction === 'desc' ? -diff : diff;
        }
        if (columnSort.column === 'sku') {
          const aSku = ((a.variants || [])[0]?.sku || '').toLowerCase();
          const bSku = ((b.variants || [])[0]?.sku || '').toLowerCase();
          const cmp = aSku.localeCompare(bSku);
          return columnSort.direction === 'desc' ? -cmp : cmp;
        }
        if (columnSort.column === 'image') {
          const aHas = (a.variants || []).some(v => v.image_url) ? 1 : 0;
          const bHas = (b.variants || []).some(v => v.image_url) ? 1 : 0;
          if (aHas !== bHas) return aHas - bHas; // missing (0) sorts before has (1)
          return (a.display_order ?? 0) - (b.display_order ?? 0);
        }
        if (columnSort.column === 'compareAt') {
          const aCA = (a.variants || [])[0]?.compare_at_price ?? null;
          const bCA = (b.variants || [])[0]?.compare_at_price ?? null;
          if (aCA === null && bCA === null) return 0;
          if (aCA === null) return 1;
          if (bCA === null) return -1;
          const diff = aCA - bCA;
          return columnSort.direction === 'desc' ? -diff : diff;
        }
        if (columnSort.column === 'type') {
          const cmp = (a.product_type || '').toLowerCase().localeCompare((b.product_type || '').toLowerCase());
          return columnSort.direction === 'desc' ? -cmp : cmp;
        }
        if (columnSort.column === 'vendor') {
          const cmp = (a.vendor || '').toLowerCase().localeCompare((b.vendor || '').toLowerCase());
          return columnSort.direction === 'desc' ? -cmp : cmp;
        }
        if (columnSort.column === 'inventory') {
          const aInv = (a.variants || [])[0]?.inventory_quantity ?? null;
          const bInv = (b.variants || [])[0]?.inventory_quantity ?? null;
          if (aInv === null && bInv === null) return 0;
          if (aInv === null) return 1;
          if (bInv === null) return -1;
          const diff = aInv - bInv;
          return columnSort.direction === 'desc' ? -diff : diff;
        }
        if (columnSort.column === 'weight') {
          const aW = (a.variants || [])[0]?.weight ?? null;
          const bW = (b.variants || [])[0]?.weight ?? null;
          if (aW === null && bW === null) return 0;
          if (aW === null) return 1;
          if (bW === null) return -1;
          const diff = aW - bW;
          return columnSort.direction === 'desc' ? -diff : diff;
        }
        if (columnSort.column === 'display_order') {
          return (a.display_order ?? 0) - (b.display_order ?? 0);
        }
        // title
        const aTitle = (a.title || '').toLowerCase();
        const bTitle = (b.title || '').toLowerCase();
        const cmp = aTitle.localeCompare(bTitle);
        return columnSort.direction === 'desc' ? -cmp : cmp;
      });
      return sorted;
    }

    // Default: source catalog order (display_order) — preserves drag-and-drop reorder for all rows
    return [...filteredProducts].sort((a, b) => (a.display_order ?? 0) - (b.display_order ?? 0));
  }, [filteredProducts, columnSort]);

  async function handleProductReorder(activeId: string, overId: string) {
    // Reorder within the full products array using the visual (sorted) positions
    const productsBefore = [...products];
    // Build the current visual order to determine intended position
    const visualOrder = sortedProducts.map(p => p.id);
    const oldVisIdx = visualOrder.indexOf(activeId);
    const newVisIdx = visualOrder.indexOf(overId);
    if (oldVisIdx === -1 || newVisIdx === -1 || oldVisIdx === newVisIdx) return;

    // Apply the move in visual order, then assign sequential display_order
    const reorderedIds = arrayMove(visualOrder, oldVisIdx, newVisIdx);
    const withOrder = reorderedIds.map((id, i) => {
      const p = productsBefore.find(pr => pr.id === id);
      return { ...p, display_order: i };
    });
    setProducts(withOrder);

    try {
      await api.reorderProducts(withOrder.map(p => ({ id: p.id, display_order: p.display_order })));
    } catch {
      setProducts(productsBefore);
      setToastMsg('Could not save order — please try again.');
    }
  }

  async function handleMultiProductReorder(activeId: string, overId: string) {
    const productsBefore = [...products];
    const visualOrder = sortedProducts.map(p => p.id);
    const draggedIds = new Set([...selectedIds].filter(id => visualOrder.includes(id)));

    // Remove all dragged IDs from visual order
    const remaining = visualOrder.filter(id => !draggedIds.has(id));

    // Find insertion point (where overId sits in remaining)
    let insertIdx = remaining.indexOf(overId);
    if (insertIdx === -1) return;

    // If dragging downward, insert after the target
    const oldActiveIdx = visualOrder.indexOf(activeId);
    const overIdx = visualOrder.indexOf(overId);
    if (oldActiveIdx < overIdx) insertIdx += 1;

    // Preserve relative order of dragged items
    const orderedDragged = visualOrder.filter(id => draggedIds.has(id));

    // Splice dragged items at insertion point
    remaining.splice(insertIdx, 0, ...orderedDragged);

    // Apply new display_order
    const withOrder = remaining.map((id, i) => {
      const p = productsBefore.find(pr => pr.id === id);
      return { ...p, display_order: i };
    });

    setProducts(withOrder);
    try {
      await api.reorderProducts(withOrder.map(p => ({ id: p.id, display_order: p.display_order })));
    } catch {
      setProducts(productsBefore);
      setToastMsg('Could not save order — please try again.');
    }
  }

  async function handleResolveFlag(flagId) {
    try {
      // Server returns { flag, variant_row_status, product_row_status, flags: freshUnresolved }
      const res = await api.resolveFlag(flagId);
      capture('flag_resolve', { flag_type: res?.flag?.flag_type ?? 'unknown' });
      const affectedVariantId = res?.flag?.variant_id;
      setProducts(prev => prev.map(p => {
        const hasAffected = (p.variants || []).some(v => v.id === affectedVariantId);
        if (!hasAffected) return p;
        const updatedVariants = (p.variants || []).map(v => {
          if (v.id !== affectedVariantId) return v;
          return {
            ...v,
            // Use fresh server flag list; fall back to filtering locally.
            flags: res.flags ?? (v.flags || []).filter(f => f.id !== flagId),
            row_status: res.variant_row_status ?? v.row_status,
          };
        });
        return {
          ...p,
          variants: updatedVariants,
          row_status: res.product_row_status ?? p.row_status,
        };
      }));
      // FlagPanel flags are derived live from products state (see render below),
      // so no separate setFlagPanel flags update is needed.
      await loadStats();
    } catch (e) { console.error('Resolve flag failed:', e.message); }
  }

  async function handleRetry() {
    try {
      const res = await api.retryJob(catalogId);
      navigate(`/review/${catalogId}?job_id=${res.job_id}`, { replace: true });
    } catch (e) { setToastMsg(e.message); }
  }


  function toggleCollapse(productId) {
    setExpanded(prev => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  }

  function expandAll() {
    setExpanded(new Set(products.map(p => p.id)));
  }
  function collapseAll() {
    setExpanded(new Set());
  }

  // ── Context menu ──────────────────────────────────────────────────────────────
  function handleRowContextMenu(e, productId, variantId, type) {
    e.preventDefault();
    const x = Math.min(e.clientX, window.innerWidth - 200);
    const y = Math.min(e.clientY, window.innerHeight - 200);
    setContextMenu({ x, y, productId, variantId, type });
  }
  useEffect(() => {
    if (!contextMenu) return;
    function onEsc(e) { if (e.key === 'Escape') setContextMenu(null); }
    function onClick(e) {
      const menu = document.getElementById('row-context-menu');
      if (menu && !menu.contains(e.target)) setContextMenu(null);
    }
    document.addEventListener('keydown', onEsc);
    document.addEventListener('mousedown', onClick);
    return () => { document.removeEventListener('keydown', onEsc); document.removeEventListener('mousedown', onClick); };
  }, [contextMenu]);

  // Multi-drag: set of product IDs being dragged together
  const draggedProductIds = useMemo(() => {
    if (!activeDragId) return new Set();
    const productIdSet = new Set(sortedProducts.map(p => p.id));
    if (!productIdSet.has(activeDragId)) return new Set(); // variant drag — skip
    if (selectedIds.has(activeDragId) && selectedIds.size > 1) {
      return new Set([...selectedIds].filter(id => productIdSet.has(id)));
    }
    return new Set([activeDragId]);
  }, [activeDragId, selectedIds, sortedProducts]);

  // Multi-drag: set of variant IDs being dragged together (within the same product)
  const draggedVariantIds = useMemo(() => {
    if (!activeDragId) return new Set();
    const productIdSet = new Set(sortedProducts.map(p => p.id));
    if (productIdSet.has(activeDragId)) return new Set(); // product drag — skip
    // Find the owning product
    const ownerProduct = sortedProducts.find(p => (p.variants || []).some(v => v.id === activeDragId));
    if (!ownerProduct) return new Set();
    const siblingIds = new Set((ownerProduct.variants || []).map(v => v.id));
    if (selectedVariantIds.has(activeDragId) && [...selectedVariantIds].filter(id => siblingIds.has(id)).length > 1) {
      return new Set([...selectedVariantIds].filter(id => siblingIds.has(id)));
    }
    return new Set([activeDragId]);
  }, [activeDragId, selectedVariantIds, sortedProducts]);

  const filterCounts = useMemo(() => ({
    all:          products.length,
    needs_review: products.filter(p => p.row_status === 'red' || p.row_status === 'yellow').length,
    green:        products.filter(p => p.row_status === 'green').length,
    no_image:     products.filter(p => !(p.variants || []).some(v => v.image_url)).length,
    no_price:     products.filter(p => (p.variants || []).some(v => v.price === null || v.price === undefined)).length,
  }), [products]);

  // ── Virtual row list: flatten products into individual <tr> descriptors ────
  // Types: 'merged' | 'product' | 'variant' | 'add-variant'
  // BUG-002 L1 fix (Phase 2B SD4): row-class predicate extracted to the pure,
  // unit-tested buildVirtualRows helper. Single-variant-with-options products
  // now route to MergedRow (live cells) instead of the collapsed ProductRow
  // header (hardcoded N/A em dashes). See client/src/utils/virtualRows.ts.
  const virtualRows = useMemo(
    () => buildVirtualRows(sortedProducts, expanded),
    [sortedProducts, expanded],
  );

  const rowVirtualizer = useVirtualizer({
    count: virtualRows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: (index) => {
      const row = virtualRows[index];
      if (row.type === 'add-variant') return 32;
      if (row.type === 'product') return 36;
      return 32;
    },
    overscan: 15,
  });

  // Restore scroll position once after products load
  useEffect(() => {
    if (restoredScrollRef.current || !sortedProducts.length || !scrollRef.current) return;
    restoredScrollRef.current = true;
    try {
      const raw = localStorage.getItem(`cc_review_state_${catalogId}`);
      if (!raw) return;
      const { productIndex } = JSON.parse(raw);
      if (typeof productIndex !== 'number' || productIndex <= 0) return;
      const idx = Math.min(productIndex, sortedProducts.length - 1);
      if (idx <= 0) return;
      const target = sortedProducts[idx];
      if (!target) return;
      const vIdx = virtualRows.findIndex(r => r.product?.id === target.id);
      if (vIdx >= 0) rowVirtualizer.scrollToIndex(vIdx, { align: 'start' });
    } catch {}
  }, [sortedProducts, virtualRows, rowVirtualizer, catalogId]);

  // Debounced scroll → save product index every 5 s
  useEffect(() => {
    const el = scrollRef.current;
    if (!el || !catalogId) return;
    function onScroll() {
      if (scrollSaveTimerRef.current) return;
      scrollSaveTimerRef.current = setTimeout(() => {
        scrollSaveTimerRef.current = null;
        const items = rowVirtualizer.getVirtualItems();
        if (!items.length) return;
        const row = virtualRows[items[0].index];
        if (!row?.product) return;
        const pi = sortedProducts.findIndex(p => p.id === row.product.id);
        try {
          const raw = localStorage.getItem(`cc_review_state_${catalogId}`);
          const state = raw ? JSON.parse(raw) : {};
          state.productIndex = Math.max(0, pi);
          localStorage.setItem(`cc_review_state_${catalogId}`, JSON.stringify(state));
        } catch {}
      }, 5000);
    }
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      el.removeEventListener('scroll', onScroll);
      if (scrollSaveTimerRef.current) { clearTimeout(scrollSaveTimerRef.current); scrollSaveTimerRef.current = null; }
    };
  }, [catalogId, sortedProducts, virtualRows, rowVirtualizer]);

  const readyPct = stats?.ready_pct ?? pct(
    allVariants.filter(v => v.row_status === 'green').length,
    allVariants.length
  );
  // Always use live variant data — stats.error_count is loaded once and goes stale after edits
  const errorCount = allVariants.flatMap(v => (v.flags||[]).filter(f => f.severity === 'error' && !f.resolved)).length;
  // Product-level ready/flagged counts for the live progress feed and completion banner.
  // Uses row_status === 'green' to match server green-row logic.
  const liveReadyCount = useMemo(() =>
    products.filter(p => p.row_status === 'green').length,
  [products]);
  const liveFlaggedCount = products.length - liveReadyCount;

  // Dominant error flag label for filter chip: if all red products share the same error flag type, show specific label
  const dominantErrFlagLabel = useMemo(() => {
    const redProducts = products.filter(p => p.row_status === 'red');
    if (redProducts.length === 0) return 'Errors';
    const flagTypeSets = redProducts.map(p => {
      const types = new Set();
      for (const v of p.variants || []) {
        for (const f of v.flags || []) {
          if ((f.severity === 'error' || ERROR_FLAG_TYPES.has(f.flag_type)) && !f.resolved) {
            types.add(f.flag_type);
          }
        }
      }
      return types;
    });
    const allTypes = new Set(flagTypeSets.flatMap(s => [...s]));
    return allTypes.size === 1 ? (FLAG_LABELS[[...allTypes][0]] || 'Errors') : 'Errors';
  }, [products]);

  const opt1Label = useMemo(() => mostCommon(allVariants.map(v => v.option1_name)) || 'Option 1', [allVariants]);
  const opt2Label = useMemo(() => mostCommon(allVariants.map(v => v.option2_name)) || 'Option 2', [allVariants]);
  const opt3Label = useMemo(() => mostCommon(allVariants.map(v => v.option3_name)) || 'Option 3', [allVariants]);
  const opt1HasData = useMemo(() => allVariants.some(v => !!v.option1_value), [allVariants]);
  const opt2HasData = useMemo(() => allVariants.some(v => !!v.option2_value), [allVariants]);
  const opt3HasData = useMemo(() => allVariants.some(v => !!v.option3_value), [allVariants]);

  // colVis: explicit merchant pref wins; PINNED_COLS always on by default;
  // remaining untouched columns use COL_FRESH_DEFAULTS (off unless explicitly turned on).
  // Option columns default OFF — on collapsed product rows they show em dashes (no info value).
  // Merchants see option values when they expand a product to view variants.
  // (Decision locked 2026-04-22 — see .claude/briefs/2026-04-22-default-columns-audit.md)
  const colVis = useMemo(() => {
    const result = {};
    for (const key of COL_KEYS) {
      if (key in merchantColPrefs) {
        result[key] = merchantColPrefs[key];              // merchant explicitly toggled this column
      } else if (PINNED_COLS.has(key)) {
        result[key] = true;                               // always visible unless merchant hides it
      } else {
        result[key] = COL_FRESH_DEFAULTS[key] || false;  // off by default unless in COL_FRESH_DEFAULTS
      }
    }
    return result;
  }, [merchantColPrefs]);

  // Compute category suggestions from existing products in this catalog (top 5 most used)
  const categorySuggestions = useMemo(() => {
    const counts = new Map();
    for (const p of products) {
      if (p.product_category_id && p.product_category) {
        const key = p.product_category_id;
        if (!counts.has(key)) counts.set(key, { gid_short: key, full_name: p.product_category, count: 0 });
        counts.get(key).count++;
      }
    }
    return Array.from(counts.values())
      .sort((a, b) => b.count - a.count)
      .slice(0, 5)
      .map(({ gid_short, full_name }) => ({ gid_short, full_name }));
  }, [products]);

  function toggleColumn(key) {
    const next = { ...merchantColPrefs, [key]: !colVis[key] };
    setMerchantColPrefs(next);
    try { localStorage.setItem(`cc_cols_${catalogId}`, JSON.stringify(next)); } catch {}
  }

  async function handleDeleteCustomColInline(col) {
    if (!window.confirm(`Delete "${col.column_label}" and all its values? This cannot be undone.`)) return;
    try {
      await api.deleteCustomColumn(col.id);
      setCustomColumns(prev => prev.filter(c => c.id !== col.id));
      setCustomValues(prev => { const n = { ...prev }; delete n[col.id]; return n; });
    } catch (err) {
      setToastMsg('Failed to delete column: ' + err.message);
    }
  }

  function hasLowConf(variant, field) {
    return (variant.flags || []).some(f => f.flag_type === 'low_confidence' && f.field_name === field && !f.resolved);
  }

  function expandErrorRows() {
    setExpanded(prev => {
      const next = new Set(prev);
      products.forEach(p => { if (p.row_status === 'red') next.add(p.id); });
      return next;
    });
    setTimeout(() => {
      const firstError = products.find(p => p.row_status === 'red');
      if (firstError && scrollRef.current) {
        const row = scrollRef.current.querySelector?.(`[data-product-id="${firstError.id}"]`);
        row?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      }
    }, 100);
  }

  function handleFixErrors() {
    setFilter('needs_review');
    setActiveFilters(prev => ({ ...prev, status: ['needs_review'] }));
    expandErrorRows();
  }

  // P0-B: Show modal for any needs-review state; direct export only when all clean
  const [directExporting, setDirectExporting] = useState(false);
  async function handleExportClick() {
    const hasNeedsReview = products.some(p => p.row_status === 'red' || p.row_status === 'yellow');
    if (hasNeedsReview) {
      setShowExport(true);
      return;
    }
    // All clean — export immediately, no dialog
    capture('export_click_clean', { product_count: products.length });
    setDirectExporting(true);
    try {
      const data = await api.exportCatalog(catalogId, {});
      if (data.csv_url) {
        const a = document.createElement('a');
        a.href = data.csv_url;
        a.download = '';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
      setExportToast({ count: products.length });
      try { localStorage.removeItem(`cc_review_state_${catalogId}`); } catch {}
    } catch (e) {
      // Fallback: show modal with error
      setShowExport(true);
    } finally {
      setDirectExporting(false);
    }
  }

  async function loadCustomData() {
    try {
      const colRes = await api.getCustomColumns(catalogId);
      setCustomColumns(colRes.columns || []);
      const valRes = await api.getCustomValues(catalogId);
      const map = {};
      for (const v of (valRes.values || [])) {
        if (!map[v.custom_column_id]) map[v.custom_column_id] = {};
        map[v.custom_column_id][v.variant_id] = v.value;
      }
      setCustomValues(map);
    } catch {}
  }

  async function loadProductImages(catId, productsList) {
    try {
      const token = getToken();
      const base = import.meta.env.VITE_SUPABASE_URL;
      const key  = import.meta.env.VITE_SUPABASE_ANON_KEY;
      const url  = `${base}/rest/v1/product_images?catalog_id=eq.${encodeURIComponent(catId)}&order=display_order.asc&select=id,product_id,variant_id,image_url,display_order`;
      const res  = await fetch(url, {
        headers: {
          'apikey': key,
          'Authorization': `Bearer ${token}`,
        },
      });
      if (!res.ok) return;
      const data = await res.json();

      // Split images: variant-specific (variant_id set) vs product-level gallery (variant_id null)
      const byProduct = {};
      const byVariant = {};
      for (const img of data || []) {
        if (img.variant_id) {
          if (!byVariant[img.variant_id]) byVariant[img.variant_id] = [];
          byVariant[img.variant_id].push(img);
        } else {
          if (!byProduct[img.product_id]) byProduct[img.product_id] = [];
          byProduct[img.product_id].push(img);
        }
      }

      // Fallback: for variants that have an AI-extracted image_url but no product_images row,
      // synthesise a display-only entry in variantImagesMap so the ImageCell shows a thumbnail.
      // id: null means ImageThumb will not render a delete button.
      const productsToCheck = productsList || products;
      for (const product of productsToCheck) {
        for (const variant of product.variants || []) {
          if (byVariant[variant.id]?.length > 0) continue;
          if (variant.image_url) {
            byVariant[variant.id] = [{
              id: null,
              product_id: product.id,
              variant_id: variant.id,
              image_url: variant.image_url,
              image_alt: variant.image_alt || '',
              display_order: 0,
              _fromVariant: true,
            }];
          }
        }
      }

      setProductImagesMap(byProduct);
      setVariantImagesMap(byVariant);
    } catch {}
  }

  async function handleCustomValueChange(columnId, variantId, value) {
    try {
      await api.upsertCustomValue({ custom_column_id: columnId, variant_id: variantId, value });
      setCustomValues(prev => ({
        ...prev,
        [columnId]: { ...(prev[columnId] || {}), [variantId]: value },
      }));
    } catch {}
  }

  // P0-D: Optimistic product delete with 8s undo window
  function handleBulkDeleted(ids) {
    const opKey = ++deleteOpCounter.current;
    // Snapshot for undo + hide from UI
    setProducts(prev => {
      deletedSnapshotRef.current.set(opKey, prev.filter(p => ids.includes(p.id)));
      return prev.filter(p => !ids.includes(p.id));
    });
    setSelectedIds(new Set());
    setSelectedVariantIds(new Set());

    // Guard polls from re-adding these products
    const idSet = new Set(ids);
    idSet.forEach(id => pendingDeleteRef.current.add(id));

    const count = ids.length;

    // Commit: fires when toast expires (8s) — sends actual DELETE to server
    function commitDelete() {
      const snapshot = [...(deletedSnapshotRef.current.get(opKey) || [])];
      pendingDeleteRef.current = new Set([...pendingDeleteRef.current].filter(id => !idSet.has(id)));
      deletedSnapshotRef.current.delete(opKey);
      setUndoToast(null);
      api.bulkDeleteProducts(ids).catch(() => {
        if (snapshot.length) {
          setProducts(prev => {
            const existing = new Set(prev.map(p => p.id));
            return [...prev, ...snapshot.filter(p => ids.includes(p.id) && !existing.has(p.id))];
          });
        }
      });
    }

    // Undo: restore rows from snapshot, cancel commit
    function undoDelete() {
      pendingDeleteRef.current = new Set([...pendingDeleteRef.current].filter(id => !idSet.has(id)));
      const reverted = (deletedSnapshotRef.current.get(opKey) || []).filter(p => ids.includes(p.id));
      setProducts(prev => {
        const existing = new Set(prev.map(p => p.id));
        return [...prev, ...reverted.filter(p => !existing.has(p.id))];
      });
      deletedSnapshotRef.current.delete(opKey);
      setUndoToast(null);
    }

    setUndoToast({
      message: `Deleted ${count} product${count !== 1 ? 's' : ''}. Undo?`,
      undoFn: undoDelete,
      onDismiss: commitDelete,
      duration: 8000,
    });
    setUndoKey(k => k + 1);
  }

  function handleBulkDeleteReverted(ids) {
    // Legacy — kept for compatibility but P0-D undo flow handles revert inline
    // Search all snapshots for matching products
    const allSnapshots = [...deletedSnapshotRef.current.values()].flat();
    const reverted = allSnapshots.filter(p => ids.includes(p.id));
    setProducts(prev => {
      const existing = new Set(prev.map(p => p.id));
      return [...prev, ...reverted.filter(p => !existing.has(p.id))];
    });
    // Clear all snapshot entries that contained these IDs
    for (const [key, snap] of deletedSnapshotRef.current.entries()) {
      const remaining = snap.filter(p => !ids.includes(p.id));
      if (remaining.length === 0) deletedSnapshotRef.current.delete(key);
      else deletedSnapshotRef.current.set(key, remaining);
    }
  }

  function handleBulkUpdated(ids, field, value, mode) {
    setProducts(prev => prev.map(p => {
      if (!ids.includes(p.id)) return p;
      const productFields = ['vendor', 'product_type', 'tags', 'collection', 'status'];
      if (!productFields.includes(field)) return p;
      let newVal = value;
      if (field === 'tags' && mode === 'add') {
        // Dispatch 2 T2 (2026-05-28) — bulk-tag-add appends to manual_tags
        // (merchant edit target per BUILD-4 + RATIFIED N4). Sets manual_tags
        // optimistically; the trigger keeps products.tags in sync server-side.
        const existing = p.manual_tags || '';
        newVal = existing ? `${existing}, ${value}` : value;
        return { ...p, manual_tags: newVal };
      }
      return { ...p, [field]: newVal };
    }));
    setSelectedIds(new Set());
    setSelectedVariantIds(new Set());
  }

  // P0-D: Optimistic variant delete with 8s undo window
  function handleBulkVariantsDeleted(vIds) {
    const opKey = ++deleteOpCounter.current;
    setProducts(prev => {
      const snap = prev
        .map(p => ({ ...p, _deletedVariants: (p.variants || []).filter(v => vIds.includes(v.id)) }))
        .filter(p => p._deletedVariants.length > 0)
        .map(({ _deletedVariants, ...p }) => ({ ...p, variants: _deletedVariants }));
      deletedSnapshotRef.current.set(opKey, snap);
      return prev.map(p => ({
        ...p,
        variants: (p.variants || []).filter(v => !vIds.includes(v.id)),
      })).filter(p => p.variants?.length > 0);
    });
    setSelectedVariantIds(new Set());
    setSelectedIds(new Set());

    const count = vIds.length;

    function restoreVariants(restoreIds, snapshotOverride) {
      const snapshot = snapshotOverride || deletedSnapshotRef.current.get(opKey) || [];
      if (!snapshot.length) return;
      const restoreSet = new Set(restoreIds);
      const filtered = snapshot.map(p => ({
        ...p,
        variants: (p.variants || []).filter(v => restoreSet.has(v.id)),
      })).filter(p => p.variants.length > 0);
      if (!filtered.length) return;
      setProducts(prev => {
        const existingIds = new Set(prev.map(p => p.id));
        const merged = prev.map(p => {
          const snap = filtered.find(s => s.id === p.id);
          if (!snap) return p;
          const presentIds = new Set((p.variants || []).map(v => v.id));
          return { ...p, variants: [...(p.variants || []), ...(snap.variants || []).filter(v => !presentIds.has(v.id))] };
        });
        const restored = filtered.filter(s => !existingIds.has(s.id));
        return [...merged, ...restored];
      });
      deletedSnapshotRef.current.delete(opKey);
    }

    function commitDelete() {
      const snapshot = [...(deletedSnapshotRef.current.get(opKey) || [])];
      deletedSnapshotRef.current.delete(opKey);
      setUndoToast(null);
      Promise.allSettled(vIds.map(id => api.deleteVariant(id))).then(results => {
        const failedIds = vIds.filter((_, i) => results[i].status === 'rejected');
        if (failedIds.length > 0) {
          restoreVariants(failedIds, snapshot);
        }
      });
    }

    function undoDelete() {
      restoreVariants(vIds);
      setUndoToast(null);
    }

    setUndoToast({
      message: `Deleted ${count} variant${count !== 1 ? 's' : ''}. Undo?`,
      undoFn: undoDelete,
      onDismiss: commitDelete,
      duration: 8000,
    });
    setUndoKey(k => k + 1);
  }

  function handleBulkVariantDeleteReverted(failedVariantIds) {
    // Legacy — kept for compatibility
    const allSnapshots = [...deletedSnapshotRef.current.values()].flat();
    if (!allSnapshots.length) return;
    const failedSet = new Set(failedVariantIds || []);
    const filteredSnapshot = allSnapshots.map(p => ({
      ...p,
      variants: (p.variants || []).filter(v => !failedSet.size || failedSet.has(v.id)),
    })).filter(p => p.variants.length > 0);
    if (!filteredSnapshot.length) return;
    setProducts(prev => {
      const existingIds = new Set(prev.map(p => p.id));
      const merged = prev.map(p => {
        const snap = filteredSnapshot.find(s => s.id === p.id);
        if (!snap) return p;
        const presentIds = new Set((p.variants || []).map(v => v.id));
        return { ...p, variants: [...(p.variants || []), ...(snap.variants || []).filter(v => !presentIds.has(v.id))] };
      });
      const restored = filteredSnapshot.filter(s => !existingIds.has(s.id));
      return [...merged, ...restored];
    });
    deletedSnapshotRef.current.clear();
  }

  function handleBulkResolved(updatedVariantIds) {
    setProducts(prev => prev.map(p => ({
      ...p,
      variants: (p.variants || []).map(v => {
        if (!updatedVariantIds.includes(v.id)) return v;
        return { ...v, flags: (v.flags || []).map(f => ({ ...f, resolved: true })) };
      }),
    })));
    setSelectedIds(new Set());
    setSelectedVariantIds(new Set());
  }

  function handleBulkMarkup(variantPrices) {
    if (!variantPrices || variantPrices.length === 0) return;
    const updateMap = {};
    for (const entry of variantPrices) updateMap[entry.id] = entry;
    setProducts(prev => prev.map(p => ({
      ...p,
      variants: (p.variants || []).map(v => {
        const upd = updateMap[v.id];
        if (!upd) return v;
        return {
          ...v,
          ...(upd.price !== undefined ? { price: upd.price } : {}),
          ...(upd.compare_at_price !== undefined ? { compare_at_price: upd.compare_at_price } : {}),
        };
      }),
    })));
  }

  function handleImagesUpdated(productId, imageRecord) {
    if (imageRecord.variant_id) {
      setVariantImagesMap(prev => {
        const existing = (prev[imageRecord.variant_id] || []).filter(img => !img._fromVariant);
        return { ...prev, [imageRecord.variant_id]: [...existing, imageRecord] };
      });
    } else {
      setProductImagesMap(prev => {
        const existing = (prev[productId] || []).filter(img => !img._fromVariant);
        return { ...prev, [productId]: [...existing, imageRecord] };
      });
    }
  }

  const [addingProduct, setAddingProduct] = useState<{ title: string; afterProductId: string | null } | null>(null);
  const [addingVariant, setAddingVariant] = useState<{ opt1: string; opt2: string; opt3: string; productId: string; afterVariantId: string | null } | null>(null);

  function handleAddProduct(afterProductId: string | null = null) {
    setAddingProduct({ title: '', afterProductId });
  }

  function handleAddVariant(product: any, afterVariantId: string | null = null) {
    setExpanded(prev => new Set([...prev, product.id]));
    setAddingVariant({ opt1: '', opt2: '', opt3: '', productId: product.id, afterVariantId });
  }

  async function handleSaveDraftProduct(title: string) {
    if (!title.trim()) return;
    const afterProductId = addingProduct?.afterProductId || null;
    setAddingProduct(null);
    try {
      const res = await api.createProduct({ catalog_id: catalogId, title: title.trim() });
      if (res.product) {
        setProducts(prev => {
          if (!afterProductId) return [...prev, res.product];
          const idx = prev.findIndex(p => p.id === afterProductId);
          if (idx === -1) return [...prev, res.product];
          const next = [...prev];
          next.splice(idx + 1, 0, res.product);
          return next;
        });
        setTimeout(() => {
          const row = scrollRef.current?.querySelector?.(`[data-product-id="${res.product.id}"]`);
          if (row) {
            row.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            row.setAttribute('data-highlight', '');
            setTimeout(() => row.removeAttribute('data-highlight'), 1500);
          }
        }, 50);
      }
    } catch (e: any) {
      setToastMsg('Could not add product: ' + e.message);
    }
  }

  async function handleSaveDraftVariant() {
    if (!addingVariant || !addingVariant.opt1.trim()) return;
    const { opt1, opt2, opt3, productId, afterVariantId } = addingVariant;
    const product = products.find(p => p.id === productId);
    const firstVariant = product?.variants?.[0];
    const o1Name = firstVariant?.option1_name || opt1Label;
    const o2Name = firstVariant?.option2_name || opt2Label;
    const o3Name = firstVariant?.option3_name || opt3Label;
    // Detect which option axes this product uses
    const hasOpt2 = product?.variants?.some(v => v.option2_value);
    const hasOpt3 = product?.variants?.some(v => v.option3_value);
    setAddingVariant(null);
    try {
      const res = await api.createVariant({
        product_id: productId,
        catalog_id: catalogId,
        option1_name: o1Name,
        option1_value: opt1.trim(),
        ...(hasOpt2 ? { option2_name: o2Name, option2_value: opt2.trim() || null } : {}),
        ...(hasOpt3 ? { option3_name: o3Name, option3_value: opt3.trim() || null } : {}),
        sku: null,
        price: null,
      });
      if (res.variant) {
        setProducts(prev => prev.map(p => {
          if (p.id !== productId) return p;
          const variants = [...(p.variants || [])];
          if (!afterVariantId) {
            variants.push(res.variant);
          } else {
            const idx = variants.findIndex(v => v.id === afterVariantId);
            if (idx === -1) variants.push(res.variant);
            else variants.splice(idx + 1, 0, res.variant);
          }
          return { ...p, variants };
        }));
        setTimeout(() => {
          const row = scrollRef.current?.querySelector?.(`[data-variant-id="${res.variant.id}"]`);
          if (row) {
            row.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
            row.setAttribute('data-highlight', '');
            setTimeout(() => row.removeAttribute('data-highlight'), 1500);
          }
        }, 50);
      }
    } catch (e: any) {
      setToastMsg('Could not add variant: ' + e.message);
    }
  }


  // ── Keep navRef in sync so the stable keydown handler can read current state ─
  navRef.current.toggleCollapse = toggleCollapse;
  navRef.current.expanded       = expanded;
  issueNavRef.current.sortedProducts    = sortedProducts;
  issueNavRef.current.sourcePanelTarget = sourcePanelTarget;

  // ── Keyboard navigation across editable cells ─────────────────────────────
  const focusInput = useCallback((cell) => {
    if (!cell) return;
    cell.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    const chipOverlay = cell.querySelector('[data-chip-overlay]');
    if (chipOverlay?._chipOpen) { chipOverlay._chipOpen(); return; }
    const candidates = Array.from(cell.querySelectorAll(
      'input:not([type="checkbox"]), textarea, [contenteditable="true"]'
    ));
    const input = candidates.find(el => window.getComputedStyle(el).display !== 'none');
    if (input) input.focus();
  }, []);

  const handleKeyDown = useCallback((e) => {
    const input = e.target;
    const isInput = input && (input.tagName === 'INPUT' || input.tagName === 'TEXTAREA');
    const isRTE   = input && input.isContentEditable && input.classList.contains('ProseMirror');
    if (!isInput && !isRTE) return;
    if (isInput && input.type === 'checkbox') return;
    // In the rich-text editor Enter adds a newline — only Tab triggers cell navigation
    const isTab   = e.key === 'Tab';
    const isEnter = e.key === 'Enter' && isInput;
    if (!isTab && !isEnter) return;
    const cell = input.closest('td[data-nav-cell]');
    if (!cell) return;

    const table = tableRef.current;
    if (!table) return;
    const cells = Array.from(table.querySelectorAll('td[data-nav-cell]'));
    const idx   = cells.indexOf(cell);
    if (idx === -1) return;

    if (isTab) {
      e.preventDefault();
      const next = e.shiftKey ? cells[idx - 1] : cells[idx + 1];
      if (next) {
        flushSync(() => input.blur());
        focusInput(next);
      } else if (!e.shiftKey) {
        // wrap to first cell of next available row
        const row = cell.closest('tr');
        if (row) {
          let nextRow = row.nextElementSibling;
          while (nextRow && nextRow.tagName === 'TR') {
            const firstCell = nextRow.querySelector('td[data-nav-cell]');
            if (firstCell) { flushSync(() => input.blur()); focusInput(firstCell); break; }
            nextRow = nextRow.nextElementSibling;
          }
        }
      }
      return;
    }

    if (isEnter) {
      e.preventDefault();
      const row = cell.closest('tr');
      if (!row) return;
      // Use data-col key for column identity (consistent across rows with different counts)
      const colKey = cell.getAttribute('data-col');

      const direction = e.shiftKey ? -1 : 1;
      let targetRow = direction === 1 ? row.nextElementSibling : row.previousElementSibling;
      while (targetRow && targetRow.tagName === 'TR') {
        const targetNavCells = Array.from(targetRow.querySelectorAll('td[data-nav-cell]'));
        if (targetNavCells.length > 0) {
          if (colKey) {
            // Prefer the same data-col column; skip this row if it doesn't have it
            const targetCell = targetNavCells.find(c => c.getAttribute('data-col') === colKey);
            if (targetCell) { flushSync(() => input.blur()); focusInput(targetCell); break; }
            // Row has nav cells but not the same column — keep scanning
          } else {
            // No colKey — land on first nav cell of this row
            const targetCell = targetNavCells[0];
            if (targetCell) { flushSync(() => input.blur()); focusInput(targetCell); }
            break;
          }
        }
        // This row has no nav cells — check if it is a collapsed prod-row
        if (targetRow.classList.contains('prod-row')) {
          const productId = targetRow.getAttribute('data-product-id');
          if (productId) {
            const isCollapsed = !navRef.current.expanded.has(productId);
            if (isCollapsed) {
              // Expand the collapsed product so variants render, then focus
              const colKeyCapture = colKey;
              const inputCapture  = input;
              navRef.current.toggleCollapse(productId);
              setTimeout(() => {
                // After React re-renders, find the newly visible first var-row
                const expandedVarRow = targetRow.nextElementSibling;
                if (expandedVarRow) {
                  const newCells = Array.from(expandedVarRow.querySelectorAll('td[data-nav-cell]'));
                  const targetCell = (colKeyCapture && newCells.find(c => c.getAttribute('data-col') === colKeyCapture))
                    || newCells[0]; // fall back to first cell when column absent in expanded row
                  if (targetCell) { flushSync(() => inputCapture.blur()); focusInput(targetCell); }
                }
              }, 50);
              break;
            }
            // Product is already expanded but prod-row has no nav cells — keep scanning
          }
        }
        targetRow = direction === 1 ? targetRow.nextElementSibling : targetRow.previousElementSibling;
      }
    }
  }, [focusInput]);

  // ── J/K issue navigation — jump to next/previous flagged (red/yellow) row ──
  const navigateToIssue = useCallback((direction) => {
    // direction: 1 = next (J), -1 = previous (K)
    const sp = issueNavRef.current.sortedProducts;
    const target = issueNavRef.current.sourcePanelTarget;
    const flaggedIndices = [];
    for (let i = 0; i < sp.length; i++) {
      if (sp[i].row_status === 'red' || sp[i].row_status === 'yellow') flaggedIndices.push(i);
    }
    if (flaggedIndices.length === 0) { setToastMsg('All products are ready'); return 0; }

    // Determine current position
    let currentIdx = -1;
    if (target) {
      currentIdx = sp.findIndex(p => p.id === target.productId);
    }

    // Find next/prev flagged index
    let nextFlaggedIdx = -1;
    if (direction === 1) {
      nextFlaggedIdx = flaggedIndices.find(i => i > currentIdx);
    } else {
      for (let i = flaggedIndices.length - 1; i >= 0; i--) {
        if (flaggedIndices[i] < (currentIdx === -1 ? sp.length : currentIdx)) {
          nextFlaggedIdx = flaggedIndices[i];
          break;
        }
      }
    }

    if (nextFlaggedIdx === -1 || nextFlaggedIdx === undefined) {
      setToastMsg('All products are ready');
      return flaggedIndices.length;
    }

    const product = sp[nextFlaggedIdx];
    const v0 = (product.variants || [])[0];
    setSourcePanelTarget({ productId: product.id, variantId: v0?.id });
    setDrawerOpen(product.id);

    // Scroll the row into view
    requestAnimationFrame(() => {
      const row = scrollRef.current?.querySelector?.(`[data-product-id="${product.id}"]`);
      if (row) row.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });

    // Return remaining issues after this one (for the button label)
    const remainingAfter = flaggedIndices.filter(i => i > nextFlaggedIdx).length;
    return remainingAfter;
  }, []);

  // Count remaining issues for the "Next issue" button
  const issueNavCounts = useMemo(() => {
    const flagged = sortedProducts.filter(p => p.row_status === 'red' || p.row_status === 'yellow');
    const total = flagged.length;
    let remaining = total;
    if (sourcePanelTarget) {
      const currentIdx = sortedProducts.findIndex(p => p.id === sourcePanelTarget.productId);
      if (currentIdx !== -1) {
        remaining = sortedProducts.filter((p, i) => i > currentIdx && (p.row_status === 'red' || p.row_status === 'yellow')).length;
      }
    }
    return { total, remaining };
  }, [sortedProducts, sourcePanelTarget]);

  useEffect(() => {
    function handleIssueNav(e) {
      // Only respond when no input/textarea/contenteditable has focus
      const active = document.activeElement;
      const isEditing = active && (
        active.tagName === 'INPUT' || active.tagName === 'TEXTAREA' ||
        active.isContentEditable
      );
      if (isEditing) return;

      if (e.key === 'j' || e.key === 'J') {
        e.preventDefault();
        navigateToIssue(1);
      } else if (e.key === 'k' || e.key === 'K') {
        e.preventDefault();
        navigateToIssue(-1);
      }
    }
    document.addEventListener('keydown', handleIssueNav);
    return () => document.removeEventListener('keydown', handleIssueNav);
  }, [navigateToIssue]);

  // ── Loading guard — skip when extraction is in progress ─────────────────────
  if (loading && !jobId) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--color-bg)' }}>
        <span style={{ color: 'var(--color-text-2)', fontSize: 14 }}>Loading catalog…</span>
      </div>
    );
  }

  if (isFailed && products.length === 0) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--color-bg)' }}>
        <TopBar name={catalogName}
          navigate={navigate} catalogId={catalogId} renameSaving={renameSaving}
          onRename={handleRename} onExport={handleExportClick}

          userPlan={userPlan} errorCount={errorCount}
        />
        <div style={{ maxWidth: 560, margin: '80px auto', padding: '0 24px', textAlign: 'center' }}>
          <div style={{ width: 64, height: 64, borderRadius: 16, background: 'var(--color-red-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            <FileWarning size={32} style={{ color: 'var(--color-red-text)' }} />
          </div>
          <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>We couldn't extract products from this catalog</h2>
          <p style={{ color: 'var(--color-text-2)', marginBottom: 24, lineHeight: 1.6 }}>
            {catalog?.status_message
              ? catalog.status_message
              : 'This may be due to an unsupported format or a temporary error.'}
          </p>
          <button onClick={handleRetry} style={{ ...primaryBtn, display: 'inline-flex', alignItems: 'center', gap: 6 }}><RefreshCw size={15} />Retry</button>
          <button onClick={() => navigate('/upload')} style={{ ...secondaryBtn, marginLeft: 12 }}>
            Upload different file
          </button>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--color-bg)' }}>
        <TopBar name={catalogName}
          navigate={navigate} catalogId={catalogId} renameSaving={renameSaving}
          onRename={handleRename} onExport={handleExportClick}

          userPlan={userPlan} errorCount={errorCount}
        />
        <div style={{ maxWidth: 560, margin: '80px auto', padding: '0 24px', textAlign: 'center' }}>
          <AlertTriangle size={48} style={{ color: 'var(--color-yellow-text)', marginBottom: 16 }} />
          <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>Couldn't load catalog</h2>
          <p style={{ color: 'var(--color-text-2)', marginBottom: 24, lineHeight: 1.6 }}>
            {loadError}
          </p>
          <button onClick={() => { setLoadError(null); setLoading(true); loadData(); }} style={primaryBtn}>
            Try again
          </button>
          <button onClick={() => navigate('/dashboard')} style={{ ...secondaryBtn, marginLeft: 12 }}>
            Back to dashboard
          </button>
        </div>
      </div>
    );
  }

  // Defensive guard: if loading finished but catalog data is missing, treat as error
  if (!loading && !catalog && !jobId) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--color-bg)' }}>
        <div style={{ maxWidth: 560, margin: '80px auto', padding: '0 24px', textAlign: 'center' }}>
          <AlertTriangle size={48} style={{ color: 'var(--color-yellow-text)', marginBottom: 16 }} />
          <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>Something went wrong</h2>
          <p style={{ color: 'var(--color-text-2)', marginBottom: 24, lineHeight: 1.6 }}>
            Catalog data could not be loaded. Please try again.
          </p>
          <button onClick={() => { setLoading(true); loadData(); }} style={primaryBtn}>
            Try again
          </button>
          <button onClick={() => navigate('/dashboard')} style={{ ...secondaryBtn, marginLeft: 12 }}>
            Back to dashboard
          </button>
        </div>
      </div>
    );
  }

  if (products.length === 0 && !jobId) {
    return (
      <div style={{ minHeight: '100vh', background: 'var(--color-bg)' }}>
        <TopBar name={catalogName}
          navigate={navigate} catalogId={catalogId} renameSaving={renameSaving}
          onRename={handleRename} onExport={handleExportClick}

          userPlan={userPlan} errorCount={errorCount}
        />
        <div style={{ maxWidth: 560, margin: '80px auto', padding: '0 24px', textAlign: 'center' }}>
          <Package size={48} style={{ color: 'var(--color-text-3)', marginBottom: 16 }} />
          <h2 style={{ fontSize: 22, fontWeight: 700, marginBottom: 8 }}>No products found</h2>
          <p style={{ color: 'var(--color-text-2)', marginBottom: 24 }}>
            The catalog is processing or no product data could be extracted.
          </p>
          <button onClick={handleRetry} style={primaryBtn}>Retry extraction</button>
        </div>
      </div>
    );
  }

  // ── Column sort cycling (Polaris: first click = descending) ─────────────────
  function cycleColumnSort(col) {
    setColumnSort(prev => {
      if (prev.column !== col) return { column: col, direction: 'desc' };
      if (prev.direction === 'desc') return { column: col, direction: 'asc' };
      return { column: null, direction: null };
    });
  }

  // ── Main render ───────────────────────────────────────────────────────────────
  const processingActive  = !!jobId && !extractionComplete;
  const showBanner        = catalog?.problem_catalog && !bannerDismissed;
  const ZIP_FLAG_TYPES = new Set(['zip_password_required', 'zip_zipcrypto_unsupported', 'zip_aes_encryption_unsupported']);
  const encryptedZipFlag = catalogFlags.find(f => ZIP_FLAG_TYPES.has(f.flag_type));
  const showZipBanner = !!encryptedZipFlag && !zipBannerDismissed;
  // T11 (2026-05-27 dispatch) — Class 2 IGNORED_HEADERS_V1 banner derivation
  // via pure helper for testability (see client/src/utils/class2Banner.ts).
  const class2BannerState = deriveClass2BannerState(
    (catalog as any)?.ignored_headers_v1,
    class2BannerDismissed,
  );
  const showClass2Banner = class2BannerState.show;
  const class2IgnoredHeaders = class2BannerState.list;
  // UB#10 (Phase 2B SD4) — catalog-level flag info banner. Surfaces all non-ZIP
  // catalog-level (variant_id NULL) flags at info-severity per D1 (Halt #5).
  // ZIP encryption flags keep their own red banner above. quality_signals is an
  // admin-only telemetry table, never returned by /catalog-flags (D3).
  const catalogFlagsBannerState = deriveCatalogFlagsBanner(catalogFlags, catalogFlagsBannerDismissed);
  const showCatalogFlagsBanner = catalogFlagsBannerState.show;
  const toolbarTop        = 56 + bannerH + (showBanner ? 44 : 0) + (showZipBanner ? 44 : 0) + (showClass2Banner ? 44 : 0) + (showCatalogFlagsBanner ? 44 : 0);
  const tableHeight = `calc(100vh - ${toolbarTop + 36}px)`;

  // Mobile fallback removed — had cross-scope references (productCount, readyCount
  // declared in ExportModal, not ReviewTable). Desktop table renders on all viewports
  // for now. Revisit mobile UX post-launch.

  return (
    <div style={{ height: '100vh', overflow: 'hidden', background: 'var(--color-bg)', paddingTop: bannerH }}>
      <style>{`
        .ie:hover { border-color: transparent !important; background: transparent !important; }
        .ie:focus { border-color: transparent !important; background: transparent !important; box-shadow: none !important; outline: none !important; }
        table td { border-right: 1px solid var(--color-border); border-bottom: 1px solid var(--color-border); }
        table td:last-child { border-right: none; }
        table td:hover { outline: 1px solid var(--color-border-strong); outline-offset: -1px; }
        table td.cell-na:hover { outline: none; }
        td:has(input:focus), td:has(textarea:focus), td:has([contenteditable]:focus) { outline: 2px solid var(--color-primary); outline-offset: -2px; }
        .ie::placeholder { color: var(--color-text-3); }
        .prod-row:hover { background: color-mix(in srgb, var(--color-primary-light) 30%, transparent) !important; }
        .var-row:hover td { background: color-mix(in srgb, var(--color-primary-light) 30%, transparent) !important; }
        .var-row:hover td.sticky-col { background: color-mix(in srgb, var(--color-primary-light) 30%, var(--color-surface)) !important; }
        .chip:hover:not(.chip-disabled) { background: rgba(0,0,0,0.04) !important; }
        .chip-active { background: rgba(0,0,0,0.06) !important; }
        .chip-disabled { opacity: 0.6; cursor: default !important; }
        .tb-btn:hover { background: var(--color-surface-2) !important; }
        thead th { position: sticky; top: 0; background: var(--color-surface-2); z-index: 10; }
        thead th:first-child { box-shadow: -20px 0 0 var(--color-surface-2); }
        tr:last-child td { border-bottom: none; }
        tbody tr td:first-child { box-shadow: inset 0 -1px 0 var(--color-border); border-bottom: none; }
        tr[data-status="red"] > td:first-child { box-shadow: inset 3px 0 0 var(--color-red), inset 0 -1px 0 var(--color-border); }
        tr[data-status="yellow"] > td:first-child { box-shadow: inset 3px 0 0 var(--color-yellow), inset 0 -1px 0 var(--color-border); }
        tr.var-row[data-status="red"] > td:first-child { box-shadow: inset 3px 0 0 var(--color-red), inset 0 -1px 0 var(--color-border); }
        tr.var-row[data-status="yellow"] > td:first-child { box-shadow: inset 3px 0 0 var(--color-yellow), inset 0 -1px 0 var(--color-border); }
        .search-input { width: 180px; transition: width 0.15s ease; }
        .search-input:focus { width: 240px; }
        mark { background: #FEF9C3; color: inherit; border-radius: 2px; padding: 0 1px; }
        @keyframes rowEntry { from { transform: translateY(6px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        @keyframes rowHighlight { 0% { background: var(--color-primary-light); } 100% { background: transparent; } }
        tr[data-highlight] { animation: rowHighlight 1.5s ease-out forwards; }
        tr[data-highlight] > td.sticky-col { animation: rowHighlight 1.5s ease-out forwards; }
        .table-locked tbody tr { pointer-events: none !important; }
        .table-locked tbody td { color: var(--color-text-3) !important; opacity: 0.7 !important; }
        .table-locked .ie { pointer-events: none !important; }
        .table-locked tbody td input, .table-locked tbody td textarea { pointer-events: auto !important; }
        .table-locked input[type="checkbox"] { pointer-events: none !important; opacity: 0.35 !important; }
        .table-locked button { pointer-events: none !important; opacity: 0.5 !important; }
        tbody td { padding-top: 6px; padding-bottom: 6px; }
        .skeleton-row td { padding: 6px 8px; }
        @keyframes shimmer { 0% { background-position: -400px 0; } 100% { background-position: 400px 0; } }
        @keyframes drawerIn { from { opacity: 0; max-height: 0; } to { opacity: 1; max-height: 400px; } }
        .th-resize-handle { position: absolute; right: -4px; top: 0; bottom: 0; width: 8px; cursor: col-resize !important; background: transparent; z-index: 2; }
        .th-resize-handle::after { content: ''; position: absolute; right: 3px; top: 0; bottom: 0; width: 2px; background: transparent; transition: background 0.1s; }
        .th-resize-handle:hover::after, .th-resize-handle:active::after { background: var(--color-primary); }
        th { position: relative; }
        .sortable-th { cursor: pointer; user-select: none; }
        .sortable-th .sort-icon { opacity: 0; transition: opacity 0.12s; }
        .sortable-th:hover .sort-icon { opacity: 0.5; }
        table td { font-size: 13px; }
        .sortable-th .sort-icon.sort-active { opacity: 1; }
        .row-drag-handle { opacity: 0; transition: opacity 0.1s ease; cursor: grab; }
        tr:hover .row-drag-handle { opacity: 1; }
        .expand-chevron { display: inline-flex; align-items: center; flex-shrink: 0; background: none; border: none; cursor: pointer; color: var(--color-text-3); padding: 0; margin-left: 2px; transition: color 120ms; gap: 2px; }
        .expand-chevron[data-expanded="true"] { color: var(--color-text-2); }
        .expand-chevron .variant-count { display: none; font-size: 11px; color: var(--color-text-3); white-space: nowrap; }
        tr:hover .expand-chevron .variant-count { display: inline; }
      `}</style>

      {/* Header */}
      <TopBar
        name={catalogName}
        navigate={navigate} catalogId={catalogId} renameSaving={renameSaving}
        onRename={handleRename}
        onExport={handleExportClick}
        userPlan={userPlan} errorCount={errorCount}
        stickyTop={bannerH}
        processing={processingActive}
        productCount={filterCounts.all}
        variantCount={allVariants.length}
      />

      {/* Extraction progress feed — visible while job_id is in the URL */}
      {!!jobId && (
        <ProcessingFeed
          jobStatus={jobStatus}
          pagesScanned={catalog?.pages_scanned ?? 0}
          pageCount={catalog?.page_count ?? 0}
          productCount={products.length}
          queuePosition={queuePosition}
          readyCount={liveReadyCount}
          flaggedCount={liveFlaggedCount}
          pct={Math.max(2, extractionPct ?? 0)}
          complete={extractionComplete}
          jobStartedAt={jobStartedAt}
        />
      )}

      {/* Problem catalog banner */}
      {showBanner && (
        <div style={{
          position: 'sticky', top: 100 + bannerH, zIndex: 22,
          background: 'var(--color-yellow-bg)', borderBottom: '1px solid var(--color-yellow-border)',
          padding: '0 24px', height: 44, display: 'flex', alignItems: 'center',
        }}>
          <AlertTriangle size={16} style={{ color: 'var(--color-yellow)', flexShrink: 0, marginRight: 10 }} />
          <span style={{ fontSize: 13, color: 'var(--color-yellow-text)', flex: 1 }}>
            This catalog has an unusual format. More rows than average require your review. This is normal for handwritten, scanned, or non-standard catalogs.
          </span>
          <button onClick={dismissBanner} style={{ ...iconBtn, color: 'var(--color-yellow-text)' }} aria-label="Dismiss banner">
            <X size={16} />
          </button>
        </div>
      )}

      {/* P3.3: Extraction warnings banner — when job completed with degradation */}
      {jobStatus === 'completed_with_warnings' && !warningBannerDismissed && (
        <div style={{
          position: 'sticky', top: 100 + bannerH + (showBanner ? 44 : 0), zIndex: 22,
          background: 'var(--color-yellow-bg)', borderBottom: '1px solid var(--color-yellow-border)',
          padding: '0 24px', minHeight: 44, display: 'flex', alignItems: 'center',
        }}>
          <AlertTriangle size={16} style={{ color: 'var(--color-yellow)', flexShrink: 0, marginRight: 10 }} />
          <span style={{ fontSize: 13, color: 'var(--color-yellow-text)', flex: 1 }}>
            Some pages were harder to read{extractionWarnings.length > 0 ? ` — ${extractionWarnings.length} product${extractionWarnings.length !== 1 ? 's' : ''} may need review` : ''}.
            {extractionWarnings.length > 0 && (
              <> Check the highlighted rows to make sure the data looks right.</>
            )}
          </span>
          <button onClick={() => setWarningBannerDismissed(true)} style={{ ...iconBtn, color: 'var(--color-yellow-text)' }} aria-label="Dismiss warning banner">
            <X size={16} />
          </button>
        </div>
      )}

      {/* Phase 4: Encrypted ZIP banner */}
      {showZipBanner && (
        <div style={{
          position: 'sticky', top: 100 + bannerH + (showBanner ? 44 : 0) + (jobStatus === 'completed_with_warnings' && !warningBannerDismissed ? 44 : 0), zIndex: 22,
          background: 'var(--color-red-bg)', borderBottom: '1px solid var(--color-red-border, var(--color-border))',
          padding: '0 24px', height: 44, display: 'flex', alignItems: 'center',
        }}>
          <AlertTriangle size={16} style={{ color: 'var(--color-red)', flexShrink: 0, marginRight: 10 }} />
          <span style={{ fontSize: 13, color: 'var(--color-red-text, var(--color-text))', flex: 1 }}>
            {encryptedZipFlag.message || 'This ZIP is encrypted and cannot be processed. Please upload an unencrypted ZIP or upload the files directly.'}
          </span>
          <button onClick={() => setZipBannerDismissed(true)} style={{ ...iconBtn, color: 'var(--color-red-text, var(--color-text))' }} aria-label="Dismiss encrypted ZIP banner">
            <X size={16} />
          </button>
        </div>
      )}

      {/* T11 (2026-05-27 dispatch) — Class 2 IGNORED_HEADERS_V1 catalog-summary
        banner. Info severity. Reads ignored_headers_v1 from /api/catalogs/:id
        top-level field (aggregated server-side from catalog_files.role_reasons.
        columnMap.ignoredHeadersV1Hit). Copy locked at COPY.md §8.4. Mirrors
        encrypted-ZIP banner pattern (positioning, dismissal); uses info-token
        styling (Phase 2.5 V2 info color tokens) + Info Lucide icon. */}
      {showClass2Banner && (
        <div
          data-testid="class2-banner"
          style={{
            position: 'sticky',
            top: 100 + bannerH + (showBanner ? 44 : 0) + (jobStatus === 'completed_with_warnings' && !warningBannerDismissed ? 44 : 0) + (showZipBanner ? 44 : 0),
            zIndex: 22,
            background: 'var(--color-info-bg, var(--color-surface-2))',
            borderBottom: '1px solid var(--color-info-border, var(--color-border))',
            padding: '0 24px', height: 44, display: 'flex', alignItems: 'center',
          }}
        >
          <Info size={16} style={{ color: 'var(--color-info-text, var(--color-text-2))', flexShrink: 0, marginRight: 10 }} />
          <span style={{ fontSize: 13, color: 'var(--color-info-text, var(--color-text))', flex: 1 }}>
            We recognized these columns from your file but they’re not projected to product fields in V1: {class2IgnoredHeaders.join(', ')}. Verify they aren’t critical for your import — if they are, contact support.
          </span>
          <button
            onClick={() => setClass2BannerDismissed(true)}
            style={{ ...iconBtn, color: 'var(--color-info-text, var(--color-text-2))' }}
            aria-label="Dismiss recognized-columns banner"
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* UB#10 (Phase 2B SD4, 2026-05-29) — catalog-level flag info banner.
        Surfaces every non-ZIP catalog-level (variant_id NULL) flag at
        info-severity per D1 (Halt #5). source_recall_drift renders the
        merchant-action message per D2 (Halt #8). Mirrors the Class 2 banner
        pattern (positioning, info-token styling, Info icon, dismissal).
        Each flag_type → merchant label (FLAG_LABELS) + message
        (DRAWER_MESSAGES, else stored message). Raw flag_type codes never
        shown — defensive fallback to the generic 'Catalog notice' label. */}
      {showCatalogFlagsBanner && (
        <div
          data-testid="catalog-flags-banner"
          style={{
            position: 'sticky',
            top: 100 + bannerH + (showBanner ? 44 : 0) + (jobStatus === 'completed_with_warnings' && !warningBannerDismissed ? 44 : 0) + (showZipBanner ? 44 : 0) + (showClass2Banner ? 44 : 0),
            zIndex: 22,
            background: 'var(--color-info-bg, var(--color-surface-2))',
            borderBottom: '1px solid var(--color-info-border, var(--color-border))',
            padding: '8px 24px', minHeight: 44, display: 'flex', alignItems: 'flex-start',
          }}
        >
          <Info size={16} style={{ color: 'var(--color-info-text, var(--color-text-2))', flexShrink: 0, marginRight: 10, marginTop: 2 }} />
          <div style={{ fontSize: 13, color: 'var(--color-info-text, var(--color-text))', flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
            {catalogFlagsBannerState.items.map(item => (
              <span key={item.flag_type}>
                <strong style={{ fontWeight: 600 }}>{FLAG_LABELS[item.flag_type] || 'Catalog notice'}{item.count > 1 ? ` (${item.count})` : ''}:</strong>{' '}
                {CATALOG_FLAG_BANNER_MESSAGES[item.flag_type] || item.message || FLAG_LABELS[item.flag_type] || 'Review this catalog before exporting.'}
              </span>
            ))}
          </div>
          <button
            onClick={() => setCatalogFlagsBannerDismissed(true)}
            style={{ ...iconBtn, color: 'var(--color-info-text, var(--color-text-2))', flexShrink: 0 }}
            aria-label="Dismiss catalog notices banner"
          >
            <X size={16} />
          </button>
        </div>
      )}

      {/* Toolbar */}
      <div style={{
        position: 'sticky', top: toolbarTop, zIndex: 20,
        background: 'var(--color-surface)', borderBottom: '1px solid var(--color-border)',
        padding: '0 20px',
      }}>
        <div style={{ margin: '0 auto', height: 36, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, width: '100%' }}>
          {/* Filter chips — always visible; BulkActionBar overlays this toolbar when rows are selected */}
          <div style={{ display: 'flex', gap: 6, flexWrap: 'nowrap', overflowX: 'auto', alignItems: 'center', flex: 1 }}>
            {(['all', 'needs_review', 'green']).map(f => {
              const count = filterCounts[f];
              const hasNoFilters = activeFilters.status.length === 0 && activeFilters.type.length === 0 && activeFilters.tags.length === 0;
              const active = f === 'all' ? hasNoFilters : (activeFilters.status.length === 1 && activeFilters.status[0] === (f === 'needs_review' ? 'needs_review' : 'green') && activeFilters.type.length === 0 && activeFilters.tags.length === 0);
              const zeroed = count === 0 && f !== 'all';
              const label = f === 'needs_review' ? 'Needs review' : f === 'green' ? 'Ready' : 'All';
              return (
                <button
                  key={f}
                  onClick={() => {
                    if (zeroed && f !== 'all') return;
                    if (!phFirstFilter.current) {
                      phFirstFilter.current = true;
                      capture('first_filter_use', { filter_name: f });
                    }
                    if (f === 'all') {
                      setFilter('all');
                      setActiveFilters({ status: [], type: [], tags: [], price: [], image: [], category: [] });
                    } else {
                      setFilter(f);
                      setActiveFilters(prev => ({ ...prev, status: f === 'needs_review' ? ['needs_review'] : ['green'] }));
                    }
                  }}
                  style={{
                    display: 'inline-flex', alignItems: 'center',
                    padding: '4px 12px', borderRadius: 8,
                    fontSize: 13, border: 'none',
                    fontWeight: active ? 500 : 400,
                    background: active
                      ? (f === 'needs_review' ? 'rgba(245,158,11,0.13)' : f === 'green' ? 'rgba(34,197,94,0.12)' : 'rgba(0,0,0,0.08)')
                      : 'transparent',
                    color: zeroed ? 'var(--color-text-3)'
                      : active
                        ? (f === 'needs_review' ? 'var(--color-yellow-text)' : f === 'green' ? 'var(--color-green-text)' : 'var(--color-text)')
                        : 'var(--color-text)',
                    cursor: zeroed ? 'default' : 'pointer', whiteSpace: 'nowrap',
                    transition: 'background 0.12s, color 0.12s',
                  }}
                  onMouseEnter={e => { if (!active) e.currentTarget.style.background = 'rgba(0,0,0,0.04)'; }}
                  onMouseLeave={e => { if (!active) e.currentTarget.style.background = 'transparent'; }}
                >
                  {label}
                  <span style={{ marginLeft: 4, fontSize: 12, fontWeight: 400, textDecoration: zeroed ? 'line-through' : 'none' }}>
                    {count}
                  </span>
                </button>
              );
            })}
            {(filter !== 'all' || searchQuery) && (
              <span style={{ fontSize: 12, color: 'var(--color-text-3)', marginLeft: 4, whiteSpace: 'nowrap' }}>
                Showing {filteredProducts.length} of {products.length}
              </span>
            )}
          </div>

          {/* Right controls — Airtable icon+text pattern */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
            {/* Import Images — always interactive */}
            <button
              onClick={() => setShowImportImg(true)}
              className="tb-icon-text"
              style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 8px', background: 'transparent', border: 'none', borderRadius: 'var(--r-sm)', fontSize: 13, fontWeight: 400, color: 'var(--color-text-2)', cursor: 'pointer', whiteSpace: 'nowrap' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--color-surface-2)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <Image size={14} />Import Images
            </button>

            {/* Search / Filter / Sort / Columns — dimmed during extraction */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 2, opacity: processingActive ? 0.4 : 1, pointerEvents: processingActive ? 'none' : undefined }}>

            {/* Search */}
            <AnimatePresence mode="wait">
            {searchOpen || searchQuery ? (
              <motion.div
                key="search-input"
                initial={{ width: 0, opacity: 0 }}
                animate={{ width: 160, opacity: 1 }}
                exit={{ width: 0, opacity: 0 }}
                transition={{ duration: 0.1, ease: 'easeOut' }}
                style={{ position: 'relative', display: 'flex', alignItems: 'center', overflow: 'hidden' }}
              >
                <Search size={14} style={{ position: 'absolute', left: 9, color: 'var(--color-text-3)', pointerEvents: 'none' }} />
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Escape') { setSearchQuery(''); setSearchOpen(false); } }}
                  onBlur={() => { if (!searchQuery) setSearchOpen(false); }}
                  placeholder="Search…"
                  className="search-input ie"
                  autoFocus
                  style={{
                    height: 28, paddingLeft: 28, paddingRight: searchQuery ? 28 : 10,
                    border: '1px solid var(--color-border)', borderRadius: 'var(--r-sm)',
                    fontSize: 13, background: 'var(--color-surface)',
                    color: 'var(--color-text)', outline: 'none', width: 160,
                  }}
                />
                {searchQuery && (
                  <button
                    onClick={() => setSearchQuery('')}
                    style={{ position: 'absolute', right: 6, background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-3)', display: 'flex', padding: 2 }}
                    aria-label="Clear search"
                  >
                    <X size={12} />
                  </button>
                )}
              </motion.div>
            ) : (
              <motion.button
                key="search-btn"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.1, ease: 'easeOut' }}
                onClick={() => setSearchOpen(true)}
                className="tb-icon-text"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 8px', background: 'transparent', border: 'none', borderRadius: 'var(--r-sm)', fontSize: 13, fontWeight: 400, color: 'var(--color-text-2)', cursor: 'pointer', whiteSpace: 'nowrap' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--color-surface-2)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <Search size={14} />Search
              </motion.button>
            )}
            </AnimatePresence>

            {/* Filter — opens filter panel dropdown */}
            {(() => {
              const hasAnyFilter = activeFilters.type.length > 0 || activeFilters.tags.length > 0 || (activeFilters.price || []).length > 0 || (activeFilters.image || []).length > 0 || (activeFilters.category || []).length > 0;
              return (
                <div style={{ position: 'relative' }}>
                  <button
                    ref={filterBtnRef}
                    onClick={() => setShowFilterPanel(v => !v)}
                    className="tb-icon-text"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 8px', background: showFilterPanel || hasAnyFilter ? 'var(--color-primary-light)' : 'transparent', border: 'none', borderRadius: 'var(--r-sm)', fontSize: 13, fontWeight: 400, color: showFilterPanel || hasAnyFilter ? 'var(--color-primary)' : 'var(--color-text-2)', cursor: 'pointer', whiteSpace: 'nowrap' }}
                    onMouseEnter={e => { if (!showFilterPanel && !hasAnyFilter) e.currentTarget.style.background = 'var(--color-surface-2)'; }}
                    onMouseLeave={e => { if (!showFilterPanel && !hasAnyFilter) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <FilterIcon size={14} />Filter
                  </button>
                  <AnimatePresence>
                  {showFilterPanel && (
                    <motion.div
                      ref={filterPanelRef}
                      initial={{ opacity: 0, y: -4 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, y: -4 }}
                      transition={{ duration: 0.12, ease: 'easeOut' }}
                      style={{
                        position: 'absolute', top: '100%', right: 0, marginTop: 4,
                        background: 'var(--color-surface)', border: '1px solid var(--color-border)',
                        borderRadius: 8, boxShadow: 'var(--shadow-card)', padding: 12, minWidth: 280, zIndex: 200,
                      }}
                    >
                      {/* Filter sections: Image → Price → Type → Tags → Category */}
                      {[
                        ['image', 'Image', ['With image', 'Missing image'], activeFilters.image || []],
                        ['price', 'Price', ['With price', 'Missing price'], activeFilters.price || []],
                        ['type', 'Type', filterOptions.type, activeFilters.type],
                        ['tags', 'Tags', filterOptions.tags, activeFilters.tags],
                        ['category', 'Category', ['Categorized', 'Uncategorized'], activeFilters.category || []],
                      ].map(([key, label, options, selected], idx) => (
                        <div key={key} style={{ borderTop: idx > 0 ? '1px solid var(--color-border)' : 'none', paddingTop: idx > 0 ? 8 : 0, marginTop: idx > 0 ? 8 : 0 }}>
                          <button
                            onClick={() => setExpandedFilterSections(prev => ({ ...prev, [key]: !prev[key] }))}
                            style={{
                              display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%',
                              background: 'none', border: 'none', cursor: 'pointer', padding: '2px 0', fontSize: 12, color: 'var(--color-text)',
                            }}
                          >
                            <span style={{ fontWeight: 500 }}>
                              {label}
                              {selected.length > 0 && <span style={{ marginLeft: 4, fontSize: 11, color: 'var(--color-primary)', fontWeight: 600 }}>({selected.length})</span>}
                            </span>
                            <ChevronDown size={12} style={{ transform: expandedFilterSections[key] ? 'rotate(180deg)' : 'none', transition: 'transform 0.15s', color: 'var(--color-text-3)' }} />
                          </button>
                          {expandedFilterSections[key] && (
                            <div style={{ maxHeight: 160, overflowY: 'auto', marginTop: 4 }}>
                              {options.length === 0 ? (
                                <div style={{ fontSize: 12, color: 'var(--color-text-3)', padding: '4px 0' }}>No values</div>
                              ) : options.map(opt => {
                                const displayLabel = opt;
                                const isChecked = selected.includes(opt);
                                return (
                                  <label
                                    key={opt}
                                    style={{
                                      display: 'flex', alignItems: 'center', gap: 6, width: '100%',
                                      cursor: 'pointer', padding: '3px 0', fontSize: 13, color: 'var(--color-text)',
                                    }}
                                  >
                                    <input
                                      type="checkbox"
                                      checked={isChecked}
                                      onChange={() => {
                                        setActiveFilters(prev => ({
                                          ...prev,
                                          [key]: isChecked ? prev[key].filter(v => v !== opt) : [...prev[key], opt],
                                        }));
                                      }}
                                      style={{ accentColor: 'var(--color-primary)', margin: 0, flexShrink: 0 }}
                                    />
                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayLabel}</span>
                                  </label>
                                );
                              })}
                            </div>
                          )}
                        </div>
                      ))}
                    </motion.div>
                  )}
                  </AnimatePresence>
                </div>
              );
            })()}


            {/* Columns */}
            <div ref={colDropdownRef} style={{ position: 'relative' }}>
              <button
                onClick={() => setShowColDropdown(v => !v)}
                className="tb-icon-text"
                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, padding: '4px 8px', background: showColDropdown ? 'var(--color-primary-light)' : 'transparent', border: 'none', borderRadius: 'var(--r-sm)', fontSize: 13, fontWeight: 400, color: showColDropdown ? 'var(--color-primary)' : 'var(--color-text-2)', cursor: 'pointer', whiteSpace: 'nowrap' }}
                onMouseEnter={e => { if (!showColDropdown) e.currentTarget.style.background = 'var(--color-surface-2)'; }}
                onMouseLeave={e => { if (!showColDropdown) e.currentTarget.style.background = 'transparent'; }}
              >
                <Columns3 size={14} />Columns
              </button>
              <AnimatePresence>
              {showColDropdown && (() => {
                const cq = colSearchQuery.toLowerCase();
                const cm = (s) => !cq || s.toLowerCase().includes(cq);
                const sep = () => <div style={{ height: 1, background: 'var(--color-border)', margin: '4px 0' }} />;
                const sHdr = (t) => <div style={{ padding: '5px 14px 3px', fontSize: 11, fontWeight: 600, color: 'var(--color-text-3)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{t}</div>;
                const tog = ({ key, label }) => (
                  <label key={key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 14px', cursor: 'pointer', fontSize: 13, color: 'var(--color-text)', userSelect: 'none' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--color-surface-2)'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}
                  >
                    <input type="checkbox" checked={!!colVis[key]} onChange={() => toggleColumn(key)} style={{ cursor: 'pointer', accentColor: 'var(--color-primary)', width: 15, height: 15, flexShrink: 0 }} />
                    {label}
                  </label>
                );
                const soon = (label) => (
                  <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 14px', fontSize: 13, color: 'var(--color-text)', userSelect: 'none', opacity: 0.45, pointerEvents: 'none' }}>
                    <input type="checkbox" readOnly checked={false} style={{ width: 15, height: 15, flexShrink: 0, accentColor: 'var(--color-primary)' }} />
                    <span style={{ flex: 1 }}>{label}</span>
                    <span style={{ fontSize: 11, color: 'var(--color-text-3)' }}>coming soon</span>
                  </div>
                );
                const alwaysLabels = ['Product title', 'SKU', 'Price', 'Review'];
                const generalItems = [
                  { key: 'image',           label: 'Images' },
                  { key: 'productCategory', label: 'Product category' },
                  { key: 'body',            label: 'Description' },
                  { key: 'status',          label: 'Status' },
                  { key: 'type',            label: 'Type' },
                  { key: 'vendor',          label: 'Vendor' },
                  { key: 'tags',            label: 'Tags' },
                  { key: 'collection',      label: 'Collection' },
                  ...(opt1HasData ? [{ key: 'option1', label: opt1Label }] : []),
                  ...(opt2HasData ? [{ key: 'option2', label: opt2Label }] : []),
                  ...(opt3HasData ? [{ key: 'option3', label: opt3Label }] : []),
                ];
                const pricingItems = [{ key: 'compareAt', label: 'Compare-at price' }];
                const inventoryItems = [
                  { key: 'barcode',            label: 'Barcode' },
                  { key: 'inventory',          label: 'Inventory quantity' },
                  { key: 'continueSellingOos', label: 'Continue selling when out of stock' },
                ];
                const shippingItems = [{ key: 'weight', label: 'Weight (grams)' }];
                const seoItems = [
                  { key: 'seoTitle', label: 'Page title (SEO)' },
                  { key: 'seoDesc',  label: 'Meta description (SEO)' },
                ];
                const metafieldItems = [].filter(Boolean);
                const METAFIELDS_DEFAULT = 3;
                const shownMetafields = showMoreMetafields ? metafieldItems : metafieldItems.slice(0, METAFIELDS_DEFAULT);
                return (
                  <motion.div
                    key="columns-panel"
                    initial={{ opacity: 0, y: -4 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, y: -4 }}
                    transition={{ duration: 0.12, ease: 'easeOut' }}
                    style={{ position: 'absolute', top: 36, right: 0, zIndex: 300, background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 'var(--r-md)', boxShadow: '0 4px 16px rgba(0,0,0,0.12)', width: 280, overflowY: 'auto', maxHeight: 'calc(100vh - 100px)' }}>
                    {/* Search — sticky */}
                    <div style={{ position: 'sticky', top: 0, zIndex: 1, background: 'var(--color-surface)', padding: '6px 10px 4px', borderBottom: '1px solid var(--color-border)' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6, background: 'var(--color-surface-2)', borderRadius: 'var(--r-sm)', padding: '4px 8px' }}>
                        <Search size={12} style={{ flexShrink: 0, color: 'var(--color-text-3)' }} />
                        <input
                          type="text"
                          placeholder="Search fields"
                          value={colSearchQuery}
                          onChange={e => setColSearchQuery(e.target.value)}
                          style={{ border: 'none', background: 'transparent', fontSize: 13, color: 'var(--color-text)', outline: 'none', flex: 1, minWidth: 0 }}
                        />
                        {colSearchQuery && (
                          <button onClick={() => setColSearchQuery('')} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', color: 'var(--color-text-3)', flexShrink: 0 }}>
                            <X size={12} />
                          </button>
                        )}
                      </div>
                    </div>
                    {/* Always visible */}
                    {alwaysLabels.some(cm) && (
                      <div style={{ paddingTop: 2 }}>
                        {sHdr('Always visible')}
                        {alwaysLabels.filter(cm).map(label => (
                          <div key={label} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 14px', fontSize: 13, color: 'var(--color-text)', userSelect: 'none', opacity: 0.45, pointerEvents: 'none' }}>
                            <input type="checkbox" checked readOnly style={{ width: 15, height: 15, flexShrink: 0, accentColor: 'var(--color-primary)' }} />
                            <span>{label}</span>
                          </div>
                        ))}
                      </div>
                    )}
                    {/* General */}
                    {generalItems.some(({ label }) => cm(label)) && (
                      <>
                        {sep()}
                        {sHdr('General')}
                        {generalItems.filter(({ label }) => cm(label)).map(tog)}
                      </>
                    )}
                    {/* Pricing */}
                    {([...pricingItems.map(({ label }) => label), 'Cost per item']).some(cm) && (
                      <>
                        {sep()}
                        {sHdr('Pricing')}
                        {pricingItems.filter(({ label }) => cm(label)).map(tog)}
                        {cm('Cost per item') && soon('Cost per item')}
                      </>
                    )}
                    {/* Inventory */}
                    {inventoryItems.some(({ label }) => cm(label)) && (
                      <>
                        {sep()}
                        {sHdr('Inventory')}
                        {inventoryItems.filter(({ label }) => cm(label)).map(tog)}
                      </>
                    )}
                    {/* Shipping */}
                    {shippingItems.some(({ label }) => cm(label)) && (
                      <>
                        {sep()}
                        {sHdr('Shipping')}
                        {shippingItems.filter(({ label }) => cm(label)).map(tog)}
                      </>
                    )}
                    {/* SEO */}
                    {([...seoItems.map(({ label }) => label), 'URL handle (SEO)']).some(cm) && (
                      <>
                        {sep()}
                        {sHdr('SEO')}
                        {seoItems.filter(({ label }) => cm(label)).map(tog)}
                        {cm('URL handle (SEO)') && soon('URL handle (SEO)')}
                      </>
                    )}
                    {/* Category Metafields — dynamic */}
                    {metafieldItems.length > 0 && metafieldItems.some(({ label }) => cm(label)) && (
                      <>
                        {sep()}
                        {sHdr('Category Metafields')}
                        {shownMetafields.filter(({ label }) => cm(label)).map(tog)}
                        {metafieldItems.length > METAFIELDS_DEFAULT && (
                          <button
                            onClick={() => setShowMoreMetafields(v => !v)}
                            style={{ display: 'flex', alignItems: 'center', gap: 4, padding: '3px 14px 5px', width: '100%', background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--color-primary)', textAlign: 'left' }}
                            onMouseEnter={e => e.currentTarget.style.background = 'var(--color-surface-2)'}
                            onMouseLeave={e => e.currentTarget.style.background = ''}
                          >
                            {showMoreMetafields ? 'Show less' : `Show more (${metafieldItems.length - METAFIELDS_DEFAULT} more)`}
                          </button>
                        )}
                      </>
                    )}
                    {/* Custom columns */}
                    {sep()}
                    <div style={{ padding: '3px 14px 4px', fontSize: 11, fontWeight: 600, color: 'var(--color-text-3)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Custom columns</div>
                    {customColumns.length === 0 && (
                      <div style={{ padding: '3px 14px 5px', fontSize: 12, color: 'var(--color-text-3)' }}>No custom columns yet.</div>
                    )}
                    {customColumns.map(col => (
                      <div key={col.id} style={{ display: 'flex', alignItems: 'center', gap: 2, padding: '4px 8px 4px 14px', fontSize: 13, color: 'var(--color-text)' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--color-surface-2)'}
                        onMouseLeave={e => e.currentTarget.style.background = ''}
                      >
                        <span style={{ flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{col.column_label}</span>
                        <button title="Edit column" onClick={e => { e.stopPropagation(); setEditingCustomCol(col); setShowCustomColModal(true); setShowColDropdown(false); }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: 'var(--color-text-3)', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                          <Pencil size={12} />
                        </button>
                        <button title="Delete column" onClick={e => { e.stopPropagation(); handleDeleteCustomColInline(col); }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: 'var(--color-text-3)', display: 'flex', alignItems: 'center', flexShrink: 0 }}>
                          <Trash2 size={12} />
                        </button>
                      </div>
                    ))}
                    {customColumns.length > 1 && (
                      <button onClick={() => { setShowColDropdown(false); setShowColReorder(true); }} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '3px 14px', width: '100%', background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--color-text-3)', textAlign: 'left' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--color-surface-2)'}
                        onMouseLeave={e => e.currentTarget.style.background = ''}
                      >
                        <GripVertical size={12} />
                        Reorder columns
                      </button>
                    )}
                    <button onClick={() => { setEditingCustomCol(null); setShowCustomColModal(true); setShowColDropdown(false); }} style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '5px 14px 7px', width: '100%', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: 'var(--color-primary)', fontWeight: 500, textAlign: 'left' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--color-surface-2)'}
                      onMouseLeave={e => e.currentTarget.style.background = ''}
                    >
                      <Plus size={12} />
                      Add custom column
                    </button>
                  </motion.div>
                );
              })()}
              </AnimatePresence>
            </div>
            </div>{/* end Search/Filter/Sort/Columns dimming wrapper */}
          </div>
        </div>
        {/* BulkActionBar — overlays FilterBar at same position, higher z-index */}
        <AnimatePresence>
          {(selectedIds.size > 0 || selectedVariantIds.size > 0) && !processingActive && (() => {
            const productVariantMap = {};
            for (const p of products) {
              for (const v of (p.variants || [])) productVariantMap[v.id] = p.id;
            }
            const hasPartialVariantSelection = [...selectedVariantIds].some(vid => !selectedIds.has(productVariantMap[vid]));
            return (
              <BulkActionBar
                key="bulk-action-bar"
                selectedIds={[...selectedIds]}
                selectedVariantIds={[...selectedVariantIds]}
                hasPartialVariantSelection={hasPartialVariantSelection}
                onDeselect={() => { setSelectedIds(new Set()); setSelectedVariantIds(new Set()); lastSelectedRef.current = null; }}
                onDeleted={handleBulkDeleted}
                onVariantsDeleted={handleBulkVariantsDeleted}
                onReverted={handleBulkDeleteReverted}
                onVariantDeleteReverted={handleBulkVariantDeleteReverted}
                onBulkUpdated={handleBulkUpdated}
                onBulkResolved={handleBulkResolved}
                onBulkMarkup={handleBulkMarkup}
                onToast={msg => setToastMsg(msg)}
                products={products}
                totalCount={products.length}
              />
            );
          })()}
        </AnimatePresence>
      </div>

      {/* Active filter + sort pills */}
      {(() => {
        const hasFilters = activeFilters.type.length > 0 || activeFilters.tags.length > 0 || (activeFilters.price || []).length > 0 || (activeFilters.image || []).length > 0 || (activeFilters.category || []).length > 0;
        const hasSort = !!(columnSort.column && columnSort.direction);
        const sortLabelMap = {
          'title_asc': 'Product A\u2192Z', 'title_desc': 'Product Z\u2192A',
          'sku_asc': 'SKU A\u2192Z', 'sku_desc': 'SKU Z\u2192A',
          'price_asc': 'Price low\u2192high', 'price_desc': 'Price high\u2192low',
          'compareAt_asc': 'Compare At low\u2192high', 'compareAt_desc': 'Compare At high\u2192low',
          'status_asc': 'Status A\u2192Z', 'status_desc': 'Status Z\u2192A',
          'type_asc': 'Type A\u2192Z', 'type_desc': 'Type Z\u2192A',
          'vendor_asc': 'Vendor A\u2192Z', 'vendor_desc': 'Vendor Z\u2192A',
          'inventory_asc': 'Inventory low\u2192high', 'inventory_desc': 'Inventory high\u2192low',
          'weight_asc': 'Weight low\u2192high', 'weight_desc': 'Weight high\u2192low',
          'display_order_asc': 'Catalog order',
        };
        const sortLabel = hasSort ? sortLabelMap[`${columnSort.column}_${columnSort.direction}`] || `${columnSort.column} ${columnSort.direction}` : '';
        const pillStyle = { display: 'inline-flex', alignItems: 'center', gap: 4, padding: '3px 10px', borderRadius: 8, background: 'rgba(0,0,0,0.06)', fontSize: 13, color: 'var(--color-text)' };
        return (
        <AnimatePresence>
        {(hasFilters || hasSort || !!searchQuery) && (
        <motion.div
          key="pills-bar"
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          transition={{ duration: 0.12, ease: 'easeOut' }}
          style={{ overflow: 'hidden' }}
        >
        <div style={{
          display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap',
          padding: '6px 20px', background: 'var(--color-surface)', borderBottom: '1px solid var(--color-border)',
        }}>
          {!!searchQuery && (
            <span style={pillStyle}>
              Search: {searchQuery}
              <button onClick={() => { setSearchQuery(''); setSearchOpen(false); }} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', color: 'var(--color-text-3)' }}><X size={10} /></button>
            </span>
          )}
          {hasSort && (
            <span style={pillStyle}>
              Sort: {sortLabel}
              <button onClick={() => setColumnSort({ column: null, direction: null })} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', color: 'var(--color-text-3)' }}><X size={10} /></button>
            </span>
          )}
          {activeFilters.type.map(v => (
            <span key={`t-${v}`} style={pillStyle}>
              Type: {v}
              <button onClick={() => setActiveFilters(prev => ({ ...prev, type: prev.type.filter(x => x !== v) }))} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', color: 'var(--color-text-3)' }}><X size={10} /></button>
            </span>
          ))}
          {activeFilters.tags.map(v => (
            <span key={`tg-${v}`} style={pillStyle}>
              Tags: {v}
              <button onClick={() => setActiveFilters(prev => ({ ...prev, tags: prev.tags.filter(x => x !== v) }))} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', color: 'var(--color-text-3)' }}><X size={10} /></button>
            </span>
          ))}
          {(activeFilters.price || []).map(v => (
            <span key={`pr-${v}`} style={pillStyle}>
              Price: {v}
              <button onClick={() => setActiveFilters(prev => ({ ...prev, price: (prev.price || []).filter(x => x !== v) }))} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', color: 'var(--color-text-3)' }}><X size={10} /></button>
            </span>
          ))}
          {(activeFilters.image || []).map(v => (
            <span key={`im-${v}`} style={pillStyle}>
              Image: {v}
              <button onClick={() => setActiveFilters(prev => ({ ...prev, image: (prev.image || []).filter(x => x !== v) }))} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', color: 'var(--color-text-3)' }}><X size={10} /></button>
            </span>
          ))}
          {(activeFilters.category || []).map(v => (
            <span key={`cat-${v}`} style={pillStyle}>
              Category: {v}
              <button onClick={() => setActiveFilters(prev => ({ ...prev, category: (prev.category || []).filter(x => x !== v) }))} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, display: 'flex', color: 'var(--color-text-3)' }}><X size={10} /></button>
            </span>
          ))}
          <button
            onClick={() => { setSearchQuery(''); setSearchOpen(false); setColumnSort({ column: null, direction: null }); setActiveFilters({ status: [], type: [], tags: [], price: [], image: [], category: [] }); setFilter('all'); }}
            style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--color-primary)', fontWeight: 500, marginLeft: 'auto', padding: '2px 4px' }}
          >Clear all</button>
        </div>
        </motion.div>
        )}
        </AnimatePresence>
        );
      })()}

      {/* Banners: three mutually exclusive cases */}
      {!processingActive && userPlan !== null && (
        <>
          {/* Case 1 — Free user, first visit: single combined banner */}
          {userPlan === 'free' && !reviewedOnceDismissed && (
            <div style={{ background: 'var(--color-primary-light)', borderBottom: '1px solid var(--color-border)', padding: '10px 24px', display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 13, color: 'var(--color-primary)', flex: 1, lineHeight: 1.5 }}>
                Your first catalog export is free — rows flagged <strong>RED</strong> need fixing before importing, <strong>GREEN</strong> rows are Shopify-ready. Subscribe to process more catalogs.
              </span>
              <button
                onClick={() => {
                  setReviewedOnceDismissed(true);
                  try { localStorage.setItem('cc_reviewed_once', '1'); } catch {}
                }}
                style={{ background: 'none', border: '1px solid var(--color-primary)', borderRadius: 6, padding: '4px 12px', fontSize: 13, fontWeight: 600, color: 'var(--color-primary)', cursor: 'pointer', whiteSpace: 'nowrap' }}
              >
                Got it
              </button>
              <button
                onClick={() => setShowUpgradeModal(true)}
                style={{ background: 'var(--color-primary)', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
              >
                Subscribe
              </button>
            </div>
          )}

          {/* Case 2 — Free user, returning visit: slim preview notice only */}
          {userPlan === 'free' && reviewedOnceDismissed && (
            <div style={{ background: 'var(--color-primary-light)', borderBottom: '1px solid var(--color-border)', padding: '10px 24px', display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 13, color: 'var(--color-primary)', flex: 1, lineHeight: 1.5 }}>
                Your first catalog export is free. Subscribe to process more catalogs.
              </span>
              <button
                onClick={() => setShowUpgradeModal(true)}
                style={{ background: 'var(--color-primary)', color: '#fff', border: 'none', borderRadius: 6, padding: '6px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap' }}
              >
                Subscribe
              </button>
            </div>
          )}

          {/* Case 3 — Paid user, first visit: orientation banner only */}
          {userPlan !== 'free' && !reviewedOnceDismissed && (
            <div style={{ background: 'var(--color-primary-light)', borderBottom: '1px solid var(--color-border)', padding: '10px 24px', display: 'flex', alignItems: 'center', gap: 12 }}>
              <span style={{ fontSize: 13, color: 'var(--color-primary)', flex: 1, lineHeight: 1.5 }}>
                Fix issues before export. Green rows are ready.
              </span>
              <button
                onClick={() => {
                  setReviewedOnceDismissed(true);
                  try { localStorage.setItem('cc_reviewed_once', '1'); } catch {}
                }}
                style={{ background: 'none', border: '1px solid var(--color-primary)', borderRadius: 6, padding: '4px 12px', fontSize: 13, fontWeight: 600, color: 'var(--color-primary)', cursor: 'pointer', whiteSpace: 'nowrap' }}
              >
                Got it
              </button>
            </div>
          )}
        </>
      )}

      {/* Completion banner — slim 36px green strip, dismisses per catalog via localStorage */}
      <AnimatePresence>
      {(jobStatus === 'complete' || jobStatus === 'completed_with_warnings') && !completionBannerDismissed && (() => {
        const N = products.length;
        const copy = liveFlaggedCount === 0
          ? `${N} products ready for Shopify · Saved ${(N * 6 / 60).toFixed(1)} hours`
          : `${N} products found · ${liveFlaggedCount} need review before export`;
        return (
          <motion.div
            key="completion-banner"
            initial={{ opacity: 0, y: -4 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -4 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            style={{
              width: '100%',
              height: 36,
              display: 'flex',
              alignItems: 'center',
              paddingLeft: 16,
              paddingRight: 12,
              background: 'var(--color-green-bg)',
              borderBottom: '1px solid var(--color-green-border)',
              boxSizing: 'border-box',
              flexShrink: 0,
            }}
          >
            <CheckCircle size={14} style={{ color: 'var(--color-green-text)', flexShrink: 0, marginRight: 8 }} />
            <span style={{
              fontSize: 13, fontWeight: 500,
              color: 'var(--color-green-text)',
              flex: 1, minWidth: 0,
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
            }}>
              {copy}
            </span>
            <button
              onClick={() => {
                setCompletionBannerDismissed(true);
                try { if (jobId && catalogId) localStorage.setItem(`cc_completion_${catalogId}`, jobId); } catch { /* non-fatal */ }
              }}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                color: 'var(--color-green-text)', fontSize: 18,
                marginLeft: 'auto', padding: '0 4px',
                display: 'flex', alignItems: 'center', flexShrink: 0, lineHeight: 1,
              }}
              aria-label="Dismiss banner"
            >
              ×
            </button>
          </motion.div>
        );
      })()}
      </AnimatePresence>

      {/* Table + Source Panel split-pane */}
      <div style={{ height: tableHeight, display: 'flex', flexDirection: 'row' }}>
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
      <div style={{ flex: 1, minHeight: 0, overflow: 'auto' }} ref={scrollRef}>
        {extractionComplete && !processingActive && !loading && products.length === 0 ? (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100%', minHeight: 360, padding: 48, textAlign: 'center', gap: 16 }}>
            <div style={{ width: 64, height: 64, borderRadius: 16, background: 'var(--color-red-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <FileWarning size={32} style={{ color: 'var(--color-red-text)' }} />
            </div>
            <div>
              <h2 style={{ fontSize: 18, fontWeight: 700, color: 'var(--color-text)', margin: '0 0 8px' }}>
                We couldn't extract products from this catalog
              </h2>
              <p style={{ fontSize: 14, color: 'var(--color-text-2)', maxWidth: 400, margin: '0 auto 24px', lineHeight: 1.6 }}>
                {catalog?.status_message
                  ? catalog.status_message
                  : 'This can happen with scanned catalogs, image-only PDFs, or unusual layouts. Try re-uploading or contact support.'}
              </p>
              <div style={{ display: 'flex', gap: 12, justifyContent: 'center', flexWrap: 'wrap' }}>
                <button
                  onClick={handleRetry}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'var(--color-primary)', color: '#fff', border: 'none', borderRadius: 8, padding: '10px 20px', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
                >
                  <RefreshCw size={15} />Retry
                </button>
                <button
                  onClick={() => navigate('/upload')}
                  style={{ display: 'inline-flex', alignItems: 'center', background: 'var(--color-surface)', color: 'var(--color-text-2)', border: '1px solid var(--color-border)', borderRadius: 8, padding: '10px 20px', fontSize: 14, fontWeight: 500, cursor: 'pointer' }}
                >
                  Upload Another Catalog
                </button>
              </div>
            </div>
          </div>
        ) : (
        <div style={{ padding: '0 20px', background: 'var(--color-surface)' }}>
          <table ref={tableRef} onKeyDown={handleKeyDown} className={processingActive ? 'table-locked' : ''} style={{ borderCollapse: 'separate', borderSpacing: 0, background: 'var(--color-surface)', minWidth: 800, width: '100%' }}>
            <thead>
              <tr>
                <TH resizable style={{ width: 300, cursor: 'pointer', paddingLeft: 12 }} sticky stickyLeft={0} isTitle onClick={() => cycleColumnSort('title')} aria-sort={columnSort.column === 'title' ? (columnSort.direction === 'asc' ? 'ascending' : 'descending') : 'none'}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <div style={{ width: 14, flexShrink: 0 }} />
                    <input
                      ref={el => {
                        if (el) {
                          const allChecked = filteredProducts.length > 0 && filteredProducts.every(p => selectedIds.has(p.id));
                          const someChecked = !allChecked && selectedIds.size > 0;
                          el.indeterminate = someChecked;
                        }
                      }}
                      type="checkbox"
                      style={{ cursor: 'pointer', flexShrink: 0, width: 15, height: 15 }}
                      checked={filteredProducts.length > 0 && filteredProducts.every(p => selectedIds.has(p.id))}
                      onChange={e => {
                        e.stopPropagation();
                        if (e.target.checked) {
                          setSelectedIds(new Set(filteredProducts.map(p => p.id)));
                          setSelectedVariantIds(new Set(filteredProducts.flatMap(p => (p.variants || []).map(v => v.id))));
                        } else {
                          setSelectedIds(new Set());
                          setSelectedVariantIds(new Set());
                          lastSelectedRef.current = null;
                        }
                      }}
                      onClick={e => e.stopPropagation()}
                    />
                    <span className="sortable-th" style={{ display: 'inline-flex', alignItems: 'center', gap: 2, paddingLeft: 6 }}>
                      Product
                      <span className={`sort-icon${columnSort.column === 'title' ? ' sort-active' : ''}`}>
                        {columnSort.column === 'title' && columnSort.direction === 'asc'
                          ? <ArrowUp size={12} />
                          : <ArrowDown size={12} />}
                      </span>
                    </span>
                  </div>
                </TH>
                {/* Column order locked 2026-04-23 — Images → SKU → Price → Compare At → Category → Type → Description → [opt-in] → Review */}
                {/* Brief 1 (2026-04-23): table-layout: fixed, all columns explicit width, Description = flex (no width, absorbs remainder) */}
                {colVis.image     && <TH resizable style={{ width: 90  }}>Images</TH>}
                <TH resizable style={{ width: 100 }} sortColumn="sku" columnSort={columnSort} cycleColumnSort={cycleColumnSort}>SKU</TH>
                <TH resizable style={{ width: 90 }} sortColumn="price" columnSort={columnSort} cycleColumnSort={cycleColumnSort}>Price</TH>
                {colVis.compareAt  && <TH resizable style={{ width: 90 }} sortColumn="compareAt" columnSort={columnSort} cycleColumnSort={cycleColumnSort}>Compare At</TH>}
                {colVis.productCategory && <TH resizable style={{ width: 180 }}>Product category</TH>}
                {colVis.type      && <TH resizable style={{ width: 90  }} sortColumn="type" columnSort={columnSort} cycleColumnSort={cycleColumnSort}>Type</TH>}
                {colVis.body      && <TH resizable style={{ width: 300 }}>Description</TH>}
                {colVis.status    && <TH resizable style={{ width: 90  }} sortColumn="status" columnSort={columnSort} cycleColumnSort={cycleColumnSort}>Status</TH>}
                {colVis.vendor    && <TH resizable style={{ width: 90  }} sortColumn="vendor" columnSort={columnSort} cycleColumnSort={cycleColumnSort}>Vendor</TH>}
                {colVis.tags      && <TH resizable style={{ width: 140 }}>Tags</TH>}
                {colVis.collection && <TH resizable style={{ width: 120 }}>Collection</TH>}
                {colVis.option1   && <TH resizable style={{ width: 100 }}>{opt1Label}</TH>}
                {colVis.option2   && <TH resizable style={{ width: 100 }}>{opt2Label}</TH>}
                {colVis.option3   && <TH resizable style={{ width: 100 }}>{opt3Label}</TH>}
                {colVis.barcode           && <TH resizable style={{ width: 120 }}>Barcode</TH>}
                {colVis.inventory         && <TH resizable style={{ width: 80  }} sortColumn="inventory" columnSort={columnSort} cycleColumnSort={cycleColumnSort}>Inventory</TH>}
                {colVis.continueSellingOos && <TH resizable style={{ width: 110 }}>Continue selling</TH>}
                {colVis.weight            && <TH resizable style={{ width: 80  }} sortColumn="weight" columnSort={columnSort} cycleColumnSort={cycleColumnSort}>Weight (g)</TH>}
                {colVis.seoTitle  && <TH resizable style={{ width: 160 }}>SEO title</TH>}
                {colVis.seoDesc   && <TH resizable style={{ width: 200 }}>SEO description</TH>}
                <TH resizable style={{ width: 90 }}>Review</TH>
                {customColumns.map(col => (
                  <TH key={col.id} style={{ width: 120, cursor: 'pointer', whiteSpace: 'nowrap' }} onClick={() => { setEditingCustomCol(col); setShowCustomColModal(true); }}>
                    {col.column_label}
                    <Pencil size={10} style={{ marginLeft: 4, color: 'var(--color-text-3)' }} />
                  </TH>
                ))}
              </tr>
            </thead>
            <tbody>
              {(loading || processingActive) && products.length === 0 ? (
                Array.from({ length: Math.min(20, Math.max(7, Math.floor((window.innerHeight - 200) / 40))) }).map((_, i) => (
                  <tr key={`skel-${i}`} className="skeleton-row">
                    {(['70%', 50, '85%', 40, '80%', '60%', '60%', 50, 40, 40, 30] as const).map((w, ci) => (
                      <td key={ci}>
                        <div style={{
                          height: 12, borderRadius: 4,
                          background: 'linear-gradient(90deg, var(--color-surface-2) 25%, var(--color-border) 50%, var(--color-surface-2) 75%)',
                          backgroundSize: '800px 100%',
                          animation: 'shimmer 2.2s infinite linear',
                          width: w,
                        }} />
                      </td>
                    ))}
                  </tr>
                ))
              ) : null}
              {filteredProducts.length === 0 && products.length > 0 && (
                <tr>
                  <td colSpan={99} style={{ textAlign: 'center', padding: '48px 24px' }}>
                    {activeFilters.status.length === 1 && activeFilters.status[0] === 'needs_review' && activeFilters.type.length === 0 && activeFilters.tags.length === 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
                        <div style={{ width: 48, height: 48, borderRadius: 12, background: 'var(--color-green-bg)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                          <CheckCircle2 size={24} style={{ color: 'var(--color-green-text)' }} />
                        </div>
                        <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--color-text)' }}>
                          All products are ready to export
                        </div>
                        <button
                          onClick={() => { setFilter('all'); setActiveFilters({ status: [], type: [], tags: [], price: [], image: [], category: [] }); setSearchQuery(''); }}
                          style={{ fontSize: 13, color: 'var(--color-primary)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
                        >
                          Show all products
                        </button>
                      </div>
                    ) : (
                      <>
                        <div style={{ color: 'var(--color-text-2)', fontSize: 14, marginBottom: 12 }}>
                          No products match the current filters.
                        </div>
                        <button
                          onClick={() => { setFilter('all'); setActiveFilters({ status: [], type: [], tags: [], price: [], image: [], category: [] }); setSearchQuery(''); }}
                          style={{ fontSize: 13, color: 'var(--color-primary)', background: 'none', border: 'none', cursor: 'pointer', textDecoration: 'underline' }}
                        >
                          Clear filters
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              )}
              {/* ── Virtualized rows — padding approach ────────── */}
              {(() => {
                const virtualItems = rowVirtualizer.getVirtualItems();
                if (virtualItems.length === 0) return null;
                const paddingTop = virtualItems[0].start;
                const paddingBottom = rowVirtualizer.getTotalSize() - virtualItems[virtualItems.length - 1].end;
                return (
                  <>
                  {paddingTop > 0 && (
                    <tr style={{ height: paddingTop, padding: 0, border: 'none' }}>
                      <td colSpan={99} style={{ padding: 0, border: 'none' }} />
                    </tr>
                  )}
                  <DndContext
                    sensors={sensors}
                    collisionDetection={closestCenter}
                    autoScroll={{ threshold: { x: 0, y: 0.15 }, acceleration: 10 }}
                    onDragStart={({ active }) => setActiveDragId(active.id)}
                    onDragOver={({ active, over }) => {
                      if (!over || over.id === active.id) { setDragOverId(null); return; }
                      const productIdSet = new Set(sortedProducts.map(p => p.id));
                      const activeIsProduct = productIdSet.has(active.id as string);
                      if (activeIsProduct) {
                        // Product dragged — resolve variant targets to their parent product
                        let targetProductId = over.id as string;
                        if (!productIdSet.has(targetProductId)) {
                          const ownerP = products.find(pr => (pr.variants || []).some(v => v.id === targetProductId));
                          targetProductId = ownerP?.id;
                        }
                        if (targetProductId && targetProductId !== active.id) {
                          setDragOverId(targetProductId);
                          const activeIdx = sortedProducts.findIndex(p => p.id === active.id);
                          const overIdx = sortedProducts.findIndex(p => p.id === targetProductId);
                          setDragOverEdge(activeIdx < overIdx ? 'below' : 'above');
                        }
                        else setDragOverId(null);
                      } else {
                        // Variant can only drop on sibling variants
                        const owner = products.find(pr => (pr.variants || []).some(v => v.id === active.id));
                        const isSibling = owner?.variants?.some(v => v.id === over.id);
                        if (isSibling) {
                          setDragOverId(over.id);
                          const variants = owner.variants || [];
                          const activeIdx = variants.findIndex(v => v.id === active.id);
                          const overIdx = variants.findIndex(v => v.id === over.id);
                          setDragOverEdge(activeIdx < overIdx ? 'below' : 'above');
                        }
                        else setDragOverId(null);
                      }
                    }}
                    onDragEnd={({ active, over }) => {
                      setDragOverId(null);
                      setActiveDragId(null);
                      if (!over || active.id === over.id) return;
                      const productIdSet = new Set(sortedProducts.map(p => p.id));
                      if (productIdSet.has(active.id as string)) {
                        // Product dragged — resolve variant targets to their parent product
                        let targetProductId = over.id as string;
                        if (!productIdSet.has(targetProductId)) {
                          const ownerP = products.find(pr => (pr.variants || []).some(v => v.id === targetProductId));
                          targetProductId = ownerP?.id;
                        }
                        if (targetProductId && targetProductId !== active.id as string) {
                          // Multi-drag: active is selected AND more than 1 selected
                          if (selectedIds.has(active.id as string) && selectedIds.size > 1) {
                            handleMultiProductReorder(active.id as string, targetProductId);
                          } else {
                            handleProductReorder(active.id as string, targetProductId);
                          }
                        }
                      } else {
                        const ownerProduct = products.find(p => (p.variants || []).some(v => v.id === active.id));
                        const isSibling = ownerProduct?.variants?.some(v => v.id === over.id);
                        if (ownerProduct && isSibling) {
                          if (selectedVariantIds.has(active.id as string) && draggedVariantIds.size > 1) {
                            handleMultiVariantReorder(ownerProduct.id, active.id as string, over.id as string);
                          } else {
                            handleVariantReorder(ownerProduct.id, active.id as string, over.id as string);
                          }
                        }
                      }
                    }}
                    onDragCancel={() => { setDragOverId(null); setDragOverEdge('below'); setActiveDragId(null); }}
                  >
                  <SortableContext items={virtualRows.flatMap(r => r.type === 'merged' ? [r.product.id] : r.type === 'product' ? [r.product.id] : r.type === 'variant' ? [r.variant.id] : [])} strategy={verticalListSortingStrategy}>
              {virtualItems.map(virtualItem => {
                const row = virtualRows[virtualItem.index];
                const { product, variants } = row;
                const v0 = variants[0];
                const allFlags  = variants.flatMap(v => (v.flags || []).filter(f => !f.resolved && !RETIRED_FLAG_TYPES.has(f.flag_type)));
                const errFlags  = allFlags.filter(f => f.severity === 'error');

                // Shared helpers for this product group
                const toggleSelect = (e) => {
                  // Shift+click: range-select products from last anchor to current
                  if (e?.shiftKey && lastSelectedRef.current?.type === 'product') {
                    const fromIdx = sortedProducts.findIndex(p => p.id === lastSelectedRef.current.id);
                    const toIdx = sortedProducts.findIndex(p => p.id === product.id);
                    if (fromIdx !== -1 && toIdx !== -1) {
                      const start = Math.min(fromIdx, toIdx);
                      const end = Math.max(fromIdx, toIdx);
                      const range = sortedProducts.slice(start, end + 1);
                      setSelectedIds(prev => {
                        const s = new Set(prev);
                        range.forEach(p => s.add(p.id));
                        return s;
                      });
                      setSelectedVariantIds(prev => {
                        const s = new Set(prev);
                        range.forEach(p => (p.variants || []).forEach(v => s.add(v.id)));
                        return s;
                      });
                      // Don't update anchor on shift-click so user can shift-click again to adjust range
                      return;
                    }
                  }
                  // Normal toggle
                  const allVariantIds = variants.map(v => v.id);
                  setSelectedIds(prev => {
                    const s = new Set(prev);
                    if (s.has(product.id)) {
                      s.delete(product.id);
                      setSelectedVariantIds(prev2 => { const s2 = new Set(prev2); allVariantIds.forEach(id => s2.delete(id)); return s2; });
                    } else {
                      s.add(product.id);
                      setSelectedVariantIds(prev2 => { const s2 = new Set(prev2); allVariantIds.forEach(id => s2.add(id)); return s2; });
                    }
                    return s;
                  });
                  lastSelectedRef.current = { type: 'product', id: product.id };
                };
                const toggleVariantSelect = (variantId, e) => {
                  // Shift+click: range-select variants within same product
                  if (e?.shiftKey && lastSelectedRef.current?.type === 'variant' && lastSelectedRef.current.productId === product.id) {
                    const fromIdx = variants.findIndex(v => v.id === lastSelectedRef.current.id);
                    const toIdx = variants.findIndex(v => v.id === variantId);
                    if (fromIdx !== -1 && toIdx !== -1) {
                      const start = Math.min(fromIdx, toIdx);
                      const end = Math.max(fromIdx, toIdx);
                      const range = variants.slice(start, end + 1);
                      setSelectedVariantIds(prev => {
                        const s = new Set(prev);
                        range.forEach(v => s.add(v.id));
                        const allVariantIds = variants.map(v => v.id);
                        const allNowSelected = allVariantIds.every(id => s.has(id));
                        setSelectedIds(prev2 => {
                          const s2 = new Set(prev2);
                          if (allNowSelected) s2.add(product.id);
                          return s2;
                        });
                        return s;
                      });
                      return;
                    }
                  }
                  // Normal toggle
                  setSelectedVariantIds(prev => {
                    const s = new Set(prev);
                    if (s.has(variantId)) s.delete(variantId);
                    else s.add(variantId);
                    const allVariantIds = variants.map(v => v.id);
                    const allSelected = allVariantIds.every(id => s.has(id));
                    setSelectedIds(prev2 => {
                      const s2 = new Set(prev2);
                      if (allSelected) s2.add(product.id);
                      else s2.delete(product.id);
                      return s2;
                    });
                    return s;
                  });
                  lastSelectedRef.current = { type: 'variant', id: variantId, productId: product.id };
                };

                const prefersReduced  = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
                const wasExtracted    = extractedIdsRef.current.has(product.id);
                const alreadyAnimated = animatedIdsRef.current.has(product.id);
                const isNew           = wasExtracted && !alreadyAnimated && processingActive;
                if (isNew) animatedIdsRef.current.add(product.id);
                const staggerDelay    = isNew ? (newRowStaggerRef.current.get(product.id) ?? 0) : 0;

                const isDrawerOpen    = sourcePanelTarget?.productId === product.id;
                const onBadgeClick    = () => {
                  if (isDrawerOpen) { setSourcePanelTarget(null); setDrawerOpen(null); }
                  else {
                    if (!phFirstSource.current) {
                      phFirstSource.current = true;
                      capture('source_panel_open');
                    }
                    setSourcePanelTarget({ productId: product.id, variantId: v0?.id }); setDrawerOpen(product.id);
                  }
                };

                // ── Merged row ───────────────────────────────────
                if (row.type === 'merged') {
                  const vFlags = (v0.flags || []).filter(f => !f.resolved && !RETIRED_FLAG_TYPES.has(f.flag_type));
                  const showDraftProductAfterMerged = addingProduct?.afterProductId === product.id;
                  return (
                    <React.Fragment key={`mf-${product.id}`}>
                    <MergedRow
                      key={`m-${product.id}`}
                      product={product} variant={v0}
                      colVis={colVis} vFlags={vFlags}
                      categorySuggestions={categorySuggestions}
                      catalogTags={filterOptions.tags}
                      hasLowConf={(f) => hasLowConf(v0, f)}
                      onSaveProduct={(f, v) => saveProduct(product.id, f, v)}
                      onSaveVariant={(f, v) => saveVariant(v0.id, product.id, f, v)}
                      onDelete={() => handleDeleteVariant(v0.id, product.id)}
                      onFlagClick={onBadgeClick}
                      flagPanelOpen={isDrawerOpen}
                      searchQuery={searchQuery}
                      isSelected={selectedIds.has(product.id)}
                      onToggleSelect={toggleSelect}
                      customColumns={customColumns}
                      customValues={customValues}
                      onCustomValueChange={handleCustomValueChange}
                      isNew={isNew && !prefersReduced}
                      staggerDelay={staggerDelay}
                      dndDisabled={dndDisabled}
                      dragOverId={dragOverId} dragOverEdge={dragOverEdge}
                      isPartOfMultiDrag={draggedProductIds.size > 1 && draggedProductIds.has(product.id) && product.id !== activeDragId}
                      productImages={productImagesMap[product.id] || []}
                      onImagesChange={updated => setProductImagesMap(prev => ({...prev, [product.id]: updated}))}
                      variantImages={variantImagesMap[v0.id] || []}
                      onVariantImagesChange={updated => setVariantImagesMap(prev => ({...prev, [v0.id]: updated}))}
                      onToast={msg => setToastMsg(msg)}
                      onContextMenu={e => handleRowContextMenu(e, product.id, v0.id, 'product')}
                    />
                    {showDraftProductAfterMerged && (
                      <tr key={`dpd-m-${product.id}`}>
                        <td colSpan={99} style={{ padding: '4px 12px', borderBottom: '1px solid var(--color-border)' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <input
                              autoFocus
                              type="text"
                              value={addingProduct.title}
                              onChange={e => setAddingProduct(prev => prev ? { ...prev, title: e.target.value } : prev)}
                              onKeyDown={e => { if (e.key === 'Enter') handleSaveDraftProduct(addingProduct.title); if (e.key === 'Escape') setAddingProduct(null); }}
                              placeholder="Product title…"
                              style={{ flex: 1, maxWidth: 300, padding: '5px 8px', fontSize: 13, border: '1.5px solid var(--color-primary)', borderRadius: 6, background: 'var(--color-surface)', color: 'var(--color-text)', outline: 'none' }}
                            />
                            <button onClick={() => handleSaveDraftProduct(addingProduct.title)} disabled={!addingProduct.title.trim()} style={{ padding: '5px 12px', fontSize: 12, fontWeight: 500, background: 'var(--color-primary)', color: '#fff', border: 'none', borderRadius: 6, cursor: addingProduct.title.trim() ? 'pointer' : 'not-allowed', opacity: addingProduct.title.trim() ? 1 : 0.5 }}>Add</button>
                            <button onClick={() => setAddingProduct(null)} style={{ padding: '5px 10px', fontSize: 12, background: 'none', border: '1px solid var(--color-border)', borderRadius: 6, cursor: 'pointer', color: 'var(--color-text-2)' }}>Cancel</button>
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  );
                }

                // ── Product header row ───────────────────────────
                if (row.type === 'product') {
                  const isCollapsed = !row.isExpanded;
                  const onMultiBadgeClick = () => {
                    if (!isDrawerOpen && isCollapsed) toggleCollapse(product.id);
                    if (isDrawerOpen) { setSourcePanelTarget(null); setDrawerOpen(null); }
                    else { setSourcePanelTarget({ productId: product.id, variantId: variants[0]?.id }); setDrawerOpen(product.id); }
                  };
                  const showDraftProductAfterCollapsed = isCollapsed && addingProduct?.afterProductId === product.id;
                  return (
                    <React.Fragment key={`pf-${product.id}`}>
                    <ProductRow
                      key={`p-${product.id}`}
                      product={product} variants={variants} isCollapsed={isCollapsed}
                      flagCount={allFlags.length} errCount={errFlags.length}
                      allFlags={allFlags} drawerIsOpen={isDrawerOpen}
                      colVis={colVis}
                      categorySuggestions={categorySuggestions}
                      catalogTags={filterOptions.tags}
                      optionLabels={[opt1HasData ? opt1Label : null, opt2HasData ? opt2Label : null, opt3HasData ? opt3Label : null]}
                      onToggle={() => toggleCollapse(product.id)}
                      onSave={(f, v) => saveProduct(product.id, f, v)}
                      onFlagClick={onMultiBadgeClick}
                      searchQuery={searchQuery}
                      isSelected={selectedIds.has(product.id)}
                      onToggleSelect={toggleSelect}
                      selectedVariantIds={selectedVariantIds}
                      customColumns={customColumns}
                      isNew={isNew && !prefersReduced}
                      staggerDelay={staggerDelay}
                      dndDisabled={dndDisabled}
                      dragOverId={dragOverId} dragOverEdge={dragOverEdge}
                      isPartOfMultiDrag={draggedProductIds.size > 1 && draggedProductIds.has(product.id) && product.id !== activeDragId}
                      productImages={productImagesMap[product.id] || []}
                      onImagesChange={updated => setProductImagesMap(prev => ({...prev, [product.id]: updated}))}
                      onToast={msg => setToastMsg(msg)}
                      onContextMenu={e => handleRowContextMenu(e, product.id, null, 'product')}
                    />
                    {showDraftProductAfterCollapsed && (
                      <tr key={`dpd-c-${product.id}`}>
                        <td colSpan={99} style={{ padding: '4px 12px', borderBottom: '1px solid var(--color-border)' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <input
                              autoFocus
                              type="text"
                              value={addingProduct.title}
                              onChange={e => setAddingProduct(prev => prev ? { ...prev, title: e.target.value } : prev)}
                              onKeyDown={e => { if (e.key === 'Enter') handleSaveDraftProduct(addingProduct.title); if (e.key === 'Escape') setAddingProduct(null); }}
                              placeholder="Product title…"
                              style={{ flex: 1, maxWidth: 300, padding: '5px 8px', fontSize: 13, border: '1.5px solid var(--color-primary)', borderRadius: 6, background: 'var(--color-surface)', color: 'var(--color-text)', outline: 'none' }}
                            />
                            <button onClick={() => handleSaveDraftProduct(addingProduct.title)} disabled={!addingProduct.title.trim()} style={{ padding: '5px 12px', fontSize: 12, fontWeight: 500, background: 'var(--color-primary)', color: '#fff', border: 'none', borderRadius: 6, cursor: addingProduct.title.trim() ? 'pointer' : 'not-allowed', opacity: addingProduct.title.trim() ? 1 : 0.5 }}>Add</button>
                            <button onClick={() => setAddingProduct(null)} style={{ padding: '5px 10px', fontSize: 12, background: 'none', border: '1px solid var(--color-border)', borderRadius: 6, cursor: 'pointer', color: 'var(--color-text-2)' }}>Cancel</button>
                          </div>
                        </td>
                      </tr>
                    )}
                    </React.Fragment>
                  );
                }

                // ── Variant row ──────────────────────────────────
                if (row.type === 'variant') {
                  const variant = row.variant;
                  const vIdx = row.vIdx;
                  const vFlags = (variant.flags || []).filter(f => !f.resolved && !RETIRED_FLAG_TYPES.has(f.flag_type));
                  const showDraftAfter = addingVariant?.productId === product.id && addingVariant?.afterVariantId === variant.id;
                  return (
                    <React.Fragment key={`vf-${variant.id}`}>
                    <VariantRow
                      key={`v-${variant.id}`}
                      variant={variant} product={product}
                      vIdx={vIdx} isFirst={vIdx === 0}
                      colVis={colVis} vFlags={vFlags}
                      categorySuggestions={categorySuggestions}
                      hasLowConf={(f) => hasLowConf(variant, f)}
                      onSaveProduct={(f, v) => saveProduct(product.id, f, v)}
                      onSave={(f, v) => saveVariant(variant.id, product.id, f, v)}
                      onDelete={() => handleDeleteVariant(variant.id, product.id)}
                      onFlagClick={onBadgeClick}
                      flagPanelOpen={false}
                      searchQuery={searchQuery}
                      isSelected={selectedIds.has(product.id)}
                      isVariantSelected={selectedVariantIds.has(variant.id)}
                      onToggleVariantSelect={(e) => toggleVariantSelect(variant.id, e)}
                      onToggleSelect={toggleSelect}
                      customColumns={customColumns}
                      customValues={customValues}
                      onCustomValueChange={handleCustomValueChange}
                      drawerHighlight={isDrawerOpen}
                      dndDisabled={dndDisabled}
                      dragOverId={dragOverId} dragOverEdge={dragOverEdge}
                      isPartOfMultiDrag={draggedVariantIds.size > 1 && draggedVariantIds.has(variant.id) && variant.id !== activeDragId}
                      productImages={productImagesMap[product.id] || []}
                      onImagesChange={updated => setProductImagesMap(prev => ({...prev, [product.id]: updated}))}
                      variantImages={variantImagesMap[variant.id] || []}
                      onVariantImagesChange={updated => setVariantImagesMap(prev => ({...prev, [variant.id]: updated}))}
                      onToast={msg => setToastMsg(msg)}
                      onContextMenu={e => handleRowContextMenu(e, product.id, variant.id, 'variant')}
                    />
                    {showDraftAfter && (() => {
                      const pHasOpt2 = product.variants?.some(v => v.option2_value);
                      const pHasOpt3 = product.variants?.some(v => v.option3_value);
                      const pOpt1Name = product.variants?.[0]?.option1_name || opt1Label;
                      const pOpt2Name = product.variants?.[0]?.option2_name || opt2Label;
                      const pOpt3Name = product.variants?.[0]?.option3_name || opt3Label;
                      const canSave = addingVariant.opt1.trim();
                      return (
                      <tr key={`avd-after-${variant.id}`}>
                        <td colSpan={99} style={{ padding: '4px 12px 4px 36px', borderBottom: '1px solid var(--color-border)' }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <input
                              autoFocus
                              type="text"
                              value={addingVariant.opt1}
                              onChange={e => setAddingVariant(prev => prev ? { ...prev, opt1: e.target.value } : prev)}
                              onKeyDown={e => { if (e.key === 'Enter') handleSaveDraftVariant(); if (e.key === 'Escape') setAddingVariant(null); }}
                              placeholder={`${pOpt1Name}…`}
                              style={{ flex: 1, maxWidth: pHasOpt2 ? 150 : 240, padding: '5px 8px', fontSize: 13, border: '1.5px solid var(--color-primary)', borderRadius: 6, background: 'var(--color-surface)', color: 'var(--color-text)', outline: 'none' }}
                            />
                            {pHasOpt2 && (
                              <input
                                type="text"
                                value={addingVariant.opt2}
                                onChange={e => setAddingVariant(prev => prev ? { ...prev, opt2: e.target.value } : prev)}
                                onKeyDown={e => { if (e.key === 'Enter') handleSaveDraftVariant(); if (e.key === 'Escape') setAddingVariant(null); }}
                                placeholder={`${pOpt2Name}…`}
                                style={{ flex: 1, maxWidth: 150, padding: '5px 8px', fontSize: 13, border: '1.5px solid var(--color-primary)', borderRadius: 6, background: 'var(--color-surface)', color: 'var(--color-text)', outline: 'none' }}
                              />
                            )}
                            {pHasOpt3 && (
                              <input
                                type="text"
                                value={addingVariant.opt3}
                                onChange={e => setAddingVariant(prev => prev ? { ...prev, opt3: e.target.value } : prev)}
                                onKeyDown={e => { if (e.key === 'Enter') handleSaveDraftVariant(); if (e.key === 'Escape') setAddingVariant(null); }}
                                placeholder={`${pOpt3Name}…`}
                                style={{ flex: 1, maxWidth: 150, padding: '5px 8px', fontSize: 13, border: '1.5px solid var(--color-primary)', borderRadius: 6, background: 'var(--color-surface)', color: 'var(--color-text)', outline: 'none' }}
                              />
                            )}
                            <button onClick={handleSaveDraftVariant} disabled={!canSave} style={{ padding: '5px 12px', fontSize: 12, fontWeight: 500, background: 'var(--color-primary)', color: '#fff', border: 'none', borderRadius: 6, cursor: canSave ? 'pointer' : 'not-allowed', opacity: canSave ? 1 : 0.5 }}>Add</button>
                            <button onClick={() => setAddingVariant(null)} style={{ padding: '5px 10px', fontSize: 12, background: 'none', border: '1px solid var(--color-border)', borderRadius: 6, cursor: 'pointer', color: 'var(--color-text-2)' }}>Cancel</button>
                          </div>
                        </td>
                      </tr>
                      );
                    })()}
                    </React.Fragment>
                  );
                }

                // ── Add-variant row (end of variant list) ──
                if (row.type === 'add-variant') {
                  const showDraftVariant = addingVariant?.productId === product.id && !addingVariant?.afterVariantId;
                  const showDraftProduct = addingProduct?.afterProductId === product.id;
                  return (
                    <React.Fragment key={`av-${product.id}`}>
                      {showDraftVariant ? (() => {
                        const pHasOpt2 = product.variants?.some(v => v.option2_value);
                        const pHasOpt3 = product.variants?.some(v => v.option3_value);
                        const pOpt1Name = product.variants?.[0]?.option1_name || opt1Label;
                        const pOpt2Name = product.variants?.[0]?.option2_name || opt2Label;
                        const pOpt3Name = product.variants?.[0]?.option3_name || opt3Label;
                        const canSave = addingVariant.opt1.trim();
                        return (
                        <tr key={`avd-${product.id}`}>
                          <td colSpan={99} style={{ padding: '4px 12px 4px 36px', borderBottom: '1px solid var(--color-border)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <input
                                autoFocus
                                type="text"
                                value={addingVariant.opt1}
                                onChange={e => setAddingVariant(prev => prev ? { ...prev, opt1: e.target.value } : prev)}
                                onKeyDown={e => { if (e.key === 'Enter') handleSaveDraftVariant(); if (e.key === 'Escape') setAddingVariant(null); }}
                                placeholder={`${pOpt1Name}…`}
                                style={{ flex: 1, maxWidth: pHasOpt2 ? 150 : 240, padding: '5px 8px', fontSize: 13, border: '1.5px solid var(--color-primary)', borderRadius: 6, background: 'var(--color-surface)', color: 'var(--color-text)', outline: 'none' }}
                              />
                              {pHasOpt2 && (
                                <input
                                  type="text"
                                  value={addingVariant.opt2}
                                  onChange={e => setAddingVariant(prev => prev ? { ...prev, opt2: e.target.value } : prev)}
                                  onKeyDown={e => { if (e.key === 'Enter') handleSaveDraftVariant(); if (e.key === 'Escape') setAddingVariant(null); }}
                                  placeholder={`${pOpt2Name}…`}
                                  style={{ flex: 1, maxWidth: 150, padding: '5px 8px', fontSize: 13, border: '1.5px solid var(--color-primary)', borderRadius: 6, background: 'var(--color-surface)', color: 'var(--color-text)', outline: 'none' }}
                                />
                              )}
                              {pHasOpt3 && (
                                <input
                                  type="text"
                                  value={addingVariant.opt3}
                                  onChange={e => setAddingVariant(prev => prev ? { ...prev, opt3: e.target.value } : prev)}
                                  onKeyDown={e => { if (e.key === 'Enter') handleSaveDraftVariant(); if (e.key === 'Escape') setAddingVariant(null); }}
                                  placeholder={`${pOpt3Name}…`}
                                  style={{ flex: 1, maxWidth: 150, padding: '5px 8px', fontSize: 13, border: '1.5px solid var(--color-primary)', borderRadius: 6, background: 'var(--color-surface)', color: 'var(--color-text)', outline: 'none' }}
                                />
                              )}
                              <button onClick={handleSaveDraftVariant} disabled={!canSave} style={{ padding: '5px 12px', fontSize: 12, fontWeight: 500, background: 'var(--color-primary)', color: '#fff', border: 'none', borderRadius: 6, cursor: canSave ? 'pointer' : 'not-allowed', opacity: canSave ? 1 : 0.5 }}>Add</button>
                              <button onClick={() => setAddingVariant(null)} style={{ padding: '5px 10px', fontSize: 12, background: 'none', border: '1px solid var(--color-border)', borderRadius: 6, cursor: 'pointer', color: 'var(--color-text-2)' }}>Cancel</button>
                            </div>
                          </td>
                        </tr>
                        );
                      })() : (
                        <tr key={`avl-${product.id}`}>
                          <td colSpan={99} style={{ padding: '4px 12px 4px 36px', borderBottom: '1px solid var(--color-border)' }}>
                            <button
                              onClick={() => handleAddVariant(product)}
                              style={{ padding: '6px 10px', border: 'none', background: 'none', cursor: 'pointer', color: 'var(--color-text-3)', fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, transition: 'color 0.12s' }}
                              onMouseEnter={e => { e.currentTarget.style.color = 'var(--color-primary)'; }}
                              onMouseLeave={e => { e.currentTarget.style.color = 'var(--color-text-3)'; }}
                            >
                              <Plus size={14} /> Add variant
                            </button>
                          </td>
                        </tr>
                      )}
                      {showDraftProduct && (
                        <tr key={`dpd-${product.id}`}>
                          <td colSpan={99} style={{ padding: '4px 12px', borderBottom: '1px solid var(--color-border)' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                              <input
                                autoFocus
                                type="text"
                                value={addingProduct.title}
                                onChange={e => setAddingProduct(prev => prev ? { ...prev, title: e.target.value } : prev)}
                                onKeyDown={e => { if (e.key === 'Enter') handleSaveDraftProduct(addingProduct.title); if (e.key === 'Escape') setAddingProduct(null); }}
                                placeholder="Product title…"
                                style={{ flex: 1, maxWidth: 300, padding: '5px 8px', fontSize: 13, border: '1.5px solid var(--color-primary)', borderRadius: 6, background: 'var(--color-surface)', color: 'var(--color-text)', outline: 'none' }}
                              />
                              <button onClick={() => handleSaveDraftProduct(addingProduct.title)} disabled={!addingProduct.title.trim()} style={{ padding: '5px 12px', fontSize: 12, fontWeight: 500, background: 'var(--color-primary)', color: '#fff', border: 'none', borderRadius: 6, cursor: addingProduct.title.trim() ? 'pointer' : 'not-allowed', opacity: addingProduct.title.trim() ? 1 : 0.5 }}>Add</button>
                              <button onClick={() => setAddingProduct(null)} style={{ padding: '5px 10px', fontSize: 12, background: 'none', border: '1px solid var(--color-border)', borderRadius: 6, cursor: 'pointer', color: 'var(--color-text-2)' }}>Cancel</button>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                }

                return null;
              })}
                  </SortableContext>
                  {activeDragId && (draggedProductIds.size > 1 || draggedVariantIds.size > 1) && (
                    <DragOverlay dropAnimation={null}>
                      <div style={{
                        padding: '8px 14px',
                        background: 'var(--color-surface)',
                        border: '1px solid var(--color-border-strong)',
                        borderRadius: 6,
                        boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                        fontSize: 13,
                        fontWeight: 600,
                        color: 'var(--color-text)',
                        whiteSpace: 'nowrap',
                      }}>
                        {draggedProductIds.size > 1
                          ? `Moving ${draggedProductIds.size} products`
                          : `Moving ${draggedVariantIds.size} variants`}
                      </div>
                    </DragOverlay>
                  )}
                  </DndContext>
                  {paddingBottom > 0 && (
                    <tr style={{ height: paddingBottom, padding: 0, border: 'none' }}>
                      <td colSpan={99} style={{ padding: 0, border: 'none' }} />
                    </tr>
                  )}
                  </>
                );
              })()}
            </tbody>
          </table>
        </div>
        )}
      </div>
      {/* Add product row — pinned to bottom of the table area */}
      {!(selectedIds.size > 0 || selectedVariantIds.size > 0) && (
      <div style={{
        flexShrink: 0,
        background: 'var(--color-surface)',
        borderTop: '1px solid var(--color-border)',
        padding: '4px 12px',
      }}>
        {addingProduct && !addingProduct.afterProductId ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              autoFocus
              type="text"
              value={addingProduct.title}
              onChange={e => setAddingProduct(prev => prev ? { ...prev, title: e.target.value } : prev)}
              onKeyDown={e => { if (e.key === 'Enter') handleSaveDraftProduct(addingProduct.title); if (e.key === 'Escape') setAddingProduct(null); }}
              placeholder="Product title…"
              style={{ flex: 1, maxWidth: 300, padding: '5px 8px', fontSize: 13, border: '1.5px solid var(--color-primary)', borderRadius: 6, background: 'var(--color-surface)', color: 'var(--color-text)', outline: 'none' }}
            />
            <button onClick={() => handleSaveDraftProduct(addingProduct.title)} disabled={!addingProduct.title.trim()} style={{ padding: '5px 12px', fontSize: 12, fontWeight: 500, background: 'var(--color-primary)', color: '#fff', border: 'none', borderRadius: 6, cursor: addingProduct.title.trim() ? 'pointer' : 'not-allowed', opacity: addingProduct.title.trim() ? 1 : 0.5 }}>Add</button>
            <button onClick={() => setAddingProduct(null)} style={{ padding: '5px 10px', fontSize: 12, background: 'none', border: '1px solid var(--color-border)', borderRadius: 6, cursor: 'pointer', color: 'var(--color-text-2)' }}>Cancel</button>
          </div>
        ) : (
          <button
            onClick={() => handleAddProduct()}
            style={{
              padding: '6px 10px',
              border: 'none', borderRadius: 6,
              background: 'none', cursor: 'pointer', color: 'var(--color-text-3)',
              fontSize: 13, display: 'flex', alignItems: 'center', gap: 6,
              transition: 'color 0.12s',
            }}
            onMouseEnter={e => { e.currentTarget.style.color = 'var(--color-primary)'; }}
            onMouseLeave={e => { e.currentTarget.style.color = 'var(--color-text-3)'; }}
          >
            <Plus size={14} />
            Add product
          </button>
        )}
      </div>
      )}
      </div>
      {/* Source Panel */}
      <AnimatePresence>
      {sourcePanelTarget && (() => {
        const sp = products.find(p => p.id === sourcePanelTarget.productId);
        if (!sp) return null;
        const spVariants = sp.variants || [];
        const spVariant = spVariants.find(v => v.id === sourcePanelTarget.variantId) || spVariants[0];
        const spFlags = spVariants.flatMap(v => (v.flags || []).filter(f => !f.resolved && !RETIRED_FLAG_TYPES.has(f.flag_type)));
        return (
          <motion.div
            key="source-panel"
            initial={{ width: 0, opacity: 0 }}
            animate={{ width: sourcePanelWidth, opacity: 1 }}
            exit={{ width: 0, opacity: 0 }}
            transition={{ duration: 0.15, ease: 'easeOut' }}
            style={{ overflow: 'hidden', flexShrink: 0, height: '100%' }}
          >
          <SourcePanel
            product={sp}
            variant={spVariant}
            allFlags={spFlags}
            width={sourcePanelWidth}
            onClose={() => { setSourcePanelTarget(null); setDrawerOpen(null); }}
            onResize={(w) => { setSourcePanelWidth(w); try { localStorage.setItem('cc_source_panel_w', String(w)); } catch {} }}
            onNavigateIssue={() => navigateToIssue(1)}
            issueRemaining={issueNavCounts.remaining}
            onNavigateFlag={(group) => {
              const flagType = group.flag_type;
              const affectedVariants = spVariants.filter(v =>
                (v.flags || []).some(f => f.flag_type === flagType && !f.resolved)
              );
              if (affectedVariants.length === 0) return;
              const firstVariant = affectedVariants[0];
              const colKey = flagType === 'low_confidence'
                ? ((firstVariant.flags || []).find(f => f.flag_type === 'low_confidence' && !f.resolved)?.field_name || 'price')
                : FLAG_COLUMNS[flagType];
              if (!colKey) return;
              const cell = document.querySelector(`[data-variant-id="${firstVariant.id}"] [data-col="${colKey}"]`);
              if (!cell) return;
              cell.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
              flashFocusRing(cell, group.severity === 'error');
              if (DRAWER_OPEN_EDIT.has(flagType)) {
                setTimeout(() => {
                  const input = cell.querySelector('input, textarea');
                  if (input) input.focus();
                }, 150);
              }
            }}
          />
          </motion.div>
        );
      })()}
      </AnimatePresence>
      </div>

      {/* Export modal */}
      {showExport && (
        <ExportModal
          catalogId={catalogId}
          stats={stats}
          products={products}
          onClose={() => setShowExport(false)}
          onFixErrors={handleFixErrors}
          onExportSuccess={(count) => { setShowExport(false); setExportToast({ count }); try { localStorage.removeItem(`cc_review_state_${catalogId}`); } catch {} }}
        />
      )}

      {/* Post-download toast */}
      {exportToast && (
        <ExportToast count={exportToast.count} onDismiss={() => setExportToast(null)} />
      )}

      {/* Standalone upgrade modal (triggered from free-tier banners) */}
      {showUpgradeModal && (
        <ExportPaywallModal
          onClose={() => setShowUpgradeModal(false)}
          productCount={stats?.product_count ?? 0}
          errorCount={stats?.error_count ?? 0}
          catalogId={catalogId}
          trigger="review_banner"
        />
      )}

      {/* Import Images modal */}
      {showImportImg && (
        <ImageUploadModal
          catalogId={catalogId}
          products={products}
          onClose={() => setShowImportImg(false)}
          onImagesUpdated={handleImagesUpdated}
        />
      )}

      {/* Custom column modal */}
      {showCustomColModal && (
        <CustomColumnModal
          catalogId={catalogId}
          existingColumn={editingCustomCol}
          onClose={() => { setShowCustomColModal(false); setEditingCustomCol(null); }}
          onSaved={col => {
            setCustomColumns(prev => {
              const idx = prev.findIndex(c => c.id === col.id);
              return idx >= 0 ? prev.map(c => c.id === col.id ? col : c) : [...prev, col];
            });
            setShowCustomColModal(false);
            setEditingCustomCol(null);
          }}
          onDeleted={colId => {
            setCustomColumns(prev => prev.filter(c => c.id !== colId));
            setCustomValues(prev => { const n = { ...prev }; delete n[colId]; return n; });
            setShowCustomColModal(false);
            setEditingCustomCol(null);
          }}
        />
      )}

      {/* Column reorder modal */}
      {showColReorder && (
        <ColumnReorder
          columns={customColumns}
          onReorder={newOrder => setCustomColumns(newOrder)}
          onClose={() => setShowColReorder(false)}
        />
      )}

      {/* Undo toast */}
      {undoToast && (
        <UndoToast
          key={undoKey}
          message={undoToast.message}
          onUndo={undoToast.undoFn}
          onDismiss={undoToast.onDismiss || dismissUndo}
          duration={undoToast.duration}
        />
      )}

      {/* Context menu */}
      <AnimatePresence>
      {contextMenu && (
        <motion.div
          id="row-context-menu"
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.95 }}
          transition={{ duration: 0.1, ease: 'easeOut' }}
          style={{
            position: 'fixed', top: contextMenu.y, left: contextMenu.x, zIndex: 9999,
            background: 'var(--color-surface)', border: '1px solid var(--color-border)',
            borderRadius: 8, boxShadow: 'var(--shadow-card)', padding: 4, minWidth: 180,
            transformOrigin: 'top left',
          }}
        >
          {contextMenu.type === 'product' ? (
            <>
              <div
                className="ctx-item"
                onClick={() => { handleAddProduct(contextMenu.productId); setContextMenu(null); }}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', fontSize: 13, fontWeight: 400, color: 'var(--color-text)', borderRadius: 4, cursor: 'pointer' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--color-surface-2)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <Plus size={14} /> Add product below
              </div>
              <div
                className="ctx-item"
                onClick={() => { const p = products.find(p => p.id === contextMenu.productId); if (p) handleAddVariant(p); setContextMenu(null); }}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', fontSize: 13, fontWeight: 400, color: 'var(--color-text)', borderRadius: 4, cursor: 'pointer' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--color-surface-2)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <Plus size={14} /> Add variant
              </div>
              <div style={{ height: 1, background: 'var(--color-border)', margin: '4px 0' }} />
              <div
                className="ctx-item"
                onClick={() => { handleBulkDeleted([contextMenu.productId]); setContextMenu(null); }}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', fontSize: 13, fontWeight: 400, color: 'var(--color-red-text)', borderRadius: 4, cursor: 'pointer' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--color-surface-2)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <Trash2 size={14} /> Delete product
              </div>
            </>
          ) : (
            <>
              <div
                className="ctx-item"
                onClick={() => { const p = products.find(p => p.id === contextMenu.productId); if (p) handleAddVariant(p, contextMenu.variantId); setContextMenu(null); }}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', fontSize: 13, fontWeight: 400, color: 'var(--color-text)', borderRadius: 4, cursor: 'pointer' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--color-surface-2)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <Plus size={14} /> Add variant below
              </div>
              <div style={{ height: 1, background: 'var(--color-border)', margin: '4px 0' }} />
              <div
                className="ctx-item"
                onClick={() => { handleDeleteVariant(contextMenu.variantId, contextMenu.productId); setContextMenu(null); }}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 12px', fontSize: 13, fontWeight: 400, color: 'var(--color-red-text)', borderRadius: 4, cursor: 'pointer' }}
                onMouseEnter={e => e.currentTarget.style.background = 'var(--color-surface-2)'}
                onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
              >
                <Trash2 size={14} /> Delete variant
              </div>
            </>
          )}
        </motion.div>
      )}
      </AnimatePresence>

      {/* Toast */}
      {toastMsg && <Toast msg={toastMsg} onDone={() => setToastMsg(null)} />}

    </div>
  );
}

// ─── TopBar ───────────────────────────────────────────────────────────────────
function TopBar({ name, navigate, catalogId, renameSaving, onRename, onExport, userPlan, errorCount, stickyTop = 0, processing = false, productCount = 0, variantCount = 0 }) {
  const [editingName, setEditingName] = useState(false);
  const [nameVal, setNameVal]         = useState(name);
  useEffect(() => { setNameVal(name); }, [name]);

  function commitRename() { setEditingName(false); onRename(nameVal); }

  const hasErrors = (errorCount ?? 0) > 0;

  return (
    <header style={{
      position: 'sticky', top: stickyTop, zIndex: 30,
      background: 'var(--color-surface)', borderBottom: '1px solid var(--color-border)',
      boxShadow: 'var(--shadow-xs)',
    }}>
      <div style={{ margin: '0 auto', padding: '12px 20px', height: 56, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {/* Left */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, minWidth: 0 }}>
          <button
            onClick={() => navigate('/dashboard')}
            title="Back to Dashboard"
            style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-2)', padding: 4, borderRadius: 'var(--r-sm)' }}
          >
            <ChevronLeft size={20} />
          </button>
          {editingName ? (
            <input
              autoFocus value={nameVal}
              onChange={e => setNameVal(e.target.value)}
              onBlur={commitRename}
              onKeyDown={e => { if (e.key === 'Enter') commitRename(); if (e.key === 'Escape') setEditingName(false); }}
              style={{
                fontSize: 16, fontWeight: 600, color: 'var(--color-text)',
                background: 'transparent', border: 'none', borderBottom: '1.5px solid var(--color-primary)',
                borderRadius: 0, padding: '2px 1px', outline: 'none',
                maxWidth: 720,
              }}
            />
          ) : (
            <span
              onClick={() => { setEditingName(true); setNameVal(name || ''); }}
              title={name || 'Untitled catalog'}
              style={{
                fontSize: 16, fontWeight: 600, color: 'var(--color-text)',
                cursor: 'text', padding: '2px 1px',
                maxWidth: 720, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
              }}
            >
              {name || 'Untitled catalog'}
              {renameSaving && <span style={{ marginLeft: 6, fontSize: 11, color: 'var(--color-text-3)' }}>saving…</span>}
            </span>
          )}
        </div>

        {/* Right */}
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          {processing ? (
            <button
              disabled
              style={{ display: 'flex', alignItems: 'center', gap: 6, height: 36, padding: '0 14px', borderRadius: 'var(--r-md)', background: 'var(--color-border-strong)', border: 'none', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'not-allowed', opacity: 0.6 }}
            >
              <Download size={14} />Generate Shopify CSV
            </button>
          ) : (
            <button
              onClick={onExport}
              style={{ display: 'flex', alignItems: 'center', gap: 6, height: 36, padding: '0 14px', borderRadius: 'var(--r-md)', background: 'var(--color-primary)', border: 'none', color: '#fff', fontSize: 14, fontWeight: 600, cursor: 'pointer' }}
            >
              <Download size={14} />Generate Shopify CSV
            </button>
          )}
        </div>
      </div>
    </header>
  );
}

// ─── StatsBar ─────────────────────────────────────────────────────────────────
function StatsBar({ stats, filterCounts, allVariants, stickyTop = 56, setFilter }) {
  return (
    <div style={{ position: 'sticky', top: stickyTop, zIndex: 25, background: 'var(--color-surface-2)', borderBottom: '1px solid var(--color-border)' }}>
      <div style={{ padding: '0 24px', minHeight: 44, display: 'flex', alignItems: 'center', gap: 0 }}>
        <Stat label="Products" value={filterCounts.all} />
        <Divider />
        <Stat label="Variants" value={allVariants.length} />
      </div>
    </div>
  );
}

function Stat({ label, value, color }) {
  return (
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 5, padding: '0 20px' }}>
      <span style={{ fontSize: 12, fontWeight: 600, color: color || 'var(--color-text-2)', fontVariantNumeric: 'tabular-nums' }}>{value ?? '—'}</span>
      <span style={{ fontSize: 12, color: 'var(--color-text-3)' }}>{label}</span>
    </div>
  );
}

function Divider() {
  return <div style={{ width: 1, height: 20, background: 'var(--color-border)', flexShrink: 0 }} />;
}

// ─── ReviewCell ──────────────────────────────────────────────────────────────
function ReviewCell({ flags = [], onClick }) {
  const active = flags.filter(f => !f.resolved && !RETIRED_FLAG_TYPES.has(f.flag_type));
  // V2: info-severity flags do not contribute to badge counts (§3.5)
  const actionable = active.filter(f => f.severity !== 'info');
  const isErr = actionable.some(f => ERROR_FLAG_TYPES.has(f.flag_type));
  const n = isErr ? actionable.filter(f => ERROR_FLAG_TYPES.has(f.flag_type)).length : actionable.length;

  return (
    <td
      onClick={e => { e.stopPropagation(); onClick(); }}
      style={{ padding: '4px 8px', cursor: 'pointer' }}
    >
      <div style={{ display: 'inline-flex', alignItems: 'center', gap: 5, whiteSpace: 'nowrap' }}>
        {n === 0 ? null : isErr ? (
          <>
            <Flag size={13} style={{ color: 'var(--color-red)', flexShrink: 0 }} />
            <span style={{ color: 'var(--color-red-text)', fontWeight: 600, fontSize: 12 }}>{n} error{n > 1 ? 's' : ''}</span>
          </>
        ) : (
          <>
            <Flag size={13} style={{ color: 'var(--color-yellow)', flexShrink: 0 }} />
            <span style={{ color: 'var(--color-yellow-text)', fontWeight: 600, fontSize: 12 }}>{n} warning{n > 1 ? 's' : ''}</span>
          </>
        )}
      </div>
    </td>
  );
}

const DRAWER_MESSAGES = {
  missing_title:       'Add a product title',
  missing_sku:         'Add a SKU',
  missing_price:       'Add a price',
  invalid_price:       'Fix the price format',
  price_exceeds_rrp:   'Wholesale price equals or exceeds the recommended retail price (MSRP). Review pricing before import.',
  duplicate_sku:       'This SKU is used by another product',
  low_confidence:      'Check this value against your catalog',
  currency_mismatch:   'Products use different currencies',
  variant_mismatch:    'Option names don\u2019t match across variants',
  field_contradiction: 'The catalog lists conflicting values for this field',
  zero_price:          'This variant\u2019s price is $0.00. Verify the price is correct before exporting.',
  invalid_image_url:   'This image URL is not HTTPS. Shopify requires HTTPS image URLs for import.',
  // V2 expansion flags (Phase 2.5)
  range_expanded_from_file: 'Size range was expanded into individual variants using companion file data.',
  expansion_skipped_no_grammar: 'No expansion grammar matched this product category. Size token preserved as-is.',
  non_expandable_multifit: 'Multi-fit size token preserved as a single variant. Companion file does not confirm separate sizes.',
  off_style_token_ignored: 'This size token doesn\u2019t match the product category and was not expanded.',
  capacity_not_size:   'This looks like a capacity measurement, not a size. Preserved as-is.',
  assortment_atomic:   'Assortment or variety pack preserved as a single product. No child variants created.',
  expansion_rpc_failed: 'Variant expansion encountered a database error. Original variant preserved.',
  // V2 authority flags (Phase 2.5)
  low_confidence_authority: 'The file\u2019s role was detected with low confidence. Automatic field updates were not applied. Verify the file type and re-import if the classification is wrong.',
  low_coverage_authority: 'This file has low field coverage. Many variants are missing data for fields this file should provide. Check if the file is complete.',
  stale_authority_warning: 'This file\u2019s data may be outdated. A fresher source was used instead.',
  // Phase 4 image association flags
  image_low_confidence_match: 'This image was matched to a product at low confidence. Verify the match before export.',
  image_unmatched: 'Some images couldn\u2019t be matched to a product. Preserved for manual assignment in image gallery (coming soon).',
  image_decorative_filtered: 'Logos, banners, and decorative images detected in supplier files were not imported.',
  image_orphan_sku_not_in_catalog: 'An image references a SKU that is not in this catalog. Image not imported.',
  image_color_code_mismatch: 'An image has a color code that doesn\u2019t match the catalog. Verify the color mapping.',
  video_requires_manual_import: 'A video file was detected. Shopify CSV doesn\u2019t support video \u2014 use Shopify Media API to upload manually.',
  image_format_converted: 'Some images were converted from SVG/HEIC to PNG/JPEG for Shopify compatibility.',
  // Phase 4 encrypted ZIP flags
  zip_password_required: 'This ZIP is password-protected, so we can\u2019t process it yet. Please upload an unencrypted ZIP or upload the files directly.',
  zip_zipcrypto_unsupported: 'This ZIP uses legacy password protection, which is not supported yet. Please re-export it as an unencrypted ZIP or upload the files directly.',
  zip_aes_encryption_unsupported: 'This ZIP uses AES encryption, which is not supported yet. Please re-export it as an unencrypted ZIP or upload the files directly.',
  // Brief 1A multi-file (Brief V1-Finishing v2 D.4 / DV1.19)
  multi_catalog_drop_detected: 'We detected multiple catalogs in this drop and processed the highest-scoring one. Other catalog(s) are stored as supplements.',
  // Dispatch A (2026-05-27): status⇄flag invariant + missing-field surfacing.
  // F-049 (2026-05-29): missing_vendor drawer entry dropped per Migration 084.
  derived_title_needs_review: 'We derived this title from the row’s description column because the source had no separate title column. Review and edit if needed before exporting.',
  missing_category:            'No product category was mapped from the source. Set a category so Shopify can place this product in its taxonomy.',
  // Dispatch B Piece 1 (2026-05-27): category flag types.
  category_missing:            'No product category was resolved for this product. Set one before exporting so Shopify can place it in its taxonomy.',
  category_uncertain:          'Multiple category candidates resolved; the system isn’t confident which one applies. Review and pick the right category.',
  category_outlier:            'This category is unusual for this catalog — most other products are in a different branch. Verify the category before exporting.',
  category_token_mismatch:     'The title doesn’t obviously match the resolved category. Confirm the category is correct.',
  // Migration 080 (2026-05-27 dispatch T10) — header-resolver flags
  unmapped_column:               'We didn’t recognize this column name from your file. Verify the column header or leave it unmapped if it isn’t a Shopify field.',
  status_coercion_unclear:       'The Status value in your file wasn’t a recognized Shopify status (active/draft/archived) or alias. We defaulted to active — change it here if that’s wrong.',
  category_source_disagreement:  'The category from your file and the category we’d infer from the title disagree at the top level. We kept your file’s value — confirm or pick a different category.',
  gender_source_disagreement:    'The Gender value from your file isn’t a recognized Shopify enum (male/female/unisex/kids). We re-resolved Gender from the title — confirm the result.',
  age_group_source_disagreement: 'The Age group value from your file isn’t a recognized Shopify enum (newborn/infant/toddler/kids/adult). We re-resolved Age group from the title — confirm the result.',
};

function flashFocusRing(cell, isError) {
  const color = isError ? 'var(--color-red)' : 'var(--color-yellow)';
  cell.style.outline = `2px solid ${color}`;
  cell.style.outlineOffset = '2px';
  setTimeout(() => {
    cell.style.outline = '';
    cell.style.outlineOffset = '';
  }, 1500);
}

// ─── SourcePanel ──────────────────────────────────────────────────────────────
function SourcePanel({ product, variant, allFlags, onClose, onNavigateFlag, onNavigateIssue, issueRemaining, width, onResize }) {
  const groups = groupFlags(allFlags);
  const hasErrors = groups.some(g => g.severity === 'error');
  const confidence = variant?.confidence_score;
  const sourcePage = variant?.source_page;
  const [dragActive, setDragActive] = useState(false);

  // Confidence color
  const confColor = confidence == null ? 'var(--color-text-3)'
    : confidence >= 0.90 ? 'var(--color-green)'
    : confidence >= 0.70 ? 'var(--color-yellow)'
    : 'var(--color-red)';
  const confLabel = confidence == null ? 'N/A'
    : `${Math.round(confidence * 100)}%`;

  // Drag handle for resizing
  const handleMouseDown = useCallback((e) => {
    e.preventDefault();
    setDragActive(true);
    const startX = e.clientX;
    const startW = width;
    function onMove(ev) {
      const delta = startX - ev.clientX;
      const newW = Math.min(Math.max(startW + delta, 280), 700);
      onResize(newW);
    }
    function onUp() {
      setDragActive(false);
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    }
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, [width, onResize]);

  // Find fields with low_confidence flags for detail cards
  const lowConfFlags = (variant?.flags || []).filter(f => f.flag_type === 'low_confidence' && !f.resolved);
  const fieldFlags = (variant?.flags || []).filter(f => !f.resolved && f.flag_type !== 'low_confidence');

  return (
    <div style={{ width, minWidth: 280, maxWidth: 700, flexShrink: 0, display: 'flex', height: '100%' }}>
      {/* Drag handle */}
      <div
        onMouseDown={handleMouseDown}
        style={{
          width: 5, cursor: 'col-resize', flexShrink: 0,
          background: dragActive ? 'var(--color-primary)' : 'transparent',
          transition: 'background 0.15s',
        }}
        onMouseEnter={e => { if (!dragActive) e.currentTarget.style.background = 'var(--color-border-strong)'; }}
        onMouseLeave={e => { if (!dragActive) e.currentTarget.style.background = 'transparent'; }}
      />
      {/* Panel content */}
      <div style={{
        flex: 1, overflow: 'auto', borderLeft: '1px solid var(--color-border)',
        background: 'var(--color-surface-2)', display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{
          padding: '12px 16px', borderBottom: '1px solid var(--color-border)',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          position: 'sticky', top: 0, background: 'var(--color-surface-2)', zIndex: 2,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <FileText size={16} style={{ color: 'var(--color-text-2)' }} />
            <span style={{ fontWeight: 600, fontSize: 14, color: 'var(--color-text)' }}>
              Source{sourcePage != null ? ` — page ${sourcePage}` : ''}
            </span>
          </div>
          <button
            onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-3)', padding: 4, display: 'flex', lineHeight: 1 }}
            aria-label="Close source panel"
          >
            <X size={16} />
          </button>
        </div>

        {/* Product / Variant info */}
        <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--color-border)' }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-text)', marginBottom: 4, lineHeight: 1.4 }}>
            {product?.title || 'Untitled Product'}
          </div>
          {variant && (variant.option1_value || variant.sku) && (
            <div style={{ fontSize: 12, color: 'var(--color-text-2)' }}>
              {variant.sku ? `SKU: ${variant.sku}` : ''}
              {variant.sku && variant.option1_value ? ' · ' : ''}
              {variant.option1_value || ''}
            </div>
          )}
        </div>

        {/* Confidence bar */}
        <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--color-border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
            <span style={{ fontSize: 12, fontWeight: 500, color: 'var(--color-text-2)' }}>Confidence</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: confColor }}>{confLabel}</span>
          </div>
          {confidence != null && (
            <div style={{ height: 6, borderRadius: 3, background: 'var(--color-surface-2)', overflow: 'hidden' }}>
              <div style={{
                width: `${Math.round(confidence * 100)}%`, height: '100%',
                borderRadius: 3, background: confColor,
                transition: 'width 0.3s ease',
              }} />
            </div>
          )}
        </div>

        {/* Flags section */}
        {allFlags.length > 0 && (
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--color-border)' }}>
            <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--color-text-2)', marginBottom: 8 }}>
              Needs review ({allFlags.length})
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {groups.map((group) => {
                const isErr = group.severity === 'error';
                const isInfo = group.severity === 'info';
                const message = group.message || DRAWER_MESSAGES[group.flag_type] || group.flag_type;
                const dotColor = isErr ? 'var(--color-red)' : isInfo ? STATUS.blue.dot : 'var(--color-yellow)';
                return (
                  <div
                    key={group.flag_type}
                    onClick={() => onNavigateFlag(group)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      cursor: 'pointer', borderRadius: 'var(--r-sm)',
                      padding: '6px 8px', margin: '0 -8px',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--color-surface-2)'}
                    onMouseLeave={e => e.currentTarget.style.background = ''}
                  >
                    <span style={{
                      width: 6, height: 6, borderRadius: '50%', flexShrink: 0,
                      background: dotColor,
                    }} />
                    <span style={{ fontSize: 13, color: 'var(--color-text-1)', flex: 1 }}>{message}</span>
                    {group.count > 1 && (
                      <span style={{ fontSize: 11, color: 'var(--color-text-3)', fontWeight: 500 }}>
                        x{group.count}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Low-confidence field detail cards */}
        {lowConfFlags.length > 0 && (
          <div style={{ padding: '12px 16px', borderBottom: '1px solid var(--color-border)' }}>
            <div style={{ fontSize: 12, fontWeight: 500, color: 'var(--color-text-2)', marginBottom: 8 }}>
              Fields to review
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              {lowConfFlags.map((f, i) => {
                const fieldName = f.field_name || 'unknown';
                const fieldValue = variant?.[fieldName];
                return (
                  <div key={i} style={{
                    padding: '8px 10px', borderRadius: 'var(--r-sm)',
                    border: '1px solid var(--color-yellow-border)',
                    background: 'var(--color-yellow-bg)',
                  }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-yellow-text)', textTransform: 'uppercase', marginBottom: 2 }}>
                      {fieldName.replace(/_/g, ' ')}
                    </div>
                    <div style={{ fontSize: 13, color: 'var(--color-text)' }}>
                      {fieldValue != null ? String(fieldValue) : '—'}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* No issues state */}
        {allFlags.length === 0 && (
          <div style={{ padding: '24px 16px', textAlign: 'center' }}>
            <CheckCircle size={24} style={{ color: 'var(--color-green)', marginBottom: 8 }} />
            <div style={{ fontSize: 13, color: 'var(--color-text-2)' }}>No issues found</div>
          </div>
        )}

        {/* Next issue navigation button */}
        {onNavigateIssue && (
          <div style={{ padding: '12px 16px', borderTop: '1px solid var(--color-border)', marginTop: 'auto' }}>
            <button
              onClick={onNavigateIssue}
              style={{
                width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                padding: '8px 12px', borderRadius: 'var(--r-sm)', border: '1px solid var(--color-border)',
                background: 'var(--color-surface)', color: 'var(--color-text)', fontSize: 13, fontWeight: 500,
                cursor: 'pointer',
              }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--color-surface-2)'}
              onMouseLeave={e => e.currentTarget.style.background = 'var(--color-surface)'}
            >
              <ArrowDown size={14} />
              Next issue{issueRemaining > 0 ? ` (${issueRemaining} remaining)` : ''}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── IssuesDrawer ─────────────────────────────────────────────────────────────
function IssuesDrawer({ product, allFlags, onClose }) {
  const groups = groupFlags(allFlags);
  const hasErrors = groups.some(g => g.severity === 'error');
  const hasWarnings = groups.some(g => g.severity === 'warning');

  function handleFlagClick(group) {
    const isErr = group.severity === 'error';
    const flagType = group.flag_type;
    const affectedVariants = (product.variants || []).filter(v =>
      (v.flags || []).some(f => f.flag_type === flagType && !f.resolved)
    );
    if (affectedVariants.length === 0) return;

    const firstVariant = affectedVariants[0];

    const colKey = flagType === 'low_confidence'
      ? ((firstVariant.flags || []).find(f => f.flag_type === 'low_confidence' && !f.resolved)?.field_name || 'price')
      : FLAG_COLUMNS[flagType];
    if (!colKey) return;

    const cell = document.querySelector(`[data-variant-id="${firstVariant.id}"] [data-col="${colKey}"]`);
    if (!cell) return;

    cell.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'nearest' });
    flashFocusRing(cell, isErr);

    setTimeout(() => {
      if (DRAWER_OPEN_EDIT.has(flagType)) {
        const input = cell.querySelector('input, textarea');
        if (input) input.focus();
      }
    }, 150);
  }

  return (
    <tr>
      <td colSpan={999} style={{ padding: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'flex-end', background: 'var(--color-surface-2)', borderTop: '1px solid var(--color-border)', borderBottom: '1px solid var(--color-border)', borderLeft: `3px solid ${hasErrors ? 'var(--color-red)' : hasWarnings ? 'var(--color-yellow)' : 'var(--color-border-strong)'}`, animation: 'drawerIn 200ms ease-out', overflow: 'hidden' }}>
          <div style={{ padding: '10px 16px', minWidth: 360, flex: '0 1 700px', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
              {groups.map((group) => {
                const isErr = group.severity === 'error';
                const isInfo = group.severity === 'info';
                const message = group.message || DRAWER_MESSAGES[group.flag_type] || group.flag_type;
                const pillBg = isErr ? 'var(--color-red-bg)' : isInfo ? STATUS.blue.bg : 'var(--color-yellow-bg)';
                const pillColor = isErr ? 'var(--color-red-text)' : isInfo ? STATUS.blue.text : 'var(--color-yellow-text)';
                const pillBorder = isErr ? 'var(--color-red-border)' : isInfo ? STATUS.blue.border : 'var(--color-yellow-border)';
                return (
                  <div
                    key={group.flag_type}
                    onClick={() => handleFlagClick(group)}
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 5, cursor: 'pointer', borderRadius: 'var(--r-full)', padding: '3px 10px 3px 7px', background: pillBg, color: pillColor, border: `1px solid ${pillBorder}`, fontSize: 12, fontWeight: 500 }}
                  >
                    {isErr ? <AlertCircle size={13} style={{ flexShrink: 0 }} /> : isInfo ? <Info size={13} style={{ flexShrink: 0 }} /> : <AlertTriangle size={13} style={{ flexShrink: 0 }} />}
                    <span>{message}</span>
                  </div>
                );
              })}
            </div>
            <button
              onClick={e => { e.stopPropagation(); onClose(); }}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-3)', padding: 2, flexShrink: 0, lineHeight: 1, fontSize: 16 }}
              aria-label="Close issues"
            >×</button>
          </div>
        </div>
      </td>
    </tr>
  );
}

// ─── ProductRow ───────────────────────────────────────────────────────────────
function ProductRow({ product, variants, isCollapsed, flagCount, errCount, allFlags = [], drawerIsOpen = false, colVis, onToggle, onSave, onFlagClick, searchQuery, isSelected, onToggleSelect, selectedVariantIds, customColumns = [], isNew = false, staggerDelay = 0, dndDisabled = false, dragOverId = null, dragOverEdge = 'below', isPartOfMultiDrag = false, productImages = [], onImagesChange, onToast, onContextMenu, categorySuggestions = [], catalogTags = [], optionLabels = [null, null, null] }) {
  const colCount          = 3 + [colVis.status, colVis.vendor, colVis.type, colVis.tags, colVis.body, colVis.collection, colVis.productCategory, colVis.option1, colVis.option2, colVis.option3, colVis.image, colVis.compareAt, colVis.barcode, colVis.inventory, colVis.continueSellingOos, colVis.weight, colVis.seoTitle, colVis.seoDesc].filter(Boolean).length;
  const checkboxRef = useRef(null);
  const allVariantIds = variants.map(v => v.id);
  const selectedCount = allVariantIds.filter(id => selectedVariantIds?.has(id)).length;
  const someSelected = selectedCount > 0 && selectedCount < allVariantIds.length;
  const allSelected = allVariantIds.length > 0 && selectedCount === allVariantIds.length;
  useEffect(() => {
    if (checkboxRef.current) checkboxRef.current.indeterminate = someSelected;
  }, [someSelected]);
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: product.id, disabled: dndDisabled });
  const dragStyle = {
    transform: isPartOfMultiDrag ? undefined : DndCSS.Transform.toString(transform),
    transition,
    opacity: isPartOfMultiDrag ? 0.2 : 1,
    filter: isPartOfMultiDrag ? 'blur(1px)' : undefined,
    boxShadow: isDragging ? '0 4px 12px rgba(0,0,0,0.15)' : undefined,
    zIndex: isDragging ? 10 : undefined,
    background: isDragging ? 'var(--color-surface)' : undefined,
  };
  const isDropTarget = dragOverId === product.id && !isDragging;
  return (
    <tr ref={setNodeRef} className="prod-row" data-product-id={product.id} data-status={product.row_status} onContextMenu={onContextMenu} style={{ ...dragStyle, background: 'var(--color-surface)', animation: isNew ? `rowEntry 200ms cubic-bezier(0.16,1,0.3,1) ${staggerDelay}ms both` : undefined, boxShadow: isDropTarget ? (dragOverEdge === 'above' ? 'inset 0 2px 0 var(--color-primary)' : 'inset 0 -2px 0 var(--color-primary)') : undefined }}>
      <td data-col="title" className="sticky-col" data-nav-cell="true" style={{ padding: '4px 8px 4px 12px', width: 300, maxWidth: 300, ...stickyTdStyle(0, true, { background: allFlags.some(f => f.flag_type === 'missing_title' && !f.resolved) ? 'var(--color-red-bg)' : 'var(--color-surface)' }), boxShadow: isDropTarget ? (dragOverEdge === 'above' ? 'inset 0 2px 0 var(--color-primary)' : 'inset 0 -2px 0 var(--color-primary)') : undefined }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {!dndDisabled ? (
          <span
            {...attributes}
            {...listeners}
            style={{ cursor: isDragging ? 'grabbing' : 'grab', display: 'flex', flexShrink: 0, touchAction: 'none' }}
          >
            <GripVertical className="row-drag-handle" size={14} color="var(--color-text-2)" style={{ flexShrink: 0 }} />
          </span>
          ) : (
          <span title="Clear filters to reorder" style={{ display: 'flex', flexShrink: 0, opacity: 0.3 }}>
            <GripVertical className="row-drag-handle" size={14} color="var(--color-text-3)" style={{ flexShrink: 0 }} />
          </span>
          )}
          <input
            ref={checkboxRef}
            type="checkbox"
            checked={allSelected}
            onChange={e => e.stopPropagation()}
            onClick={e => { e.stopPropagation(); onToggleSelect(e); }}
            style={{ cursor: 'pointer', accentColor: 'var(--color-primary)', flexShrink: 0, width: 15, height: 15 }}
          />
          <InlineEdit
              value={product.title || ''} onSave={v => onSave('title', v)}
              placeholder="Product title" missingLabel="missing"
              extraStyle={{ fontWeight: 400 }}
              renderValue={searchQuery ? highlight(product.title || '', searchQuery) : (product.title || null)}
            />
          <button
            className="expand-chevron"
            data-expanded={!isCollapsed}
            onClick={e => { e.stopPropagation(); onToggle(); }}
            aria-label={isCollapsed ? `Expand ${variants.length} variants` : 'Collapse variants'}
          >
            <span className="variant-count">{variants.length} variant{variants.length !== 1 ? 's' : ''}</span>
            <ChevronDown size={14} />
          </button>
        </div>
      </td>
      {/* Column order locked 2026-04-22 — Images → SKU → Price → Compare At → Category → Description → [opt-in] → Review */}
      {colVis.image      && <td className="cell-na" aria-label="Not applicable" style={{ padding: '4px 8px', cursor: 'default' }}><span aria-hidden="true" style={{ color: 'var(--color-text-3)', userSelect: 'none' }}>—</span></td>}
      {/* Variant-level columns — N/A in product header row */}
      <td className="cell-na" aria-label="Not applicable" style={{ padding: '4px 8px', cursor: 'default' }}><span aria-hidden="true" style={{ color: 'var(--color-text-3)', userSelect: 'none' }}>—</span></td>
      <td className="cell-na" aria-label="Not applicable" style={{ padding: '4px 8px', cursor: 'default' }}><span aria-hidden="true" style={{ color: 'var(--color-text-3)', userSelect: 'none' }}>—</span></td>
      {colVis.compareAt  && <td className="cell-na" aria-label="Not applicable" style={{ padding: '4px 8px', cursor: 'default' }}><span aria-hidden="true" style={{ color: 'var(--color-text-3)', userSelect: 'none' }}>—</span></td>}
      {colVis.productCategory && <td style={{ padding: '4px 8px', cursor: 'pointer' }}><CategoryPicker value={product.product_category || ''} categoryId={product.product_category_id || ''} onSelect={(gid, name) => { onSave('product_category', name); onSave('product_category_id', gid); }} suggestions={categorySuggestions} /></td>}
      {colVis.type       && <td style={{ padding: '4px 8px' }}><EditableCell value={product.product_type || null} onSave={v => onSave('product_type', v)} placeholder="" /></td>}
      {colVis.body       && <td style={{ padding: '4px 8px', cursor: 'pointer', maxWidth: 400 }}><DescriptionEdit value={product.description || ''} onSave={v => onSave('description', v)} /></td>}
      {colVis.status     && <td style={{ padding: '4px 8px', cursor: 'pointer' }}><StatusCell value={product.status || 'draft'} onSave={v => onSave('status', v)} /></td>}
      {colVis.vendor     && <td style={{ padding: '4px 8px' }}><EditableCell value={product.vendor || null} onSave={v => onSave('vendor', v)} placeholder="" /></td>}
      {colVis.tags       && <td style={{ padding: '4px 8px', cursor: 'pointer' }}><TagChipInput value={mergeRenderTags(product)} onSave={v => onSave('tags', v)} catalogTags={catalogTags} /></td>}
      {colVis.collection && <td style={{ padding: '4px 8px' }}><EditableCell value={product.collection || null} onSave={v => onSave('collection', v)} placeholder="" /></td>}
      {colVis.option1    && <td className="cell-na" aria-label="Not applicable" style={{ padding: '4px 8px', cursor: 'default' }}><span aria-hidden="true" style={{ color: 'var(--color-text-3)', userSelect: 'none' }}>—</span></td>}
      {colVis.option2    && <td className="cell-na" aria-label="Not applicable" style={{ padding: '4px 8px', cursor: 'default' }}><span aria-hidden="true" style={{ color: 'var(--color-text-3)', userSelect: 'none' }}>—</span></td>}
      {colVis.option3    && <td className="cell-na" aria-label="Not applicable" style={{ padding: '4px 8px', cursor: 'default' }}><span aria-hidden="true" style={{ color: 'var(--color-text-3)', userSelect: 'none' }}>—</span></td>}
      {colVis.barcode            && <td className="cell-na" aria-label="Not applicable" style={{ padding: '4px 8px', cursor: 'default' }}><span aria-hidden="true" style={{ color: 'var(--color-text-3)', userSelect: 'none' }}>—</span></td>}
      {colVis.inventory          && <td className="cell-na" aria-label="Not applicable" style={{ padding: '4px 8px', cursor: 'default' }}><span aria-hidden="true" style={{ color: 'var(--color-text-3)', userSelect: 'none' }}>—</span></td>}
      {colVis.continueSellingOos && <td className="cell-na" aria-label="Not applicable" style={{ padding: '4px 8px', cursor: 'default' }}><span aria-hidden="true" style={{ color: 'var(--color-text-3)', userSelect: 'none' }}>—</span></td>}
      {colVis.weight             && <td className="cell-na" aria-label="Not applicable" style={{ padding: '4px 8px', cursor: 'default' }}><span aria-hidden="true" style={{ color: 'var(--color-text-3)', userSelect: 'none' }}>—</span></td>}
      {colVis.seoTitle   && <td style={{ padding: '4px 8px' }}><EditableCell value={product.seo_title || null} onSave={v => onSave('seo_title', v)} placeholder="" /></td>}
      {colVis.seoDesc    && <td style={{ padding: '4px 8px' }}><EditableCell value={product.seo_description || null} onSave={v => onSave('seo_description', v)} placeholder="" /></td>}
      <ReviewCell flags={allFlags} onClick={onFlagClick} />
      {customColumns.map(col => <td key={col.id} />)}
    </tr>
  );
}

// ─── VariantRow ───────────────────────────────────────────────────────────────
function VariantRow({ variant, product, vIdx, isFirst, colVis, vFlags, hasLowConf, onSaveProduct, onSave, onDelete, onFlagClick, flagPanelOpen, searchQuery, isSelected, isVariantSelected, onToggleVariantSelect, onToggleSelect, customColumns = [], customValues = {}, onCustomValueChange, drawerHighlight = false, dndDisabled = false, dragOverId = null, dragOverEdge = 'below', isPartOfMultiDrag = false, productImages = [], onImagesChange, variantImages = [], onVariantImagesChange, onToast, onContextMenu, categorySuggestions = [] }) {
  const errFlags  = vFlags.filter(f => f.severity === 'error');
  const effectiveSelected = isVariantSelected != null ? isVariantSelected : isSelected;
  const rowBg = drawerHighlight ? 'color-mix(in srgb, var(--color-primary-light) 50%, transparent)' : effectiveSelected ? 'var(--color-primary-light)' : 'var(--color-surface)';
  const stickyBg = drawerHighlight ? 'color-mix(in srgb, var(--color-primary-light) 50%, var(--color-surface))' : effectiveSelected ? 'color-mix(in srgb, var(--color-primary-light) 80%, var(--color-surface))' : 'var(--color-surface)';
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: variant.id, disabled: dndDisabled });
  const dragStyle = {
    transform: isPartOfMultiDrag ? undefined : DndCSS.Transform.toString(transform),
    transition,
    opacity: isPartOfMultiDrag ? 0.2 : 1,
    filter: isPartOfMultiDrag ? 'blur(1px)' : undefined,
    boxShadow: isDragging ? '0 4px 12px rgba(0,0,0,0.15)' : undefined,
    zIndex: isDragging ? 10 : undefined,
    background: isDragging ? 'var(--color-surface)' : undefined,
  };
  const isDropTarget = dragOverId === variant.id && !isDragging;

  return (
    <tr ref={setNodeRef} className="var-row" data-variant-id={variant.id} data-status={variant.row_status} onContextMenu={onContextMenu} style={{ ...dragStyle, background: drawerHighlight ? 'color-mix(in srgb, var(--color-primary-light) 50%, transparent)' : effectiveSelected ? 'var(--color-primary-light)' : undefined, boxShadow: isDropTarget ? (dragOverEdge === 'above' ? 'inset 0 2px 0 var(--color-primary)' : 'inset 0 -2px 0 var(--color-primary)') : undefined }}>
      <td data-col="title" className="sticky-col" style={{ padding: '4px 8px 4px 12px', width: 300, maxWidth: 300, cursor: 'default', ...stickyTdStyle(0, true, { background: stickyBg }), boxShadow: isDropTarget ? (dragOverEdge === 'above' ? 'inset 0 2px 0 var(--color-primary)' : 'inset 0 -2px 0 var(--color-primary)') : undefined }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {!dndDisabled ? (
          <span
            {...attributes}
            {...listeners}
            style={{ cursor: isDragging ? 'grabbing' : 'grab', display: 'flex', flexShrink: 0, touchAction: 'none' }}
          >
            <GripVertical className="row-drag-handle" size={14} color="var(--color-text-2)" style={{ flexShrink: 0 }} />
          </span>
          ) : (
          <span title="Clear filters to reorder" style={{ display: 'flex', flexShrink: 0, opacity: 0.3 }}>
            <GripVertical className="row-drag-handle" size={14} color="var(--color-text-3)" style={{ flexShrink: 0 }} />
          </span>
          )}
          <input
            type="checkbox"
            checked={!!effectiveSelected}
            onChange={e => e.stopPropagation()}
            onClick={e => { e.stopPropagation(); onToggleVariantSelect ? onToggleVariantSelect(e) : onToggleSelect?.(e); }}
            style={{ cursor: 'pointer', accentColor: 'var(--color-primary)', flexShrink: 0, width: 15, height: 15 }}
          />
          <span style={{ color: 'var(--color-text-2)', fontSize: 12, fontWeight: 400, paddingLeft: 20 }}>
            {[variant.option1_value, variant.option2_value, variant.option3_value].filter(Boolean).join(' · ') || variant.sku || ''}
          </span>
        </div>
      </td>
      {/* Column order locked 2026-04-22 — Images → SKU → Price → Compare At → Category → Description → [opt-in] → Review */}
      {colVis.image && (
        <td data-col="image" style={{ padding: '4px 8px', cursor: 'pointer', background: vFlags.some(f => f.flag_type === 'invalid_image_url' && !f.resolved) ? 'var(--color-red-bg)' : undefined }}>
          <ImageCell
            productId={product.id}
            variantId={variant.id}
            images={variantImages}
            onImagesChange={onVariantImagesChange}
            onToast={onToast}
          />
        </td>
      )}
      {/* SKU cell: no red bg, no missingLabel — missing_sku is a non-blocking warning (see 2026-04-22 brief) */}
      <td data-col="sku" data-nav-cell="true" style={{ padding: '4px 8px', fontVariantNumeric: 'tabular-nums', background: vFlags.some(f => f.flag_type === 'duplicate_sku' && !f.resolved) ? 'var(--color-yellow-bg)' : hasLowConf('sku') ? 'var(--color-yellow-bg)' : undefined }}>
        <InlineEdit
          value={variant.sku || ''} onSave={v => onSave('sku', v)}
          placeholder="" expandable={false}
          underline={hasLowConf('sku')}
          renderValue={searchQuery ? highlight(variant.sku || '', searchQuery) : null}
        />
      </td>
      <td data-col="price" data-nav-cell="true" style={{ padding: '4px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', background: vFlags.some(f => (f.flag_type === 'missing_price' || f.flag_type === 'invalid_price') && !f.resolved) ? 'var(--color-red-bg)' : vFlags.some(f => f.flag_type === 'zero_price' && !f.resolved) ? 'var(--color-yellow-bg)' : hasLowConf('price') ? 'var(--color-yellow-bg)' : undefined }}>
        <InlineEdit
          value={variant.price != null ? Number(variant.price).toFixed(2) : ''}
          onSave={v => onSave('price', v?.trim() ? parseFloat(v) || null : null)}
          placeholder="0.00" missingLabel="missing" expandable={false}
          validate={v => v && isNaN(parseFloat(v)) ? 'Must be a number' : null}
          underline={hasLowConf('price')}           textAlign="right"
        />
      </td>
      {colVis.compareAt && (
        <td data-col="compareAt" data-nav-cell="true" style={{ padding: '4px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
          <InlineEdit
            value={variant.compare_at_price != null ? Number(variant.compare_at_price).toFixed(2) : ''}
            onSave={v => onSave('compare_at_price', v?.trim() ? parseFloat(v) || null : null)}
            placeholder="" expandable={false}
            validate={v => v && isNaN(parseFloat(v)) ? 'Must be a number' : null}
            textAlign="right"
          />
        </td>
      )}
      {colVis.productCategory && <td className="cell-na" aria-label="Not applicable" data-col="productCategory" style={{ padding: '4px 8px', cursor: 'default' }}><span aria-hidden="true" style={{ color: 'var(--color-text-3)', userSelect: 'none' }}>—</span></td>}
      {colVis.type       && <td className="cell-na" aria-label="Not applicable" data-col="type"       style={{ padding: '4px 8px', cursor: 'default' }}><span aria-hidden="true" style={{ color: 'var(--color-text-3)', userSelect: 'none' }}>—</span></td>}
      {colVis.body       && <td className="cell-na" aria-label="Not applicable" data-col="body"       style={{ padding: '4px 8px', cursor: 'default', maxWidth: 400 }}><span aria-hidden="true" style={{ color: 'var(--color-text-3)', userSelect: 'none' }}>—</span></td>}
      {colVis.status     && <td className="cell-na" aria-label="Not applicable" data-col="status"     style={{ padding: '4px 8px', cursor: 'default' }}><span aria-hidden="true" style={{ color: 'var(--color-text-3)', userSelect: 'none' }}>—</span></td>}
      {colVis.vendor     && <td className="cell-na" aria-label="Not applicable" data-col="vendor"     style={{ padding: '4px 8px', cursor: 'default' }}><span aria-hidden="true" style={{ color: 'var(--color-text-3)', userSelect: 'none' }}>—</span></td>}
      {colVis.tags       && <td className="cell-na" aria-label="Not applicable" data-col="tags"       style={{ padding: '4px 8px', cursor: 'default' }}><span aria-hidden="true" style={{ color: 'var(--color-text-3)', userSelect: 'none' }}>—</span></td>}
      {colVis.collection && <td className="cell-na" aria-label="Not applicable" data-col="collection" style={{ padding: '4px 8px', cursor: 'default' }}><span aria-hidden="true" style={{ color: 'var(--color-text-3)', userSelect: 'none' }}>—</span></td>}
      {colVis.option1 && <td data-col="option1" data-nav-cell="true" style={{ padding: '4px 8px' }}><InlineEdit value={variant.option1_value || ''} onSave={v => onSave('option1_value', v)} placeholder="" underline={hasLowConf('option1_value')} /></td>}
      {colVis.option2 && <td data-col="option2" data-nav-cell="true" style={{ padding: '4px 8px' }}><InlineEdit value={variant.option2_value || ''} onSave={v => onSave('option2_value', v)} placeholder="" underline={hasLowConf('option2_value')} /></td>}
      {colVis.option3 && <td data-col="option3" data-nav-cell="true" style={{ padding: '4px 8px' }}><InlineEdit value={variant.option3_value || ''} onSave={v => onSave('option3_value', v)} placeholder="" underline={hasLowConf('option3_value')} /></td>}
      {colVis.barcode && (
        <td data-col="barcode" data-nav-cell="true" style={{ padding: '4px 8px' }}>
          <InlineEdit value={variant.barcode || ''} onSave={v => onSave('barcode', v)} placeholder="" expandable={false} />
        </td>
      )}
      {colVis.inventory && (
        <td data-col="inventory" data-nav-cell="true" style={{ padding: '4px 8px' }}>
          <InlineEdit
            value={variant.inventory_qty != null ? String(variant.inventory_qty) : ''}
            onSave={v => onSave('inventory_qty', v ? parseInt(v, 10) || null : null)}
            placeholder="" expandable={false}
            validate={v => v && isNaN(parseInt(v, 10)) ? 'Must be a number' : null}
          />
        </td>
      )}
      {colVis.continueSellingOos && <td data-col="continueSellingOos" onClick={() => onSave('continue_selling_oos', !variant.continue_selling_oos)} style={{ padding: '4px 8px', textAlign: 'center', cursor: 'pointer' }}><input type="checkbox" checked={!!variant.continue_selling_oos} onChange={e => onSave('continue_selling_oos', e.target.checked)} onClick={e => e.stopPropagation()} style={{ cursor: 'pointer', accentColor: 'var(--color-primary)', width: 15, height: 15 }} /></td>}
      {colVis.weight && (
        <td data-col="weight" data-nav-cell="true" style={{ padding: '4px 8px' }}>
          <InlineEdit
            value={variant.weight_grams != null ? String(variant.weight_grams) : ''}
            onSave={v => onSave('weight_grams', v ? parseFloat(v) || null : null)}
            placeholder="" expandable={false}
            validate={v => v && isNaN(parseFloat(v)) ? 'Must be a number' : null}
          />
        </td>
      )}
      {colVis.seoTitle  && <td className="cell-na" aria-label="Not applicable" data-col="seoTitle"  style={{ padding: '4px 8px', cursor: 'default' }}><span aria-hidden="true" style={{ color: 'var(--color-text-3)', userSelect: 'none' }}>—</span></td>}
      {colVis.seoDesc   && <td className="cell-na" aria-label="Not applicable" data-col="seoDesc"   style={{ padding: '4px 8px', cursor: 'default' }}><span aria-hidden="true" style={{ color: 'var(--color-text-3)', userSelect: 'none' }}>—</span></td>}
      <ReviewCell flags={vFlags} onClick={onFlagClick} />
      {customColumns.map(col => (
        <td key={col.id} data-col={`custom-${col.id}`} data-nav-cell="true" style={{ padding: '4px 8px' }}>
          <InlineEdit
            value={(customValues[col.id]?.[variant.id]) || ''}
            onSave={v => onCustomValueChange && onCustomValueChange(col.id, variant.id, v)}
            placeholder=""
          />
        </td>
      ))}
    </tr>
  );
}

// ─── MergedRow ────────────────────────────────────────────────────────────────
function MergedRow({ product, variant, colVis, vFlags, hasLowConf, onSaveProduct, onSaveVariant, onDelete, onFlagClick, flagPanelOpen, searchQuery, isSelected, onToggleSelect, customColumns = [], customValues = {}, onCustomValueChange, isNew = false, staggerDelay = 0, drawerHighlight = false, dndDisabled = false, dragOverId = null, dragOverEdge = 'below', isPartOfMultiDrag = false, productImages = [], onImagesChange, variantImages = [], onVariantImagesChange, onToast, onContextMenu, categorySuggestions = [], catalogTags = [] }) {
  const errFlags = vFlags.filter(f => f.severity === 'error');
  const stripImages = productImages.length > 0 ? productImages : [];
  const rowBg = drawerHighlight ? 'color-mix(in srgb, var(--color-primary-light) 50%, transparent)' : isSelected ? 'var(--color-primary-light)' : flagPanelOpen ? 'var(--color-primary-light)' : 'var(--color-surface)';
  const stickyBg = drawerHighlight ? 'color-mix(in srgb, var(--color-primary-light) 50%, var(--color-surface))' : (isSelected || flagPanelOpen) ? 'color-mix(in srgb, var(--color-primary-light) 80%, var(--color-surface))' : 'var(--color-surface)';
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: product.id, disabled: dndDisabled });
  const dragStyle = {
    transform: isPartOfMultiDrag ? undefined : DndCSS.Transform.toString(transform),
    transition,
    opacity: isPartOfMultiDrag ? 0.2 : 1,
    filter: isPartOfMultiDrag ? 'blur(1px)' : undefined,
    boxShadow: isDragging ? '0 4px 12px rgba(0,0,0,0.15)' : undefined,
    zIndex: isDragging ? 10 : undefined,
    background: isDragging ? 'var(--color-surface)' : undefined,
  };
  const isDropTarget = dragOverId === product.id && !isDragging;
  return (
    <tr ref={setNodeRef} className="var-row merged-row" data-product-id={product.id} data-variant-id={variant.id} data-status={variant.row_status} onContextMenu={onContextMenu} style={{ ...dragStyle, background: rowBg, animation: isNew ? `rowEntry 200ms cubic-bezier(0.16,1,0.3,1) ${staggerDelay}ms both` : undefined, boxShadow: isDropTarget ? (dragOverEdge === 'above' ? 'inset 0 2px 0 var(--color-primary)' : 'inset 0 -2px 0 var(--color-primary)') : undefined }}>
      <td data-col="title" className="sticky-col" data-nav-cell="true" style={{ padding: '4px 8px 4px 12px', width: 300, maxWidth: 300, ...stickyTdStyle(0, true, { background: vFlags.some(f => f.flag_type === 'missing_title' && !f.resolved) ? 'var(--color-red-bg)' : stickyBg }), boxShadow: isDropTarget ? (dragOverEdge === 'above' ? 'inset 0 2px 0 var(--color-primary)' : 'inset 0 -2px 0 var(--color-primary)') : undefined }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {!dndDisabled ? (
          <span
            {...attributes}
            {...listeners}
            style={{ cursor: isDragging ? 'grabbing' : 'grab', display: 'flex', flexShrink: 0, touchAction: 'none' }}
          >
            <GripVertical className="row-drag-handle" size={14} color="var(--color-text-2)" style={{ flexShrink: 0 }} />
          </span>
          ) : (
          <span title="Clear filters to reorder" style={{ display: 'flex', flexShrink: 0, opacity: 0.3 }}>
            <GripVertical className="row-drag-handle" size={14} color="var(--color-text-3)" style={{ flexShrink: 0 }} />
          </span>
          )}
          <input type="checkbox" checked={isSelected} onChange={e => e.stopPropagation()} onClick={e => { e.stopPropagation(); onToggleSelect(e); }} style={{ cursor: 'pointer', flexShrink: 0, width: 15, height: 15 }} />
          <InlineEdit
              value={product.title || ''} onSave={v => onSaveProduct('title', v)}
              placeholder="Product title" missingLabel="missing"
              extraStyle={{ fontWeight: 400 }}
              renderValue={searchQuery ? highlight(product.title || '', searchQuery) : (product.title || null)}
            />
        </div>
      </td>
      {/* Column order locked 2026-04-22 — Images → SKU → Price → Compare At → Category → Description → [opt-in] → Review */}
      {colVis.image && (
        <td data-col="image" style={{ padding: '4px 8px', cursor: 'pointer', background: vFlags.some(f => f.flag_type === 'invalid_image_url' && !f.resolved) ? 'var(--color-red-bg)' : undefined }}>
          <ImageCell
            productId={product.id}
            variantId={variant.id}
            images={variantImages}
            onImagesChange={onVariantImagesChange}
            onToast={onToast}
          />
        </td>
      )}
      {/* SKU cell: no red bg, no missingLabel — missing_sku is a non-blocking warning (see 2026-04-22 brief) */}
      <td data-col="sku" data-nav-cell="true" style={{ padding: '4px 8px', fontVariantNumeric: 'tabular-nums', background: vFlags.some(f => f.flag_type === 'duplicate_sku' && !f.resolved) ? 'var(--color-yellow-bg)' : hasLowConf('sku') ? 'var(--color-yellow-bg)' : undefined }}>
        <InlineEdit
          value={variant.sku || ''} onSave={v => onSaveVariant('sku', v)}
          placeholder="" expandable={false} underline={hasLowConf('sku')}
          renderValue={searchQuery ? highlight(variant.sku || '', searchQuery) : null}
        />
      </td>
      <td data-col="price" data-nav-cell="true" style={{ padding: '4px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums', background: vFlags.some(f => (f.flag_type === 'missing_price' || f.flag_type === 'invalid_price') && !f.resolved) ? 'var(--color-red-bg)' : vFlags.some(f => f.flag_type === 'zero_price' && !f.resolved) ? 'var(--color-yellow-bg)' : hasLowConf('price') ? 'var(--color-yellow-bg)' : undefined }}>
        <InlineEdit
          value={variant.price != null ? Number(variant.price).toFixed(2) : ''}
          onSave={v => onSaveVariant('price', v?.trim() ? parseFloat(v) || null : null)}
          placeholder="0.00" missingLabel="missing" expandable={false}
          validate={v => v && isNaN(parseFloat(v)) ? 'Must be a number' : null}
          underline={hasLowConf('price')}           textAlign="right"
        />
      </td>
      {colVis.compareAt && (
        <td data-col="compareAt" data-nav-cell="true" style={{ padding: '4px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
          <InlineEdit
            value={variant.compare_at_price != null ? Number(variant.compare_at_price).toFixed(2) : ''}
            onSave={v => onSaveVariant('compare_at_price', v?.trim() ? parseFloat(v) || null : null)}
            placeholder="" expandable={false}
            validate={v => v && isNaN(parseFloat(v)) ? 'Must be a number' : null}
            textAlign="right"
          />
        </td>
      )}
      {colVis.productCategory && <td data-col="productCategory" data-nav-cell="true" style={{ padding: '4px 8px', cursor: 'pointer', background: vFlags.some(f => !f.resolved && (f.flag_type === 'missing_category' || f.flag_type === 'category_missing' || f.flag_type === 'category_outlier' || f.flag_type === 'category_uncertain')) ? 'var(--color-yellow-bg)' : undefined }}><CategoryPicker value={product.product_category || ''} categoryId={product.product_category_id || ''} onSelect={(gid, name) => { onSaveProduct('product_category', name); onSaveProduct('product_category_id', gid); }} suggestions={categorySuggestions} /></td>}
      {colVis.type       && <td data-col="type"       data-nav-cell="true" style={{ padding: '4px 8px' }}><InlineEdit value={product.product_type || ''} onSave={v => onSaveProduct('product_type', v)} placeholder="" renderValue={searchQuery ? highlight(product.product_type || '', searchQuery) : null} /></td>}
      {colVis.body       && <td data-col="body"       data-nav-cell="true" style={{ padding: '4px 8px', cursor: 'pointer', maxWidth: 400 }}><DescriptionEdit value={product.description || ''} onSave={v => onSaveProduct('description', v)} /></td>}
      {colVis.status     && <td data-col="status"     data-nav-cell="true" style={{ padding: '4px 8px', cursor: 'pointer' }}><StatusCell value={product.status || 'draft'} onSave={v => onSaveProduct('status', v)} /></td>}
      {colVis.vendor     && <td data-col="vendor"     data-nav-cell="true" style={{ padding: '4px 8px' }}><InlineEdit value={product.vendor || ''} onSave={v => onSaveProduct('vendor', v)} placeholder="" renderValue={searchQuery ? highlight(product.vendor || '', searchQuery) : null} /></td>}
      {colVis.tags       && <td data-col="tags"       data-nav-cell="true" style={{ padding: '4px 8px', cursor: 'pointer' }}><TagChipInput value={mergeRenderTags(product)} onSave={v => onSaveProduct('tags', v)} catalogTags={catalogTags} /></td>}
      {colVis.collection && <td data-col="collection" data-nav-cell="true" style={{ padding: '4px 8px' }}><InlineEdit value={product.collection || ''} onSave={v => onSaveProduct('collection', v)} placeholder="" /></td>}
      {colVis.option1    && <td data-col="option1"   data-nav-cell="true" style={{ padding: '4px 8px' }}><InlineEdit value={variant.option1_value || ''} onSave={v => onSaveVariant('option1_value', v)} placeholder="" /></td>}
      {colVis.option2    && <td data-col="option2"   data-nav-cell="true" style={{ padding: '4px 8px' }}><InlineEdit value={variant.option2_value || ''} onSave={v => onSaveVariant('option2_value', v)} placeholder="" /></td>}
      {colVis.option3    && <td data-col="option3"   data-nav-cell="true" style={{ padding: '4px 8px' }}><InlineEdit value={variant.option3_value || ''} onSave={v => onSaveVariant('option3_value', v)} placeholder="" /></td>}
      {colVis.barcode    && <td data-col="barcode"   data-nav-cell="true" style={{ padding: '4px 8px' }}><InlineEdit value={variant.barcode || ''} onSave={v => onSaveVariant('barcode', v)} placeholder="" expandable={false} /></td>}
      {colVis.inventory  && <td data-col="inventory" data-nav-cell="true" style={{ padding: '4px 8px' }}><InlineEdit value={variant.inventory_qty != null ? String(variant.inventory_qty) : ''} onSave={v => onSaveVariant('inventory_qty', v ? parseInt(v, 10) || null : null)} placeholder="" expandable={false} validate={v => v && isNaN(parseInt(v, 10)) ? 'Must be a number' : null} /></td>}
      {colVis.continueSellingOos && <td data-col="continueSellingOos" onClick={() => onSaveVariant('continue_selling_oos', !variant.continue_selling_oos)} style={{ padding: '4px 8px', textAlign: 'center', cursor: 'pointer' }}><input type="checkbox" checked={!!variant.continue_selling_oos} onChange={e => onSaveVariant('continue_selling_oos', e.target.checked)} onClick={e => e.stopPropagation()} style={{ cursor: 'pointer', accentColor: 'var(--color-primary)', width: 15, height: 15 }} /></td>}
      {colVis.weight     && <td data-col="weight"    data-nav-cell="true" style={{ padding: '4px 8px' }}><InlineEdit value={variant.weight_grams != null ? String(variant.weight_grams) : ''} onSave={v => onSaveVariant('weight_grams', v ? parseFloat(v) || null : null)} placeholder="" expandable={false} validate={v => v && isNaN(parseFloat(v)) ? 'Must be a number' : null} /></td>}
      {colVis.seoTitle   && <td data-col="seoTitle"  data-nav-cell="true" style={{ padding: '4px 8px' }}><InlineEdit value={product.seo_title || ''} onSave={v => onSaveProduct('seo_title', v)} placeholder="" expandable={false} /></td>}
      {colVis.seoDesc    && <td data-col="seoDesc"   data-nav-cell="true" style={{ padding: '4px 8px' }}><InlineEdit value={product.seo_description || ''} onSave={v => onSaveProduct('seo_description', v)} placeholder="" expandable={false} /></td>}
      <ReviewCell flags={vFlags} onClick={onFlagClick} />
      {customColumns.map(col => (
        <td key={col.id} data-col={`custom-${col.id}`} data-nav-cell="true" style={{ padding: '4px 8px' }}>
          <InlineEdit
            value={(customValues[col.id]?.[variant.id]) || ''}
            onSave={v => onCustomValueChange && onCustomValueChange(col.id, variant.id, v)}
            placeholder=""
          />
        </td>
      ))}
    </tr>
  );
}

// ─── FlatRow (placeholder — not used) ─────────────────────────────────────────
function FlatRow({ product, variants, colVis, allFlags, hasLowConf, onSaveProduct, onSaveVariant, onDelete, onFlagClick, flagPanelOpen, searchQuery, isSelected, onToggleSelect, customColumns = [], customValues = {}, onCustomValueChange, isNew = false, staggerDelay = 0, drawerHighlight = false, onToggle, isExpanded, productImages = [], onImagesChange, variantImages = [], onVariantImagesChange, onToast, onContextMenu }) {
  const v0 = variants[0];
  const errFlags = allFlags.filter(f => f.severity === 'error');
  const isMulti = variants.length > 1;
  const stickyBg = drawerHighlight ? 'color-mix(in srgb, var(--color-primary-light) 50%, var(--color-surface))' : (isSelected || flagPanelOpen) ? 'color-mix(in srgb, var(--color-primary-light) 80%, var(--color-surface))' : 'var(--color-surface)';
  return (
    <tr className="var-row flat-row" data-variant-id={v0.id} data-status={v0.row_status} onContextMenu={onContextMenu} style={{ background: drawerHighlight ? 'color-mix(in srgb, var(--color-primary-light) 50%, transparent)' : isSelected ? 'var(--color-primary-light)' : flagPanelOpen ? 'var(--color-primary-light)' : 'var(--color-surface)', animation: isNew ? `rowEntry 200ms cubic-bezier(0.16,1,0.3,1) ${staggerDelay}ms both` : undefined }}>
      <td data-col="title" data-nav-cell="true" className="sticky-col" style={{ padding: '4px 8px 4px 12px', width: 300, maxWidth: 300, ...stickyTdStyle(0, true, { background: allFlags.some(f => f.flag_type === 'missing_title' && !f.resolved) ? 'var(--color-red-bg)' : stickyBg }) }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <GripVertical className="row-drag-handle" size={14} color="var(--color-text-2)" style={{ flexShrink: 0 }} />
          <input type="checkbox" checked={isSelected} onChange={e => e.stopPropagation()} onClick={e => { e.stopPropagation(); onToggleSelect(e); }} style={{ cursor: 'pointer', flexShrink: 0, width: 15, height: 15 }} />
          <InlineEdit
              value={product.title || ''} onSave={v => onSaveProduct('title', v)}
              placeholder="Product title" missingLabel="missing"
              extraStyle={{ fontWeight: 400 }}
              renderValue={searchQuery ? highlight(product.title || '', searchQuery) : (product.title || null)}
            />
          {isMulti ? (
            <button
              className="expand-chevron"
              data-expanded={!!isExpanded}
              onClick={e => { e.stopPropagation(); onToggle(); }}
              aria-label={isExpanded ? 'Collapse variants' : `Expand ${variants.length} variants`}
            >
              <span className="variant-count">{variants.length} variant{variants.length !== 1 ? 's' : ''}</span>
              <ChevronDown size={14} />
            </button>
          ) : null}
        </div>
      </td>
      {/* Column order locked 2026-04-22 — Images → SKU → Price → Compare At → Category → Description → [opt-in] → Review */}
      {colVis.image && (
        <td data-col="image" style={{ padding: '4px 8px', cursor: 'pointer', background: allFlags.some(f => f.flag_type === 'invalid_image_url' && !f.resolved) ? 'var(--color-red-bg)' : undefined }}>
          <ImageCell
            productId={product.id}
            variantId={v0.id}
            images={variantImages}
            onImagesChange={onVariantImagesChange}
            onToast={onToast}
          />
        </td>
      )}
      <td data-col="sku" style={{ padding: '4px 8px', fontVariantNumeric: 'tabular-nums' }}>
        <InlineEdit
          value={v0.sku || ''} onSave={v => onSaveVariant('sku', v)}
          placeholder="" expandable={false} underline={hasLowConf('sku')}
          renderValue={searchQuery ? highlight(v0.sku || '', searchQuery) : null}
        />
      </td>
      <td data-col="price" style={{ padding: '4px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
        <InlineEdit
          value={v0.price != null ? Number(v0.price).toFixed(2) : ''}
          onSave={v => onSaveVariant('price', v?.trim() ? parseFloat(v) || null : null)}
          placeholder="0.00" missingLabel="missing" expandable={false}
          validate={v => v && isNaN(parseFloat(v)) ? 'Must be a number' : null}
          underline={hasLowConf('price')}           textAlign="right"
        />
      </td>
      {colVis.compareAt && (
        <td style={{ padding: '4px 8px', textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
          <InlineEdit
            value={v0.compare_at_price != null ? Number(v0.compare_at_price).toFixed(2) : ''}
            onSave={v => onSaveVariant('compare_at_price', v?.trim() ? parseFloat(v) || null : null)}
            placeholder="" expandable={false}
            validate={v => v && isNaN(parseFloat(v)) ? 'Must be a number' : null}
            textAlign="right"
          />
        </td>
      )}
      {colVis.productCategory && <td style={{ padding: '4px 8px', cursor: 'pointer', background: allFlags.some(f => !f.resolved && (f.flag_type === 'missing_category' || f.flag_type === 'category_missing' || f.flag_type === 'category_outlier' || f.flag_type === 'category_uncertain')) ? 'var(--color-yellow-bg)' : undefined }}><CategoryPicker value={product.product_category || ''} categoryId={product.product_category_id || ''} onSelect={(gid, name) => { onSaveProduct('product_category', name); onSaveProduct('product_category_id', gid); }} suggestions={categorySuggestions} /></td>}
      {colVis.type       && <td style={{ padding: '4px 8px' }}><InlineEdit value={product.product_type || ''} onSave={v => onSaveProduct('product_type', v)} placeholder="" renderValue={searchQuery ? highlight(product.product_type || '', searchQuery) : null} /></td>}
      {colVis.body       && <td style={{ padding: '4px 8px', cursor: 'pointer', maxWidth: 400 }}><DescriptionEdit value={product.description || ''} onSave={v => onSaveProduct('description', v)} /></td>}
      {colVis.status     && <td style={{ padding: '4px 8px', cursor: 'pointer' }}><StatusCell value={product.status || 'draft'} onSave={v => onSaveProduct('status', v)} /></td>}
      {colVis.vendor     && <td style={{ padding: '4px 8px' }}><InlineEdit value={product.vendor || ''} onSave={v => onSaveProduct('vendor', v)} placeholder="" renderValue={searchQuery ? highlight(product.vendor || '', searchQuery) : null} /></td>}
      {colVis.tags       && <td style={{ padding: '4px 8px', cursor: 'pointer' }}><TagChipInput value={mergeRenderTags(product)} onSave={v => onSaveProduct('tags', v)} catalogTags={catalogTags} /></td>}
      {colVis.collection && <td style={{ padding: '4px 8px' }}><InlineEdit value={product.collection || ''} onSave={v => onSaveProduct('collection', v)} placeholder="" /></td>}
      {colVis.option1    && <td style={{ padding: '4px 8px' }}><InlineEdit value={v0.option1_value || ''} onSave={v => onSaveVariant('option1_value', v)} placeholder="" /></td>}
      {colVis.option2    && <td style={{ padding: '4px 8px' }}><InlineEdit value={v0.option2_value || ''} onSave={v => onSaveVariant('option2_value', v)} placeholder="" /></td>}
      {colVis.option3    && <td style={{ padding: '4px 8px' }}><InlineEdit value={v0.option3_value || ''} onSave={v => onSaveVariant('option3_value', v)} placeholder="" /></td>}
      {colVis.barcode    && <td style={{ padding: '4px 8px' }}><InlineEdit value={v0.barcode || ''} onSave={v => onSaveVariant('barcode', v)} placeholder="" expandable={false} /></td>}
      {colVis.inventory  && <td style={{ padding: '4px 8px' }}><InlineEdit value={v0.inventory_qty != null ? String(v0.inventory_qty) : ''} onSave={v => onSaveVariant('inventory_qty', v ? parseInt(v, 10) || null : null)} placeholder="" expandable={false} validate={v => v && isNaN(parseInt(v, 10)) ? 'Must be a number' : null} /></td>}
      {colVis.continueSellingOos && <td onClick={() => onSaveVariant('continue_selling_oos', !v0.continue_selling_oos)} style={{ padding: '4px 8px', textAlign: 'center', cursor: 'pointer' }}><input type="checkbox" checked={!!v0.continue_selling_oos} onChange={e => onSaveVariant('continue_selling_oos', e.target.checked)} onClick={e => e.stopPropagation()} style={{ cursor: 'pointer', accentColor: 'var(--color-primary)', width: 15, height: 15 }} /></td>}
      {colVis.weight     && <td style={{ padding: '4px 8px' }}><InlineEdit value={v0.weight_grams != null ? String(v0.weight_grams) : ''} onSave={v => onSaveVariant('weight_grams', v ? parseFloat(v) || null : null)} placeholder="" expandable={false} validate={v => v && isNaN(parseFloat(v)) ? 'Must be a number' : null} /></td>}
      {colVis.seoTitle   && <td style={{ padding: '4px 8px' }}><InlineEdit value={product.seo_title || ''} onSave={v => onSaveProduct('seo_title', v)} placeholder="" expandable={false} /></td>}
      {colVis.seoDesc    && <td style={{ padding: '4px 8px' }}><InlineEdit value={product.seo_description || ''} onSave={v => onSaveProduct('seo_description', v)} placeholder="" expandable={false} /></td>}
      <ReviewCell flags={allFlags} onClick={onFlagClick} />
      {customColumns.map(col => (
        <td key={col.id} style={{ padding: '4px 8px' }}>
          <InlineEdit
            value={(customValues[col.id]?.[v0.id]) || ''}
            onSave={v => onCustomValueChange && onCustomValueChange(col.id, v0.id, v)}
            placeholder=""
          />
        </td>
      ))}
    </tr>
  );
}

// ─── TH helper ────────────────────────────────────────────────────────────────
function TH({ children, style, sticky = false, stickyLeft = null, isTitle = false, title, onClick, 'aria-sort': ariaSort, sortColumn, columnSort, cycleColumnSort, resizable = false }) {
  const isSortable = !!sortColumn;
  const isActive = isSortable && columnSort?.column === sortColumn;
  const handleClick = isSortable ? (e) => { if (onClick) onClick(e); cycleColumnSort(sortColumn); } : onClick;
  const thRef = React.useRef(null);

  const handleResizeMouseDown = React.useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    const th = thRef.current;
    if (!th) return;
    const row = th.parentElement;
    if (!row) return;
    const table = th.closest('table');
    if (!table) return;
    const allThs = Array.from(row.querySelectorAll('th'));
    const startX = e.clientX;
    const startW = th.offsetWidth;
    // Freeze every column to its current rendered width
    allThs.forEach(col => {
      const w = col.offsetWidth + 'px';
      col.style.width = w;
      col.style.minWidth = w;
      col.style.maxWidth = w;
      col.style.overflow = 'hidden';
    });
    // Switch to fixed layout so only TH widths determine column sizes (TDs ignored)
    table.style.tableLayout = 'fixed';
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    const MIN_COL_W = 36;
    let didMove = false;
    function onMove(ev) {
      didMove = true;
      const newW = Math.max(startW + (ev.clientX - startX), MIN_COL_W);
      th.style.width = newW + 'px';
      th.style.minWidth = newW + 'px';
      th.style.maxWidth = newW + 'px';
    }
    function onUp() {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      // Keep fixed layout + locked column widths so other columns don't shift.
      // Set table width to auto — whitespace appears on the right when total < viewport.
      table.style.width = 'auto';
      // Keep overflow:hidden on THs — content must not push columns wider under fixed layout
      // Suppress the click that follows mouseup so sort doesn't fire
      if (didMove) {
        const suppress = (ev) => { ev.stopPropagation(); ev.preventDefault(); };
        th.addEventListener('click', suppress, { capture: true, once: true });
      }
    }
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  }, []);

  return (
    <th ref={thRef} title={title} onClick={handleClick} aria-sort={ariaSort || (isActive ? (columnSort.direction === 'asc' ? 'ascending' : 'descending') : undefined)} style={{
      padding: '8px 10px 8px 14px', textAlign: 'left', fontSize: 12,
      fontWeight: 600, color: 'var(--color-text)',
      background: 'var(--color-surface-2)', whiteSpace: 'nowrap',
      userSelect: 'none', borderBottom: '1px solid var(--color-border)',
      borderRight: '1px solid var(--color-border)',
      position: 'sticky',
      ...(isSortable ? { cursor: 'pointer' } : {}),
      ...(sticky ? {
        left: stickyLeft !== null ? stickyLeft : 0,
        zIndex: 12,
        background: 'var(--color-surface-2)',
      } : {}),
      ...style,
    }}>
      {isSortable ? (
        <span className="sortable-th" style={{ display: 'inline-flex', alignItems: 'center', gap: 2 }}>
          {children}
          <span className={`sort-icon${isActive ? ' sort-active' : ''}`}>
            {isActive && columnSort.direction === 'asc'
              ? <ArrowUp size={12} />
              : <ArrowDown size={12} />}
          </span>
        </span>
      ) : children}
      {resizable && (
        <div
          className="th-resize-handle"
          onMouseDown={handleResizeMouseDown}
          onClick={(ev) => { ev.stopPropagation(); ev.preventDefault(); }}
        />
      )}
    </th>
  );
}

// ─── Sticky TD helper ─────────────────────────────────────────────────────────
function stickyTdStyle(left, isTitle = false, extra = {}) {
  return {
    position: 'sticky',
    left,
    zIndex: 2,
    background: 'inherit',
    ...(isTitle ? { borderRight: '1px solid var(--color-border)' } : {}),
    ...extra,
  };
}

// ─── TagChipInput ─────────────────────────────────────────────────────────────
function TagChipInput({ value, onSave, catalogTags = [] }) {
  const [chips, setChips]       = useState(() => value ? String(value).split(/,\s*/).filter(Boolean) : []);
  const [inputVal, setInputVal] = useState('');
  const [open, setOpen]         = useState(false);
  const [highlightIdx, setHighlightIdx] = useState(-1);
  const inputRef                = useRef(null);  // real typing input (inside overlay, open only)
  const overlayRef              = useRef(null);
  const committed               = useRef(value ?? '');

  useEffect(() => {
    setChips(value ? String(value).split(/,\s*/).filter(Boolean) : []);
    committed.current = value ?? '';
  }, [value]);

  // Close on window blur
  useEffect(() => {
    if (!open) return;
    function handleBlur() {
      if (inputVal.trim()) addChip(inputVal);
      const finalChips = inputVal.trim() ? [...chips, inputVal.trim()] : chips;
      commit(finalChips);
      setInputVal('');
      setOpen(false);
    }
    window.addEventListener('blur', handleBlur);
    return () => window.removeEventListener('blur', handleBlur);
  }, [open, inputVal, chips]);

  // Register an imperative open+focus function on the overlay DOM node so the table's
  // keydown handler can call it directly without going through React's event system.
  // useLayoutEffect (not useEffect) is critical: it runs synchronously after React commits,
  // so _chipOpen is always registered before any caller following a flushSync() returns.
  // useEffect fires after paint — too late if a preceding flushSync caused a re-render.
  useLayoutEffect(() => {
    const el = overlayRef.current;
    if (!el) return;
    el._chipOpen = () => {
      flushSync(() => setOpen(true));
      inputRef.current?.focus();
    };
    return () => { delete el._chipOpen; };
  });

  function addChip(raw) {
    const trimmed = raw.trim();
    if (!trimmed) return;
    setChips(prev => [...prev, trimmed]);
    setInputVal('');
  }

  function removeChip(idx) {
    setChips(prev => prev.filter((_, i) => i !== idx));
  }

  function commit(finalChips) {
    const joined = finalChips.join(', ');
    if (joined !== committed.current) { committed.current = joined; onSave(joined || null); }
  }

  // All catalog tags with checked state — applied tags stay visible (Shopify pattern)
  const suggestions = useMemo(() => {
    const lowerChips = new Set(chips.map(c => c.toLowerCase()));
    const all = catalogTags.map(t => ({ tag: t, checked: lowerChips.has(t.toLowerCase()) }));
    if (!inputVal.trim()) return all;
    const q = inputVal.trim().toLowerCase();
    return all.filter(item => item.tag.toLowerCase().includes(q));
  }, [catalogTags, chips, inputVal]);

  // Show "Add [typed text]" when input doesn't match any existing catalog tag
  const showAddOption = useMemo(() => {
    if (!inputVal.trim()) return false;
    const q = inputVal.trim().toLowerCase();
    return !catalogTags.some(t => t.toLowerCase() === q);
  }, [catalogTags, inputVal]);

  function toggleSuggestion(tag) {
    const lowerChips = new Set(chips.map(c => c.toLowerCase()));
    if (lowerChips.has(tag.toLowerCase())) {
      setChips(prev => prev.filter(c => c.toLowerCase() !== tag.toLowerCase()));
    } else {
      setChips(prev => [...prev, tag]);
    }
    setInputVal('');
    setHighlightIdx(-1);
    inputRef.current?.focus();
  }

  function addNewTag() {
    const trimmed = inputVal.trim();
    if (!trimmed) return;
    setChips(prev => [...prev, trimmed]);
    setInputVal('');
    setHighlightIdx(-1);
    inputRef.current?.focus();
  }

  function handleKeyDown(e) {
    // Total items: "Add [tag]" option (if shown) + suggestions
    const totalItems = (showAddOption ? 1 : 0) + suggestions.length;
    if (e.key === 'ArrowDown' && totalItems > 0) {
      e.preventDefault();
      setHighlightIdx(i => Math.min(i + 1, totalItems - 1));
    } else if (e.key === 'ArrowUp' && totalItems > 0) {
      e.preventDefault();
      setHighlightIdx(i => Math.max(i - 1, -1));
    } else if (e.key === 'Enter') {
      e.preventDefault(); e.stopPropagation();
      if (highlightIdx >= 0 && highlightIdx < totalItems) {
        if (showAddOption && highlightIdx === 0) {
          addNewTag();
        } else {
          const suggIdx = showAddOption ? highlightIdx - 1 : highlightIdx;
          toggleSuggestion(suggestions[suggIdx].tag);
        }
      } else if (inputVal.trim()) {
        addChip(inputVal);
      }
    } else if (e.key === ',') {
      e.preventDefault(); e.stopPropagation(); addChip(inputVal);
    } else if (e.key === 'Backspace' && inputVal === '') {
      setChips(prev => prev.length === 0 ? prev : prev.slice(0, -1));
    } else if (e.key === 'Escape') {
      e.stopPropagation();
      setChips(value ? String(value).split(/,\s*/).filter(Boolean) : []);
      setInputVal('');
      setOpen(false);
      committed.current = value ?? '';
    }
  }

  function handleBlur(e) {
    // Stay open when focus moves to a chip's ✕ button or suggestion dropdown (inside overlay)
    if (overlayRef.current && overlayRef.current.contains(e.relatedTarget)) return;
    if (inputVal.trim()) addChip(inputVal);
    setOpen(false);
    const finalChips = inputVal.trim() ? [...chips, inputVal.trim()] : chips;
    commit(finalChips);
    setInputVal('');
    setHighlightIdx(-1);
  }

  // Opens overlay and focuses typing input — called on click and via Tab nav (.click() dispatch).
  // flushSync forces React to synchronously commit setOpen(true) so inputRef.current is set
  // before focus() is called — avoids the 0ms setTimeout race with React's scheduler.
  function handleOpen(e) {
    e.stopPropagation();
    flushSync(() => setOpen(true));
    inputRef.current?.focus();
  }

  const chipStyle = {
    display: 'inline-flex', alignItems: 'center', gap: 3,
    background: 'var(--color-surface-2)',
    color: 'var(--color-text)',
    border: 'none',
    borderRadius: 8,
    fontSize: 12, fontWeight: 500, padding: '1px 6px',
    whiteSpace: 'nowrap', flexShrink: 0,
    maxWidth: 120, overflow: 'hidden',
  };
  const chipLabelStyle = {
    flex: '1 1 auto', minWidth: 0,
    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
  };

  const xStyle = {
    background: 'none', border: 'none', padding: '0 1px',
    cursor: 'pointer', color: 'var(--color-text-2)',
    fontSize: 12, lineHeight: 1, display: 'flex', alignItems: 'center',
    opacity: open ? 1 : 0, pointerEvents: open ? 'auto' : 'none',
    transition: 'opacity 0.1s ease',
  };

  const MAX_VISIBLE = 2;
  const overflow = chips.length > MAX_VISIBLE ? chips.length - MAX_VISIBLE : 0;
  const overflowPillStyle = {
    ...chipStyle,
    background: 'var(--color-surface-2)',
    color: 'var(--color-text)',
    border: 'none',
    fontWeight: 500,
    cursor: 'pointer',
  };

  return (
    <div style={{ position: 'relative', minWidth: 0, width: '100%' }}>
      {/* Shadow — mirrors resting overlay exactly (same truncation + pill) to pin row height */}
      <div style={{
        display: 'flex', flexWrap: 'nowrap', alignItems: 'center',
        gap: 3, padding: '3px 6px',
        border: '1px solid transparent', boxSizing: 'border-box',
        visibility: 'hidden', pointerEvents: 'none',
      }}>
        {chips.length === 0
          ? <span style={{ fontSize: 13 }}>&nbsp;</span>
          : <>
              <div style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center', gap: 3, flex: '0 1 auto', overflow: 'hidden', minWidth: 0 }}>
                {chips.slice(0, MAX_VISIBLE).map((chip, i) => (
                  <span key={i} style={chipStyle}>
                    <span style={chipLabelStyle}>{chip}</span>
                    <button style={xStyle} tabIndex={-1}><X size={12} /></button>
                  </span>
                ))}
              </div>
              {overflow > 0 && <span style={{ ...overflowPillStyle, flexShrink: 0 }}>+{overflow}</span>}
            </>
        }
      </div>

      {/* Overlay — chips + typing input (when open) as natural flex-wrap row.
          Input is the last flex item so it always appears after chips, never on top of them.
          data-chip-overlay lets focusInput() dispatch a click here for Tab navigation. */}
      <div
        ref={overlayRef}
        data-chip-overlay
        onClick={handleOpen}
        style={{
          position: 'absolute', top: 0, left: 0, right: 0,
          display: 'flex', flexWrap: open ? 'wrap' : 'nowrap', alignItems: 'center',
          gap: 3,
          padding: '3px 6px',
          cursor: 'pointer',
          borderRadius: 'var(--r-sm)',
          border: open ? '1.5px solid var(--color-primary)' : '1px solid transparent',
          boxShadow: 'none',
          background: open ? 'var(--color-surface)' : 'transparent',
          transition: 'border-color 0.12s, background 0.12s',
          zIndex: open ? 10 : 1,
          minHeight: '100%',
          boxSizing: 'border-box',
        }}
      >
        {chips.length === 0 && !open && (
          <span style={{ fontSize: 13, color: 'transparent', pointerEvents: 'none' }}>&nbsp;</span>
        )}
        {open
          ? chips.map((chip, i) => (
              <span key={i} style={chipStyle}>
                <span style={chipLabelStyle}>{chip}</span>
                <button
                  style={xStyle}
                  onMouseDown={e => e.preventDefault()}
                  onClick={e => { e.stopPropagation(); removeChip(i); }}
                  tabIndex={-1}
                  aria-label={`Remove ${chip}`}
                ><X size={12} /></button>
              </span>
            ))
          : <>
              <div style={{ display: 'flex', flexWrap: 'nowrap', alignItems: 'center', gap: 3, flex: '0 1 auto', overflow: 'hidden', minWidth: 0 }}>
                {chips.slice(0, MAX_VISIBLE).map((chip, i) => (
                  <span key={i} style={chipStyle}>
                    <span style={chipLabelStyle}>{chip}</span>
                    <button
                      style={xStyle}
                      onMouseDown={e => e.preventDefault()}
                      onClick={e => { e.stopPropagation(); removeChip(i); const updated = chips.filter((_, idx) => idx !== i); commit(updated); }}
                      tabIndex={-1}
                      aria-label={`Remove ${chip}`}
                    ><X size={12} /></button>
                  </span>
                ))}
              </div>
              {overflow > 0 && <span style={{ ...overflowPillStyle, flexShrink: 0 }}>+{overflow}</span>}
            </>
        }
        {/* Typing input — last flex item so it always sits after chips, never overlapping them */}
        {open && (
          <input
            ref={inputRef}
            type="text"
            value={inputVal}
            onChange={e => { setInputVal(e.target.value); setHighlightIdx(-1); }}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            onClick={e => e.stopPropagation()}
            tabIndex={-1}
            style={{
              flex: '1 1 60px', minWidth: 60,
              border: 'none', outline: 'none', background: 'transparent',
              fontSize: 13, padding: '1px 2px', cursor: 'text',
            }}
            placeholder="Search or add tags"
          />
        )}
      </div>

      {/* Suggestions dropdown — Shopify checkbox pattern */}
      {open && (suggestions.length > 0 || showAddOption) && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, minWidth: 200, zIndex: 200,
          marginTop: 4, maxHeight: 240, overflowY: 'auto',
          background: 'var(--color-surface)', border: '1px solid var(--color-border)',
          borderRadius: 'var(--r-sm)', boxShadow: 'var(--shadow-card)',
        }}>
          {/* "Add [typed text]" option */}
          {showAddOption && (
            <button
              onMouseDown={e => e.preventDefault()}
              onClick={e => { e.stopPropagation(); addNewTag(); }}
              onMouseEnter={() => setHighlightIdx(0)}
              style={{
                display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left',
                padding: '7px 12px', fontSize: 13, cursor: 'pointer',
                border: 'none', borderBottom: suggestions.length > 0 ? '1px solid var(--color-border)' : 'none',
                background: highlightIdx === 0 ? 'var(--color-surface-2)' : 'transparent',
                color: 'var(--color-primary)', fontWeight: 500, lineHeight: 1.4,
              }}
            >
              Add "{inputVal.trim()}"
            </button>
          )}
          {!inputVal.trim() && suggestions.length > 0 && (
            <div style={{ padding: '6px 12px 4px', fontSize: 11, fontWeight: 600, color: 'var(--color-text-3)', textTransform: 'uppercase', letterSpacing: '0.03em' }}>
              Used in catalog
            </div>
          )}
          {suggestions.map((item, idx) => {
            const itemIdx = showAddOption ? idx + 1 : idx;
            return (
              <button
                key={item.tag}
                onMouseDown={e => e.preventDefault()}
                onClick={e => { e.stopPropagation(); toggleSuggestion(item.tag); }}
                onMouseEnter={() => setHighlightIdx(itemIdx)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 8, width: '100%', textAlign: 'left',
                  padding: '7px 12px', fontSize: 13, cursor: 'pointer',
                  border: 'none', borderBottom: idx < suggestions.length - 1 ? '1px solid var(--color-border)' : 'none',
                  background: itemIdx === highlightIdx ? 'var(--color-surface-2)' : 'transparent',
                  color: 'var(--color-text)', lineHeight: 1.4,
                }}
              >
                <span style={{
                  display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                  width: 16, height: 16, flexShrink: 0,
                  borderRadius: 3,
                  border: item.checked ? 'none' : '1.5px solid var(--color-border-strong)',
                  background: item.checked ? 'var(--color-primary)' : 'transparent',
                  color: '#fff', fontSize: 11,
                }}>
                  {item.checked && <Check size={11} style={{ color: '#fff' }} />}
                </span>
                <span style={{ flex: 1, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{item.tag}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}


